import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import {
  metroCacheGet,
  metroCacheSet,
  metroCacheIsFresh,
} from './metro_cache_v2';
import metroClustersData from './metro_clusters.json';

// =============================================================================
// SUPABASE CONFIGURATION
// =============================================================================
const SUPABASE_URL = 'https://api.carpe-app.com';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlvcXhjYmJhaXpicGllaHFpcnNqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzIxMzAxNDgsImV4cCI6MjA4NzcwNjE0OH0._C7wQownGD9EW2Pw8PNYqgtpzfXQyxOTTU9jYr8FqA8';

// =============================================================================
// MAPS URL HELPER — reads user preference from localStorage at call time
// =============================================================================
// Default: Google Maps (works everywhere; degrades to web if app missing).
// User can opt into Apple Maps via Settings (iOS only — Apple Maps URLs are
// only useful on iOS/macOS; we ignore the preference on Android).
//   • Google Maps app installed → universal link opens the app
//   • No Google Maps app        → opens Google Maps web in system browser
//   • Apple Maps preference + iOS → opens Apple Maps app
const getMapsUrl = (query) => {
  const encoded = encodeURIComponent(query);
  let pref = 'google';
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('carpe_maps_preference') : null;
    if (stored === 'apple') pref = 'apple';
  } catch (_) {}
  if (pref === 'apple' && Capacitor.getPlatform() === 'ios') {
    return `https://maps.apple.com/?q=${encoded}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encoded}`;
};

// Supabase POST/PATCH helper for creating/updating records
const supabasePost = async (table, body, options = {}) => {
  const { method = 'POST', query = '', prefer = 'return=representation' } = options;
  let url = `${SUPABASE_URL}/rest/v1/${table}`;
  if (query) url += `?${query}`;

  const response = await fetch(url, {
    method,
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
      'Prefer': prefer,
    },
    body: JSON.stringify(body),
  });
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  return response.json();
};

// Error logger — writes to Supabase error_logs table
const classifyError = (message = '', stack = '') => {
  const m = message.toLowerCase();
  if (m.includes('metro') || m.includes('resolvemetros')) return 'metro_resolution';
  if (m.includes('aborterror') || m.includes('timeout')) return 'supabase_timeout';
  if (m.includes('supabase') || m.includes('rest/v1')) return 'supabase_query';
  if (m.includes('viator') || m.includes('ticketmaster') || m.includes('opentable')) return 'affiliate_link';
  if (m.includes('geolocation') || m.includes('coords')) return 'geolocation';
  if (m.includes('auth') || m.includes('token') || m.includes('unauthorized')) return 'auth';
  if (m.includes('cors') || m.includes('access-control')) return 'cors';
  if (m.includes('empty') || m.includes('no results')) return 'spin_empty';
  return 'unknown';
};

const logError = (error, context = {}) => {
  try {
    const payload = {
      error_message: String(error?.message || error).slice(0, 2000),
      stack_trace: String(error?.stack || '').slice(0, 5000),
      url: context.url || window.location.href,
      user_agent: navigator.userAgent,
      error_category: classifyError(error?.message, error?.stack),
      context: { url: context.url || window.location.href, userAgent: navigator.userAgent, ...context },
      first_seen: new Date().toISOString(),
      last_seen: new Date().toISOString(),
      occurrence_count: 1,
    };
    // Fire-and-forget — never let logging itself break the app
    fetch(`${SUPABASE_URL}/rest/v1/error_logs?on_conflict=error_message`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates',
      },
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch (_) {}
};

// Supabase fetch helper
// @protected — DO NOT MODIFY. Filter operator logic (eq/in/ilike/gte/neq) debugged extensively.
// =============================================================================
// FAMILY-FRIENDLY FILTER (frontend-only, applied to activities + events pool)
// Positive: kid-appropriate categories/subcategories.
// Negative: nightlife / bar / brewery / casino / dispensary keywords.
// No schema changes — runs client-side after fetch.
// =============================================================================
const KIDS_POSITIVE_SUBS = new Set([
  'Park','City Park','State Park','National Park','Nature Preserve',
  'Beach','Botanical Garden','Garden',
  'Zoo','Aquarium','Wildlife Park','Petting Zoo','Farm',
  'Museum',"Children's Museum",'History Museum','Art Museum','Science Museum','Natural History Museum',
  'Theme Park','Amusement Park','Water Park',
  'Mini Golf','Miniature Golf','Bowling','Bowling Alley','Bowling Center','Arcade',
  'Playground','Trampoline Park','Skating Rink','Ice Skating Rink','Roller Skating Rink',
  'Planetarium','Observatory','Library','Discovery Center'
]);
const KIDS_POSITIVE_CATS = new Set(['Family','Parks & Outdoors','Parks & Rec','Nature','Sports']);
const KIDS_NEGATIVE_CATS = new Set(['Nightlife']);
const KIDS_NEGATIVE_RE = /\b(bar|brewery|pub|tavern|casino|dispensary|cannabis|nightclub|strip\s*club|hookah)\b/i;
const KIDS_POSITIVE_KEYWORD_RE = /\b(museum|aquarium|zoo|park|arcade|bowling|theme\s*park|water\s*park)\b/i;

const isFamilyFriendly = (item) => {
  if (!item) return false;
  if (KIDS_NEGATIVE_CATS.has(item.category)) return false;
  const haystack = `${item.name || ''} ${item.subcategory || ''}`;
  if (KIDS_NEGATIVE_RE.test(haystack)) return false;
  if (item.subcategory && KIDS_POSITIVE_SUBS.has(item.subcategory)) return true;
  if (item.category && KIDS_POSITIVE_CATS.has(item.category)) return true;
  if (item.subcategory && KIDS_POSITIVE_KEYWORD_RE.test(item.subcategory)) return true;
  return false;
};

const supabaseFetch = async (table, options = {}) => {
  const { select = '*', filters = [], limit = 100, order } = options;

  let url = `${SUPABASE_URL}/rest/v1/${table}?select=${select}`;

  filters.forEach(f => {
    if (f.operator === 'in' && Array.isArray(f.value)) {
      const vals = f.value.map(v => /[,\s"]/.test(v) ? `"${v}"` : v).join(',');
      url += `&${f.column}=in.(${vals})`;
    } else {
      url += `&${f.column}=${f.operator}.${encodeURIComponent(f.value)}`;
    }
  });

  if (limit) url += `&limit=${limit}`;
  if (order) url += `&order=${order.column}.${order.ascending ? 'asc' : 'desc'}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);

  try {
    const response = await fetch(url, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Supabase error: ${response.status}`);
    return response.json();
  } catch (err) {
    if (err.name === 'AbortError') {
      const timeoutErr = new Error('Request timed out. Check your connection and try again.');
      logError(timeoutErr, { url });
      throw timeoutErr;
    }
    logError(err, { url });
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
};

// RPC helper — calls Supabase stored functions (e.g. proximity search)
const supabaseRpc = async (fnName, params = {}) => {
  const url = `${SUPABASE_URL}/rest/v1/rpc/${fnName}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Supabase RPC error: ${response.status}`);
    return response.json();
  } catch (err) {
    if (err.name === 'AbortError') {
      const timeoutErr = new Error('Request timed out.');
      logError(timeoutErr, { rpc: fnName });
      throw timeoutErr;
    }
    logError(err, { rpc: fnName });
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
};

// =============================================================================
// CACHE VERSION — bump when the shape of cached restaurant/activity rows changes
// or when a bug poisons cached data. On boot, if the stored version doesn't
// match, all IndexedDB databases are dropped so the app rehydrates from the
// updated RPCs / mappers. Self-healing — no manual cache clear needed.
// =============================================================================
const CACHE_VERSION = 'v2-id-preserved-2026-05';
const CACHE_VERSION_KEY = 'carpe_cache_version';

const migrateCacheIfStale = async () => {
  try {
    const stored = typeof localStorage !== 'undefined'
      ? localStorage.getItem(CACHE_VERSION_KEY)
      : null;
    if (stored === CACHE_VERSION) return;

    // Drop every IndexedDB database. metro_cache_v2 owns its own DB; we don't
    // hardcode the name here in case it ever changes.
    if (typeof indexedDB !== 'undefined' && typeof indexedDB.databases === 'function') {
      const dbs = await indexedDB.databases();
      await Promise.all(
        (dbs || []).map((d) =>
          new Promise((resolve) => {
            if (!d?.name) return resolve();
            const req = indexedDB.deleteDatabase(d.name);
            req.onsuccess = req.onerror = req.onblocked = () => resolve();
          })
        )
      );
    }
    // Older Safari / Firefox: indexedDB.databases() unsupported. Best-effort
    // fall back to deleting known cache DB names. metro_cache_v2 is the only
    // one we maintain today; add others here if introduced.
    else if (typeof indexedDB !== 'undefined') {
      ['metro_cache_v2'].forEach((name) => {
        try { indexedDB.deleteDatabase(name); } catch {}
      });
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
    }
    console.info('[cache] migrated to', CACHE_VERSION);
  } catch (err) {
    console.warn('[cache] migration check failed:', err?.message || err);
  }
};

// Hardcoded city-level metro coords — fallback if dynamic fetch fails
const METRO_COORDS = [
  { name: 'Jacksonville', lat: 30.3322, lng: -81.6557 },
  { name: 'Miami', lat: 25.7617, lng: -80.1918 },
  { name: 'Tampa', lat: 27.9506, lng: -82.4572 },
  { name: 'Orlando', lat: 28.5383, lng: -81.3792 },
  { name: 'Daytona Beach', lat: 29.2108, lng: -81.0228 },
  { name: 'Austin', lat: 30.2672, lng: -97.7431 },
  { name: 'Houston', lat: 29.7604, lng: -95.3698 },
  { name: 'Dallas', lat: 32.7767, lng: -96.7970 },
  { name: 'San Antonio', lat: 29.4241, lng: -98.4936 },
  { name: 'New York', lat: 40.7128, lng: -74.0060 },
  { name: 'Los Angeles', lat: 34.0522, lng: -118.2437 },
  { name: 'San Francisco', lat: 37.7749, lng: -122.4194 },
  { name: 'San Diego', lat: 32.7157, lng: -117.1611 },
  { name: 'Chicago', lat: 41.8781, lng: -87.6298 },
  { name: 'Phoenix', lat: 33.4484, lng: -112.0740 },
  { name: 'Seattle', lat: 47.6062, lng: -122.3321 },
  { name: 'Denver', lat: 39.7392, lng: -104.9903 },
  { name: 'Boston', lat: 42.3601, lng: -71.0589 },
  { name: 'Nashville', lat: 36.1627, lng: -86.7816 },
  { name: 'Portland', lat: 45.5152, lng: -122.6784 },
  { name: 'Las Vegas', lat: 36.1699, lng: -115.1398 },
  { name: 'New Orleans', lat: 29.9511, lng: -90.0715 },
  { name: 'Atlanta', lat: 33.7490, lng: -84.3880 },
  { name: 'Washington', lat: 38.9072, lng: -77.0369 },
  { name: 'Philadelphia', lat: 39.9526, lng: -75.1652 },
  { name: 'Minneapolis', lat: 44.9778, lng: -93.2650 },
  { name: 'Detroit', lat: 42.3314, lng: -83.0458 },
  { name: 'Charlotte', lat: 35.2271, lng: -80.8431 },
  { name: 'Raleigh', lat: 35.7796, lng: -78.6382 },
  { name: 'Baltimore', lat: 39.2904, lng: -76.6122 },
  { name: 'Salt Lake City', lat: 40.7608, lng: -111.8910 },
  { name: 'San Jose', lat: 37.3382, lng: -121.8863 },
  { name: 'Columbus', lat: 39.9612, lng: -82.9988 },
  { name: 'Indianapolis', lat: 39.7684, lng: -86.1581 },
  { name: 'Milwaukee', lat: 43.0389, lng: -87.9065 },
  { name: 'Kansas City', lat: 39.0997, lng: -94.5786 },
  { name: 'St. Louis', lat: 38.6270, lng: -90.1994 },
  { name: 'Cleveland', lat: 41.4993, lng: -81.6944 },
  { name: 'Cincinnati', lat: 39.1031, lng: -84.5120 },
  { name: 'Pittsburgh', lat: 40.4406, lng: -79.9959 },
  { name: 'Louisville', lat: 38.2460, lng: -85.7276 },
  { name: 'Memphis', lat: 35.1331, lng: -89.9390 },
  { name: 'Honolulu', lat: 21.3069, lng: -157.8583 },
  { name: 'Savannah', lat: 32.0809, lng: -81.0912 },
  { name: 'Charleston', lat: 32.7765, lng: -79.9311 },
  { name: 'Asheville', lat: 35.5951, lng: -82.5515 },
  { name: 'Sacramento', lat: 38.5816, lng: -121.4944 },
  { name: 'Fresno', lat: 36.7378, lng: -119.7871 },
  { name: 'Oakland', lat: 37.8087, lng: -122.2537 },
  { name: 'Albuquerque', lat: 35.0844, lng: -106.6504 },
  { name: 'Tucson', lat: 32.2226, lng: -110.9747 },
  // Neighborhoods — fallback coords from DB avg (US-filtered)
  // NYC
  { name: 'DUMBO', lat: 40.6994, lng: -73.9872 },
  { name: 'SoHo', lat: 40.7232, lng: -73.9945 },
  { name: 'Midtown', lat: 40.7537, lng: -73.9846 },
  { name: 'Long Island City', lat: 40.7507, lng: -73.8886 },
  // Chicago
  { name: 'Gold Coast', lat: 41.9041, lng: -87.6447 },
  { name: 'Wicker Park', lat: 41.9052, lng: -87.6776 },
  { name: 'West Loop', lat: 41.8840, lng: -87.6531 },
  { name: 'Lincoln Park', lat: 41.9314, lng: -87.6542 },
  { name: 'River North', lat: 41.8914, lng: -87.6306 },
  { name: 'Old Town', lat: 41.9016, lng: -87.6481 },
  // Atlanta
  { name: 'Buckhead', lat: 33.8391, lng: -84.3784 },
  { name: 'Midtown Atlanta', lat: 33.7870, lng: -84.3934 },
  { name: 'Inman Park', lat: 33.7686, lng: -84.3670 },
  // Phoenix
  { name: 'Paradise Valley', lat: 33.5653, lng: -111.9735 },
  // Seattle
  { name: 'Capitol Hill', lat: 47.6169, lng: -122.3181 },
  { name: 'Fremont', lat: 47.5144, lng: -122.3350 },
  { name: 'Ballard', lat: 47.6745, lng: -122.3750 },
  // DC
  { name: 'Adams Morgan', lat: 38.9204, lng: -77.0425 },
  { name: 'Dupont Circle', lat: 38.9081, lng: -77.0446 },
  { name: 'Navy Yard', lat: 38.8877, lng: -77.0083 },
  { name: 'Bethesda', lat: 38.9940, lng: -77.1016 },
  // LA / Bay Area
  { name: 'Venice Beach', lat: 33.9947, lng: -118.4632 },
  { name: 'Mountain View', lat: 37.4001, lng: -122.0940 },
  // Louisville
  { name: 'St. Matthews', lat: 38.2426, lng: -85.6460 },
  { name: 'Germantown Louisville', lat: 38.2125, lng: -85.6675 },
  // Nashville
  { name: 'Germantown Nashville', lat: 36.1725, lng: -86.7738 },
  // Memphis
  { name: 'Crosstown', lat: 35.1647, lng: -89.8469 },
  { name: 'Overton Square', lat: 35.1289, lng: -89.9957 },
  { name: 'South Main', lat: 35.1245, lng: -89.9213 },
  // Utah
  { name: 'Provo', lat: 40.2509, lng: -111.6664 },
  // Oklahoma
  { name: 'Oklahoma City', lat: 35.4799, lng: -97.5245 },
  { name: 'Tulsa', lat: 36.1392, lng: -95.9720 },
  { name: 'Norman', lat: 35.2396, lng: -97.4605 },
  { name: 'Broken Arrow', lat: 36.0330, lng: -95.8212 },
  { name: 'Edmond', lat: 35.6230, lng: -97.4942 },
  // Wisconsin / Iowa / Nebraska / Dakotas
  { name: 'Green Bay', lat: 44.5017, lng: -88.0302 },
  { name: 'Appleton', lat: 44.2616, lng: -88.4122 },
  { name: 'Davenport', lat: 41.5437, lng: -90.5660 },
  { name: 'Sioux Falls', lat: 43.5288, lng: -96.7378 },
  { name: 'Iowa City', lat: 41.6670, lng: -91.5406 },
  { name: 'Cedar Rapids', lat: 41.9874, lng: -91.6693 },
  { name: 'Grand Island', lat: 40.9646, lng: -98.1411 },
  { name: 'Rapid City', lat: 44.0824, lng: -103.1814 },
  { name: 'Bismarck', lat: 46.8163, lng: -100.7965 },
  // Alabama / Mississippi
  { name: 'Mobile', lat: 30.6679, lng: -88.0716 },
  { name: 'Montgomery', lat: 32.4148, lng: -86.2705 },
  { name: 'Auburn', lat: 32.6306, lng: -85.5107 },
  { name: 'Gulfport', lat: 30.2908, lng: -88.7954 },
  { name: 'Hattiesburg', lat: 31.2942, lng: -89.3253 },
  // West Virginia
  { name: 'Charleston WV', lat: 36.5604, lng: -81.0986 },
  { name: 'Huntington', lat: 38.4708, lng: -82.2675 },
  { name: 'Parkersburg', lat: 39.2782, lng: -81.4620 },
  // Idaho / Montana
  { name: "Coeur d'Alene", lat: 47.6923, lng: -116.7898 },
  { name: 'Idaho Falls', lat: 43.6729, lng: -112.3109 },
  { name: 'Great Falls', lat: 47.4395, lng: -111.2783 },
  { name: 'Helena', lat: 46.5959, lng: -112.0122 },
  // Vermont / Maine / New Mexico
  { name: 'Burlington', lat: 44.4573, lng: -73.1883 },
  { name: 'Bangor', lat: 44.7707, lng: -68.8277 },
  { name: 'Las Cruces', lat: 32.3523, lng: -106.7615 },
  // Maine
  { name: 'Rockland', lat: 44.1037, lng: -69.1089 },
  { name: 'Kennebunkport', lat: 43.3615, lng: -70.4776 },
  { name: 'Bar Harbor', lat: 44.3876, lng: -68.2039 },
  { name: 'Augusta ME', lat: 44.3106, lng: -69.7795 },
  // Vermont
  { name: 'Montpelier', lat: 44.2601, lng: -72.5754 },
  // Delaware
  { name: 'Rehoboth Beach', lat: 38.7209, lng: -75.0760 },
  { name: 'Newark DE', lat: 39.6837, lng: -75.7497 },
  { name: 'Dover DE', lat: 39.1582, lng: -75.5244 },
  // Rhode Island
  { name: 'Cranston', lat: 41.7798, lng: -71.4373 },
  { name: 'Newport RI', lat: 41.4901, lng: -71.3128 },
  // Nebraska / Kansas
  { name: 'Lincoln NE', lat: 40.8136, lng: -96.7026 },
  { name: 'Bellevue NE', lat: 41.1567, lng: -95.8939 },
  { name: 'Overland Park', lat: 38.9822, lng: -94.6708 },
  { name: 'Topeka', lat: 39.0558, lng: -95.6890 },
  { name: 'Lawrence', lat: 38.9717, lng: -95.2353 },
  // Wisconsin / Iowa / North Dakota / Wyoming
  { name: 'Kenosha', lat: 42.5847, lng: -87.8212 },
  { name: 'Racine', lat: 42.7261, lng: -87.7829 },
  { name: 'Dubuque', lat: 42.4963, lng: -90.6646 },
  { name: 'Grand Forks', lat: 47.9253, lng: -97.0329 },
  { name: 'Casper', lat: 42.8501, lng: -106.3252 },
  { name: 'Cheyenne', lat: 41.1400, lng: -104.8202 },
  // South Carolina
  { name: 'Spartanburg', lat: 34.9496, lng: -81.9320 },
  { name: 'Hilton Head', lat: 32.2163, lng: -80.7526 },
  { name: 'Myrtle Beach', lat: 33.6891, lng: -78.8867 },
  // Alabama
  { name: 'Tuscaloosa', lat: 33.2098, lng: -87.5692 },
  { name: 'Decatur AL', lat: 34.6059, lng: -86.9833 },
  { name: 'Florence AL', lat: 34.7998, lng: -87.6773 },
  // West Virginia
  { name: 'Morgantown', lat: 39.6295, lng: -79.9559 },
  // Mississippi
  { name: 'Biloxi', lat: 30.3960, lng: -88.8853 },
  { name: 'Oxford MS', lat: 34.3665, lng: -89.5191 },
  { name: 'Natchez', lat: 31.5604, lng: -91.4032 },
  { name: 'Meridian', lat: 43.6121, lng: -116.3915 },
  // Arkansas
  { name: 'Fort Smith', lat: 35.3859, lng: -94.3985 },
  { name: 'Rogers', lat: 36.3320, lng: -94.1185 },
  { name: 'Hot Springs', lat: 34.5037, lng: -93.0552 },
  { name: 'Jonesboro', lat: 35.8423, lng: -90.7043 },
  { name: 'Bentonville', lat: 36.3729, lng: -94.2088 },
  // Idaho
  { name: 'Nampa', lat: 43.5407, lng: -116.5635 },
  // New Jersey
  { name: 'Atlantic City', lat: 39.3643, lng: -74.4229 },
  { name: 'Montclair', lat: 40.8259, lng: -74.2090 },
  { name: 'Princeton', lat: 40.3573, lng: -74.6672 },
  { name: 'Asbury Park', lat: 40.2204, lng: -74.0121 },
  // New Hampshire
  { name: 'Nashua', lat: 42.7654, lng: -71.4676 },
  { name: 'Manchester', lat: 42.9956, lng: -71.4548 },
  { name: 'Portsmouth NH', lat: 43.0718, lng: -70.7626 },
  { name: 'Concord NH', lat: 43.2081, lng: -71.5376 },

  // Tourist destinations, college towns, mid-size cities (added April 2026)
  { name: 'Bend', lat: 44.0581, lng: -121.3153 },
  { name: 'Ashland', lat: 42.1929, lng: -122.7103 },
  { name: 'Eugene', lat: 44.0521, lng: -123.0868 },
  { name: 'Bellingham', lat: 48.7519, lng: -122.4787 },
  { name: 'Missoula', lat: 46.8683, lng: -113.9921 },
  { name: 'Bozeman', lat: 45.6769, lng: -111.0429 },
  { name: 'Jackson', lat: 43.4799, lng: -110.7624 },
  { name: 'Moab', lat: 38.5733, lng: -109.5498 },
  { name: 'Park City', lat: 40.6461, lng: -111.4980 },
  { name: 'Durango', lat: 37.2807, lng: -107.8784 },
  { name: 'Fort Collins', lat: 40.5853, lng: -105.0844 },
  { name: 'Boulder', lat: 40.0150, lng: -105.2705 },
  { name: 'Aspen', lat: 39.1911, lng: -106.8175 },
  { name: 'Telluride', lat: 37.9375, lng: -107.8123 },
  { name: 'Vail', lat: 39.6403, lng: -106.3742 },
  { name: 'Steamboat Springs', lat: 40.4849, lng: -106.8317 },
  { name: 'Sedona', lat: 34.4270, lng: -111.9260 },
  { name: 'Flagstaff', lat: 35.1945, lng: -111.6513 },
  { name: 'Prescott', lat: 34.5400, lng: -112.4686 },
  { name: 'Santa Fe', lat: 35.0853, lng: -106.6504 },
  { name: 'Taos', lat: 36.4069, lng: -105.5898 },
  { name: 'Boone', lat: 36.2173, lng: -81.6743 },
  { name: 'Chapel Hill', lat: 35.9132, lng: -79.0558 },
  { name: 'Greenville', lat: 34.8526, lng: -82.3940 },
  { name: 'Beaufort', lat: 32.4312, lng: -80.6714 },
  { name: 'Wilmington', lat: 34.2257, lng: -77.9447 },
  { name: 'Outer Banks', lat: 35.2300, lng: -75.5200 },
  { name: 'Athens', lat: 33.9519, lng: -83.3766 },
  { name: 'Destin', lat: 30.3935, lng: -86.4728 },
  { name: 'Pensacola', lat: 30.4215, lng: -87.2169 },
  { name: 'Gulf Shores', lat: 30.2590, lng: -87.6796 },
  { name: 'Orange Beach', lat: 30.3283, lng: -87.5534 },
  { name: 'Galveston', lat: 29.3117, lng: -94.7889 },
  { name: 'Corpus Christi', lat: 27.5704, lng: -97.3964 },
  { name: 'South Padre Island', lat: 26.1127, lng: -97.1684 },
  { name: 'San Marcos', lat: 29.8832, lng: -97.9413 },
  { name: 'New Braunfels', lat: 29.7010, lng: -97.9949 },
  { name: 'Boerne', lat: 29.7882, lng: -98.7597 },
  { name: 'Wimberley', lat: 30.0945, lng: -98.2769 },
  { name: 'Fredericksburg', lat: 30.2711, lng: -98.8723 },
  { name: 'Johnson City', lat: 30.2769, lng: -98.3986 },
  { name: 'Mackinac Island', lat: 45.8447, lng: -84.6149 },
  { name: 'Traverse City', lat: 44.7597, lng: -85.6213 },
  { name: 'Marquette', lat: 46.5538, lng: -87.3906 },
  { name: 'Madison', lat: 43.0731, lng: -89.4012 },
  { name: 'Sturgeon Bay', lat: 44.8553, lng: -87.3638 },
  { name: 'La Crosse', lat: 43.8041, lng: -91.2394 },
  { name: 'Galena', lat: 42.4086, lng: -90.4284 },
  { name: 'Carbondale', lat: 37.7249, lng: -89.2382 },
  { name: 'Bloomington', lat: 40.4842, lng: -88.9937 },
  { name: 'Champaign', lat: 40.1164, lng: -88.2434 },
  { name: 'Ann Arbor', lat: 42.2808, lng: -83.7430 },
  { name: 'East Lansing', lat: 42.7335, lng: -84.4855 },
  { name: 'Kalamazoo', lat: 42.2917, lng: -85.5872 },
  { name: 'Ypsilanti', lat: 42.2324, lng: -83.6089 },
  { name: 'Bloomington IN', lat: 39.1668, lng: -86.5344 },
  { name: 'Terre Haute', lat: 39.4667, lng: -87.4141 },
  { name: 'Lafayette IN', lat: 40.4259, lng: -86.9081 },
  { name: 'Muncie', lat: 40.1934, lng: -85.3857 },
  { name: 'Valparaiso', lat: 41.4629, lng: -87.0610 },
  { name: 'South Bend', lat: 41.6829, lng: -86.2500 },
  { name: 'Dayton', lat: 39.7589, lng: -84.1916 },
  { name: 'Toledo', lat: 41.6639, lng: -83.5552 },
  { name: 'Akron', lat: 41.0814, lng: -81.5186 },
  { name: 'Marietta OH', lat: 39.4239, lng: -81.4560 },
  { name: 'Athens OH', lat: 39.3301, lng: -82.1012 },
  { name: 'Oxford OH', lat: 39.5084, lng: -84.7446 },
  { name: 'Bowling Green OH', lat: 41.3775, lng: -83.6547 },
  { name: 'Portsmouth OH', lat: 38.7241, lng: -82.5990 },
  { name: 'State College', lat: 40.7934, lng: -77.8601 },
  { name: 'Altoona', lat: 40.4935, lng: -78.3952 },
  { name: 'Erie', lat: 42.0898, lng: -80.0848 },
  { name: 'Scranton', lat: 41.4082, lng: -75.6244 },
  { name: 'Wilkes-Barre', lat: 41.2465, lng: -75.8815 },
  { name: 'Reading', lat: 40.3336, lng: -75.9297 },
  { name: 'Lancaster', lat: 40.0376, lng: -76.3055 },
  { name: 'Harrisburg', lat: 40.2732, lng: -76.8867 },
  { name: 'Gettysburg', lat: 39.8301, lng: -77.2303 },
  { name: 'York', lat: 39.9626, lng: -76.7276 },
  { name: 'Chambersburg', lat: 39.9404, lng: -77.6612 },
  { name: 'Carlisle', lat: 40.2000, lng: -77.1903 },
  { name: 'Hershey', lat: 40.2881, lng: -76.6449 },
  { name: 'Lebanon', lat: 40.3356, lng: -76.4113 },
  { name: 'Stowe', lat: 44.4667, lng: -72.7167 },
  { name: 'Woodstock VT', lat: 43.6441, lng: -72.5170 },
  { name: 'Bennington', lat: 42.8787, lng: -73.1945 },
  { name: 'New Haven', lat: 41.3083, lng: -72.9279 },
  { name: 'Stamford', lat: 41.0534, lng: -73.5387 },
  { name: 'Hartford', lat: 41.7658, lng: -72.6734 },
  { name: 'New London', lat: 41.3556, lng: -72.0986 },
  { name: 'Mystic', lat: 41.3753, lng: -71.9755 },
  { name: 'Salem MA', lat: 42.5195, lng: -70.8967 },
  { name: 'Lowell', lat: 42.6342, lng: -71.3161 },
  { name: 'Worcester', lat: 42.2625, lng: -71.8023 },
  { name: 'Springfield MA', lat: 42.1015, lng: -72.5898 },
  { name: 'Amherst', lat: 42.3740, lng: -72.5186 },
  { name: 'Northampton', lat: 42.3250, lng: -72.6358 },
  { name: 'Newburyport', lat: 42.8129, lng: -70.8553 },
  { name: 'Gloucester', lat: 42.6352, lng: -70.6659 },
  { name: 'Rockport MA', lat: 42.6495, lng: -70.6131 },
  { name: 'Brunswick ME', lat: 43.6707, lng: -69.9658 },
  { name: 'Lewiston ME', lat: 44.1038, lng: -70.1957 },
  { name: 'Hanover NH', lat: 43.7029, lng: -72.2895 },
  { name: 'Plymouth NH', lat: 43.7598, lng: -71.6822 },
  { name: 'Durham NH', lat: 43.1378, lng: -71.2025 },
  { name: 'Keene', lat: 42.9356, lng: -72.2822 },
  { name: 'Ithaca', lat: 42.4534, lng: -76.4735 },
  { name: 'Rochester NY', lat: 43.1613, lng: -77.6109 },
  { name: 'Buffalo', lat: 42.8864, lng: -78.8784 },
  { name: 'Syracuse', lat: 43.0481, lng: -76.1474 },
  { name: 'Binghamton', lat: 42.0987, lng: -75.9180 },
  { name: 'Glens Falls', lat: 43.3185, lng: -73.6410 },
  { name: 'Plattsburgh', lat: 44.7012, lng: -73.4577 },
  { name: 'Albany', lat: 42.6526, lng: -73.7562 },
  { name: 'New Brunswick NJ', lat: 40.4774, lng: -74.4446 },
  { name: 'Trenton', lat: 40.2206, lng: -74.7597 },
  { name: 'Cape May', lat: 38.9357, lng: -74.9095 },
  { name: 'Wildwood', lat: 38.9924, lng: -74.8119 },
  { name: 'Ocean City NJ', lat: 39.2844, lng: -74.5753 },
  { name: 'Bethany Beach', lat: 38.5344, lng: -75.0705 },
  { name: 'Lewes', lat: 38.7681, lng: -75.1391 },
  { name: 'Leesburg', lat: 39.1142, lng: -77.8648 },
  { name: 'Charlottesville', lat: 38.0293, lng: -78.4767 },
  { name: 'Blacksburg', lat: 37.2296, lng: -80.4139 },
  { name: 'Harrisonburg', lat: 38.4496, lng: -78.8691 },
  { name: 'Front Royal', lat: 38.9147, lng: -78.1909 },
  { name: 'Staunton', lat: 38.1491, lng: -79.0717 },
  { name: 'Lexington VA', lat: 37.7846, lng: -79.4412 },
  { name: 'Johnson City TN', lat: 36.3131, lng: -82.3546 },
  { name: 'Knoxville', lat: 35.9606, lng: -83.9207 },
  { name: 'Gatlinburg', lat: 35.7143, lng: -83.5111 },
  { name: 'Pigeon Forge', lat: 35.7847, lng: -83.5711 },
  { name: 'Townsend', lat: 35.7833, lng: -83.6667 },
  { name: 'Maryville', lat: 35.7568, lng: -83.9814 },
  { name: 'Murfreesboro', lat: 35.3395, lng: -86.3887 },
  { name: 'Columbia TN', lat: 35.6117, lng: -87.0349 },
  { name: 'Fayetteville TN', lat: 35.1507, lng: -86.5712 },
  { name: 'Tullahoma', lat: 35.3867, lng: -86.2050 },
  { name: 'Sewanee', lat: 35.2000, lng: -85.9167 },
  { name: 'Lexington KY', lat: 38.0496, lng: -84.4674 },
  { name: 'Bowling Green KY', lat: 36.9906, lng: -86.4104 },
  { name: 'Covington KY', lat: 39.0840, lng: -84.5087 },
  { name: 'Eureka Springs', lat: 36.4019, lng: -93.7314 },
  { name: 'Baton Rouge', lat: 30.4515, lng: -91.1871 },
  { name: 'Lafayette LA', lat: 30.2241, lng: -92.0198 },
  { name: 'Shreveport', lat: 32.5250, lng: -93.7502 },
  { name: 'Jackson MS', lat: 32.2988, lng: -90.1848 },
  { name: 'Stillwater OK', lat: 36.1200, lng: -97.0600 },
  { name: 'Lawrence KS', lat: 38.9717, lng: -95.2353 },

  // Pacific Northwest + Idaho small towns (added April 2026)
  { name: 'Cannon Beach', lat: 45.8891, lng: -123.9623 },
  { name: 'Seaside OR', lat: 45.9896, lng: -123.9226 },
  { name: 'Yachats', lat: 44.2980, lng: -124.1076 },
  { name: 'Bandon', lat: 42.7254, lng: -124.4040 },
  { name: 'Brookings OR', lat: 42.6555, lng: -124.2835 },
  { name: 'Cottage Grove', lat: 43.7833, lng: -123.2833 },
  { name: 'Newport OR', lat: 44.6349, lng: -124.0544 },
  { name: 'Corvallis', lat: 44.5646, lng: -123.2620 },
  { name: 'Salem OR', lat: 44.9429, lng: -123.2351 },
  { name: 'Tillamook', lat: 45.4131, lng: -123.5773 },
  { name: 'Lincoln City', lat: 44.9641, lng: -124.0089 },
  { name: 'Manzanita', lat: 45.7219, lng: -123.9337 },
  { name: 'Pacific City', lat: 45.4286, lng: -123.9613 },
  { name: 'Florence OR', lat: 43.9869, lng: -124.0998 },
  { name: 'Coos Bay', lat: 43.3868, lng: -124.2158 },
  { name: 'Roseburg', lat: 43.2188, lng: -123.2813 },
  { name: 'Grants Pass', lat: 42.4394, lng: -123.3311 },
  { name: 'Medford', lat: 42.3265, lng: -122.8756 },
  { name: 'Klamath Falls', lat: 42.2250, lng: -121.7824 },
  { name: 'Gresham', lat: 45.4988, lng: -122.4292 },
  { name: 'Lake Oswego', lat: 45.4069, lng: -122.6614 },
  { name: 'Oregon City', lat: 45.3547, lng: -122.6070 },
  { name: 'McCall', lat: 44.9069, lng: -116.1075 },
  { name: 'Ketchum', lat: 43.6601, lng: -114.3701 },
  { name: 'Hailey', lat: 43.5209, lng: -114.2976 },
  { name: 'Stanley', lat: 44.2162, lng: -115.0025 },
  { name: 'Sandpoint', lat: 48.2765, lng: -116.5500 },
  { name: 'Coeur d Alene', lat: 47.6768, lng: -116.7825 },
  { name: 'Walla Walla', lat: 46.0646, lng: -118.3430 },
  { name: 'Pullman', lat: 46.7314, lng: -117.1769 },
  { name: 'Moscow ID', lat: 46.7296, lng: -117.2877 },
  { name: 'Lewiston ID', lat: 46.4124, lng: -117.0176 },
  { name: 'Joseph', lat: 45.3533, lng: -117.2419 },
  { name: 'Enterprise OR', lat: 45.4330, lng: -118.2767 },
  { name: 'Baker City', lat: 44.7747, lng: -118.7840 },
  { name: 'Mountain Home', lat: 43.1278, lng: -115.6892 },
];

// Haversine distance in miles (module-level for use in resolveMetros + components)
// @protected — DO NOT MODIFY. Geo functions are the foundation of all city/metro queries.
const haversineDistance = (lat1, lng1, lat2, lng2) => {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Find nearest metro by coordinates using provided metro list (dynamic or fallback)
const nearestMetro = (lat, lng, metroList = METRO_COORDS) => {
  if (lat < 18 || lat > 72 || lng < -172 || lng > -65) return null;
  let best = null, bestDist = Infinity;
  for (const m of metroList) {
    const d = (lat - m.lat) ** 2 + (lng - m.lng) ** 2;
    if (d < bestDist) { bestDist = d; best = m; }
  }
  return best ? best.name : null;
};

// Neighborhood bubbling: 2mi → 10mi → nearest
// @protected — DO NOT MODIFY. Metro resolution drives all city data loading.
const resolveMetros = (lat, lng, metroList) => {
  if (!lat || !lng || !metroList?.length) return [];

  // Calculate distances for all metros
  const withDist = metroList.map(m => ({
    name: m.name,
    dist: haversineDistance(lat, lng, m.lat, m.lng),
  }));
  withDist.sort((a, b) => a.dist - b.dist);

  // 1. Exact neighborhood: metros within 2 miles
  const within2 = withDist.filter(m => m.dist <= 2);
  if (within2.length > 0) return within2.map(m => m.name);

  // 2. Expanded radius: metros within 10 miles
  const within10 = withDist.filter(m => m.dist <= 10);
  if (within10.length > 0) return within10.map(m => m.name);

  // 3. Fallback: single nearest metro
  return withDist.length > 0 ? [withDist[0].name] : [];
};

// @protected — DO NOT MODIFY. City-to-metro lookup logic.
const cityToMetroSlug = (cityName, coords = null) => {
  const baseName = cityName.split(',')[0].trim();
  if (!baseName) return null;

  // 1. Exact match against known metros (case-insensitive)
  const known = METRO_COORDS.find(m => m.name.toLowerCase() === baseName.toLowerCase());
  if (known) return known.name;

  // 2. Nearest metro by geocoded coordinates
  if (coords) return nearestMetro(coords.lat, coords.lng);

  // 3. Last resort: return city name as-is (ilike query will handle case)
  return baseName;
};

// Fisher-Yates shuffle — uniform distribution, patent-defensible randomness
// Replaces Array.sort(() => Math.random() - 0.5) which is biased
const fisherYatesShuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Rolling window of recent results to suppress repeats across spins.
// Tuned to be long enough to avoid the "same 3 restaurants" feel after restrictive
// filters, short enough that small-pool fallbacks still work.
const RECENT_MEMORY = 10;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('Carpe error boundary caught:', error, info);
    logError(error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'DM Sans, sans-serif', background: '#FBF7F4', gap: '16px' }}>
          <h2 style={{ fontSize: '1.4rem', color: '#2D2A26' }}>Something went wrong</h2>
          <p style={{ color: '#6B6560', fontSize: '0.95rem' }}>Refresh the page to try again.</p>
          <button onClick={() => window.location.reload()} style={{ padding: '10px 24px', background: '#2D2A26', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem' }}>
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// =============================================================================
// SUPABASE AUTH HELPERS
// =============================================================================
const SUPABASE_AUTH_URL = `${SUPABASE_URL}/auth/v1`;

// Store session in localStorage
const storeSession = (session) => {
  if (session) {
    localStorage.setItem('carpe_auth_session', JSON.stringify(session));
  } else {
    localStorage.removeItem('carpe_auth_session');
  }
};

// Get stored session
const getStoredSession = () => {
  try {
    const stored = localStorage.getItem('carpe_auth_session');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Sign up with email/password
const supabaseSignUp = async (email, password, displayName = '') => {
  const response = await fetch(`${SUPABASE_AUTH_URL}/signup`, {
    method: 'POST',
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      password,
      data: { full_name: displayName || email.split('@')[0] }
    }),
  });
  
  const data = await response.json();
  if (!response.ok) throw new Error(data.error_description || data.msg || 'Sign up failed');
  
  if (data.access_token) {
    storeSession(data);
  }
  return data;
};

// Sign in with email/password
const supabaseSignIn = async (email, password) => {
  const response = await fetch(`${SUPABASE_AUTH_URL}/token?grant_type=password`, {
    method: 'POST',
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });
  
  const data = await response.json();
  if (!response.ok) throw new Error(data.error_description || data.msg || 'Sign in failed');
  
  storeSession(data);
  return data;
};

// Sign out
const supabaseSignOut = async (accessToken) => {
  if (accessToken) {
    await fetch(`${SUPABASE_AUTH_URL}/logout`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    });
  }
  storeSession(null);
};

// Get current user from token
const supabaseGetUser = async (accessToken) => {
  const response = await fetch(`${SUPABASE_AUTH_URL}/user`, {
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
    },
  });
  
  if (!response.ok) return null;
  return response.json();
};

// Send password reset email
const supabaseResetPassword = async (email) => {
  const response = await fetch(`${SUPABASE_AUTH_URL}/recover`, {
    method: 'POST',
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      redirect_to: 'https://carpe-app.com',
    }),
  });
  
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error_description || data.msg || 'Password reset failed');
  }
  return true;
};

// Update user password (when logged in)
const supabaseUpdatePassword = async (accessToken, newPassword) => {
  const response = await fetch(`${SUPABASE_AUTH_URL}/user`, {
    method: 'PUT',
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password: newPassword }),
  });
  
  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error_description || data.msg || 'Password update failed');
  }
  return response.json();
};

// Refresh token
const supabaseRefreshToken = async (refreshToken) => {
  const response = await fetch(`${SUPABASE_AUTH_URL}/token?grant_type=refresh_token`, {
    method: 'POST',
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  
  const data = await response.json();
  if (!response.ok) throw new Error(data.error_description || 'Token refresh failed');
  
  storeSession(data);
  return data;
};

// Auth-aware fetch helper (includes user's access token)
const supabaseAuthFetch = async (table, accessToken, options = {}) => {
  const { select = '*', filters = [], limit = 100, order } = options;
  
  let url = `${SUPABASE_URL}/rest/v1/${table}?select=${select}`;
  
  filters.forEach(f => {
    if (f.operator === 'in' && Array.isArray(f.value)) {
      const vals = f.value.map(v => /[,\s"]/.test(v) ? `"${v}"` : v).join(',');
      url += `&${f.column}=in.(${vals})`;
    } else {
      url += `&${f.column}=${f.operator}.${encodeURIComponent(f.value)}`;
    }
  });
  
  if (limit) url += `&limit=${limit}`;
  if (order) url += `&order=${order.column}.${order.ascending ? 'asc' : 'desc'}`;
  
  const response = await fetch(url, {
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) throw new Error(`Supabase error: ${response.status}`);
  return response.json();
};

// Auth-aware POST helper
const supabaseAuthPost = async (table, accessToken, body, options = {}) => {
  const { method = 'POST', query = '' } = options;
  let url = `${SUPABASE_URL}/rest/v1/${table}`;
  if (query) url += `?${query}`;
  
  const response = await fetch(url, {
    method,
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'Prefer': 'return=representation',
    },
    body: JSON.stringify(body),
  });
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  return response.json();
};

// Auth-aware DELETE helper
const supabaseAuthDelete = async (table, accessToken, query) => {
  const url = `${SUPABASE_URL}/rest/v1/${table}?${query}`;
  
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${accessToken}`,
    },
  });
  
  if (!response.ok) throw new Error(`Delete failed: ${response.status}`);
  return true;
};

// =============================================================================
// HAPTIC & SOUND FEEDBACK - Mechanical Slot Machine
// =============================================================================
let audioContext = null;

// @protected — DO NOT MODIFY. Sound engine: Heavier Roll (continuous brown-noise rumble + sub-bass) × Triple Ding celebration.
// Architecture: function names (playRatchetClick / playSlowingClunk / playWheelLock / playFinalSettle) preserved
// for backward compatibility with WheelSpinner spin loop, but bodies are completely different:
//   playRatchetClick → starts continuous rolling sound (idempotent — first call only)
//   playSlowingClunk → no-op (rolling sound already playing continuously)
//   playWheelLock    → steps down rolling volume (100% → 65% → 32%) + plays reel-stop click+thud (descending pitch 280 → 240 Hz)
//   playFinalSettle  → plays third reel-stop thud (200 Hz), fades rolling out, then plays Triple Ding celebration (delayed 120ms)
// Tuned by hand. Changing frequencies/Q values/gains breaks UX.
const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  return audioContext;
};

// Create noise buffer for mechanical sounds
const createNoiseBuffer = (ctx, duration) => {
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const output = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    output[i] = Math.random() * 2 - 1;
  }
  return buffer;
};

// Brown noise — heavy low-frequency content, used for rolling rumble body
const createBrownNoiseBuffer = (ctx, duration) => {
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const output = buffer.getChannelData(0);
  let lastOut = 0;
  for (let i = 0; i < bufferSize; i++) {
    const w = Math.random() * 2 - 1;
    output[i] = (lastOut + 0.02 * w) / 1.02;
    lastOut = output[i];
    output[i] *= 3.5;
  }
  return buffer;
};

// Pink noise — equal energy per octave, used for surface texture in rolling sound
const createPinkNoiseBuffer = (ctx, duration) => {
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const output = buffer.getChannelData(0);
  let b0=0,b1=0,b2=0,b3=0,b4=0,b5=0,b6=0;
  for (let i = 0; i < bufferSize; i++) {
    const w = Math.random() * 2 - 1;
    b0 = 0.99886*b0 + w*0.0555179;
    b1 = 0.99332*b1 + w*0.0750759;
    b2 = 0.96900*b2 + w*0.1538520;
    b3 = 0.86650*b3 + w*0.3104856;
    b4 = 0.55000*b4 + w*0.5329522;
    b5 = -0.7616*b5 - w*0.0168980;
    output[i] = (b0+b1+b2+b3+b4+b5+b6+w*0.5362) * 0.11;
    b6 = w*0.115926;
  }
  return buffer;
};

// =============================================================================
// CONTINUOUS ROLLING SOUND STATE
// =============================================================================
// rollingState manages a singleton "rolling ball" sound that runs from spin-start
// to spin-end. Multiple wheels share one continuous sound stream rather than each
// firing independent click events. Volume steps down as wheels lock.
let rollingState = {
  active: false,
  masterGain: null,    // single gain node controls overall rolling volume
  sources: [],         // BufferSourceNodes (brown noise, pink noise) — looped
  oscillators: [],     // OscillatorNodes (sub bass, mid hum) — continuous
  wheelsLocked: 0,     // counter incremented per playWheelLock() call
};

const resetRollingState = () => {
  rollingState.active = false;
  rollingState.masterGain = null;
  rollingState.sources = [];
  rollingState.oscillators = [];
  rollingState.wheelsLocked = 0;
};

// Idempotent — first call starts the rolling sound, subsequent calls are no-ops
const startRollingSound = () => {
  if (rollingState.active) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    // Master gain controls overall rolling volume (steps down as wheels lock)
    const masterGain = ctx.createGain();
    masterGain.connect(ctx.destination);
    masterGain.gain.setValueAtTime(0, now);
    masterGain.gain.linearRampToValueAtTime(1.0, now + 0.12);

    // Brown noise rumble — main body of the rolling sound (looped 10s buffer)
    const brown = ctx.createBufferSource();
    brown.buffer = createBrownNoiseBuffer(ctx, 10);
    brown.loop = true;
    const brownFilter = ctx.createBiquadFilter();
    const brownGain = ctx.createGain();
    brown.connect(brownFilter);
    brownFilter.connect(brownGain);
    brownGain.connect(masterGain);
    brownFilter.type = 'lowpass';
    brownFilter.frequency.setValueAtTime(220, now);
    brownFilter.Q.setValueAtTime(0.8, now);
    brownGain.gain.setValueAtTime(0.32, now);
    brown.start(now);

    // Pink noise — light surface texture (looped)
    const pink = ctx.createBufferSource();
    pink.buffer = createPinkNoiseBuffer(ctx, 10);
    pink.loop = true;
    const pinkFilter = ctx.createBiquadFilter();
    const pinkGain = ctx.createGain();
    pink.connect(pinkFilter);
    pinkFilter.connect(pinkGain);
    pinkGain.connect(masterGain);
    pinkFilter.type = 'bandpass';
    pinkFilter.frequency.setValueAtTime(450, now);
    pinkFilter.Q.setValueAtTime(1.2, now);
    pinkGain.gain.setValueAtTime(0.03, now);
    pink.start(now);

    // Sub-bass sine drone — adds weight
    const sub = ctx.createOscillator();
    const subGain = ctx.createGain();
    sub.connect(subGain);
    subGain.connect(masterGain);
    sub.type = 'sine';
    sub.frequency.setValueAtTime(55, now);
    subGain.gain.setValueAtTime(0.14, now);
    sub.start(now);

    // Mid hum — tonal body
    const hum = ctx.createOscillator();
    const humGain = ctx.createGain();
    hum.connect(humGain);
    humGain.connect(masterGain);
    hum.type = 'sine';
    hum.frequency.setValueAtTime(90, now);
    humGain.gain.setValueAtTime(0.04, now);
    hum.start(now);

    rollingState.active = true;
    rollingState.masterGain = masterGain;
    rollingState.sources = [brown, pink];
    rollingState.oscillators = [sub, hum];
    rollingState.wheelsLocked = 0;
  } catch (e) {}
};

// Step master gain down as each non-last wheel locks (1st lock → 65%, 2nd lock → 32%)
const stepDownRollingVolume = () => {
  if (!rollingState.active || !rollingState.masterGain) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    rollingState.wheelsLocked++;
    const targetGain = rollingState.wheelsLocked === 1 ? 0.65 : 0.32;
    rollingState.masterGain.gain.cancelScheduledValues(now);
    rollingState.masterGain.gain.setValueAtTime(rollingState.masterGain.gain.value, now);
    rollingState.masterGain.gain.linearRampToValueAtTime(targetGain, now + 0.08);
  } catch (e) {}
};

// Fade out rolling sound and clean up all nodes
const stopRollingSound = () => {
  if (!rollingState.active || !rollingState.masterGain) {
    resetRollingState();
    return;
  }
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    rollingState.masterGain.gain.cancelScheduledValues(now);
    rollingState.masterGain.gain.setValueAtTime(rollingState.masterGain.gain.value, now);
    rollingState.masterGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    const stopTime = now + 0.15;
    rollingState.sources.forEach(s => { try { s.stop(stopTime); } catch (e) {} });
    rollingState.oscillators.forEach(o => { try { o.stop(stopTime); } catch (e) {} });
  } catch (e) {}
  resetRollingState();
};

// Bell note with inharmonic partials (1x + 2.76x + 5.4x) — physically accurate bell timbre
const playBellNote = (t0, freq, gain, dur) => {
  try {
    const ctx = getAudioContext();
    const partials = [
      { ratio: 1.0, gain: 1.0, decayMul: 1.0 },
      { ratio: 2.76, gain: 0.45, decayMul: 0.6 },
      { ratio: 5.4, gain: 0.20, decayMul: 0.35 },
    ];
    partials.forEach(p => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.type = 'sine';
      o.frequency.setValueAtTime(freq * p.ratio, t0);
      const partialDur = dur * p.decayMul;
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(gain * p.gain, t0 + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + partialDur);
      o.start(t0);
      o.stop(t0 + partialDur + 0.02);
    });
  } catch (e) {}
};

// Reel-stop "lock" sound — small audible cue when a wheel comes to rest.
// Reads rollingState.wheelsLocked AFTER it's been incremented (or accepts a forced
// index for the final wheel where wheelsLocked isn't bumped). Successive stops
// descend in pitch (280 Hz → 240 Hz → 200 Hz) — each wheel feels like it's settling
// deeper. Layered: a quick high click (mechanical lock attack) over a soft low
// thud (body) over a tiny noise burst (organic texture).
const playReelStopSound = (forcedIndex = null) => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    // Pitch descends with each successive wheel stop
    // wheelsLocked is 1 for first stop, 2 for second stop (already incremented).
    // For the third (last) wheel, playFinalSettle passes forcedIndex=3.
    const stopIndex = forcedIndex !== null ? forcedIndex : rollingState.wheelsLocked;
    const popFreq = 280 - (stopIndex - 1) * 40; // 280, 240, 200

    // High-frequency click — mechanical lock attack (cuts through the rolling rumble)
    const click = ctx.createOscillator();
    const clickFilter = ctx.createBiquadFilter();
    const clickGain = ctx.createGain();
    click.connect(clickFilter);
    clickFilter.connect(clickGain);
    clickGain.connect(ctx.destination);
    click.type = 'square';
    click.frequency.setValueAtTime(1200, now);
    click.frequency.exponentialRampToValueAtTime(400, now + 0.012);
    clickFilter.type = 'bandpass';
    clickFilter.frequency.setValueAtTime(900, now);
    clickFilter.Q.setValueAtTime(3, now);
    clickGain.gain.setValueAtTime(0.08, now);
    clickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.015);
    click.start(now);
    click.stop(now + 0.02);

    // Soft low-frequency thud — body of the lock, descending pitch
    const pop = ctx.createOscillator();
    const popGain = ctx.createGain();
    pop.connect(popGain);
    popGain.connect(ctx.destination);
    pop.type = 'sine';
    pop.frequency.setValueAtTime(popFreq, now);
    pop.frequency.exponentialRampToValueAtTime(popFreq * 0.5, now + 0.08);
    popGain.gain.setValueAtTime(0.10, now);
    popGain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
    pop.start(now);
    pop.stop(now + 0.12);

    // Tiny filtered noise burst — soft "tk" texture
    const noise = ctx.createBufferSource();
    noise.buffer = createPinkNoiseBuffer(ctx, 0.06);
    const noiseFilter = ctx.createBiquadFilter();
    const noiseGain = ctx.createGain();
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(ctx.destination);
    noiseFilter.type = 'lowpass';
    noiseFilter.frequency.setValueAtTime(800, now);
    noiseGain.gain.setValueAtTime(0.05, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
    noise.start(now);
  } catch (e) {}
};

// Heavier Roll: triggers the continuous rolling sound on first call (idempotent).
// Subsequent calls (from other wheels' tick loops, or the slow phase) are ignored.
// The volume parameter is unused — kept for backward compat with the spin loop.
const playRatchetClick = (volume = 0.13) => {
  startRollingSound();
};

// Heavier Roll: no-op. The continuous rolling sound is already playing from
// playRatchetClick. The slowing phase doesn't need additional sound events —
// the rolling sound continues until each wheel locks.
// volume and pitch parameters are unused — kept for backward compat.
const playSlowingClunk = (volume = 0.22, pitch = 1.0) => {
  // intentionally empty — see startRollingSound()
};

// Heavier Roll: each non-last wheel locking does two things:
//   1. Steps the rolling volume down by ~35% (100% → 65% → 32%)
//   2. Plays a small "reel stopped" lock sound — high click + low thud, descending pitch
const playWheelLock = () => {
  stepDownRollingVolume(); // increments wheelsLocked counter
  playReelStopSound();     // reads counter to compute descending pitch
};

// Triple Ding final celebration — last wheel locks with its own thud (200 Hz),
// rolling sound fades out, then 3 rapid bright bell dings (D6, F#6, A6 = D major
// arpeggio) play over a sustained D major chord pad (D5+F#5+A5) with low D3 body,
// finishing with a high A7 ping + sparkle at 0.32s. Total duration ~1.5s.
const playFinalSettle = () => {
  try {
    // Step 1 — play the third wheel-stop thud (200 Hz, completes the 280→240→200 sequence)
    playReelStopSound(3);

    // Step 2 — fade out the continuous rolling sound (cleans up its nodes too)
    stopRollingSound();

    // Step 3 — Triple Ding celebration, delayed 0.12s so the third thud lands first
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const dingStart = now + 0.12;

    // Three rapid ascending bell dings — D major arpeggio
    playBellNote(dingStart + 0.00, 1174.66, 0.13, 0.5); // D6
    playBellNote(dingStart + 0.08, 1479.98, 0.13, 0.5); // F#6
    playBellNote(dingStart + 0.16, 1760.00, 0.13, 0.5); // A6

    // Sustained D major chord pad underneath (D5 + F#5 + A5) — triangle waves
    [587.33, 739.99, 880.00].forEach(freq => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.type = 'triangle';
      o.frequency.setValueAtTime(freq, dingStart);
      g.gain.setValueAtTime(0, dingStart);
      g.gain.linearRampToValueAtTime(0.08, dingStart + 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, dingStart + 1.2);
      o.start(dingStart);
      o.stop(dingStart + 1.22);
    });

    // Low D3 sine for body/power
    const lowO = ctx.createOscillator();
    const lowG = ctx.createGain();
    lowO.connect(lowG);
    lowG.connect(ctx.destination);
    lowO.type = 'sine';
    lowO.frequency.setValueAtTime(146.83, dingStart);
    lowG.gain.setValueAtTime(0, dingStart);
    lowG.gain.linearRampToValueAtTime(0.09, dingStart + 0.04);
    lowG.gain.exponentialRampToValueAtTime(0.001, dingStart + 1.2);
    lowO.start(dingStart);
    lowO.stop(dingStart + 1.22);

    // Final high A7 bell ping at 0.32s — the sparkle finale note
    playBellNote(dingStart + 0.32, 3520.00, 0.10, 0.7);

    // High-frequency sparkle noise on the finale ping
    const sparkleT = dingStart + 0.32;
    const sparkle = ctx.createBufferSource();
    sparkle.buffer = createNoiseBuffer(ctx, 0.5);
    const sparkleFilter = ctx.createBiquadFilter();
    const sparkleGain = ctx.createGain();
    sparkle.connect(sparkleFilter);
    sparkleFilter.connect(sparkleGain);
    sparkleGain.connect(ctx.destination);
    sparkleFilter.type = 'highpass';
    sparkleFilter.frequency.setValueAtTime(6000, sparkleT);
    sparkleGain.gain.setValueAtTime(0, sparkleT);
    sparkleGain.gain.linearRampToValueAtTime(0.05, sparkleT + 0.04);
    sparkleGain.gain.exponentialRampToValueAtTime(0.001, sparkleT + 0.5);
    sparkle.start(sparkleT);
    sparkle.stop(sparkleT + 0.52);
  } catch (e) {}
};

const triggerHaptic = (pattern = 'tick') => {
  try {
    if (navigator.vibrate) {
      if (pattern === 'lock') {
        navigator.vibrate(30);
      } else if (pattern === 'final') {
        navigator.vibrate([25, 40, 50]);
      } else {
        navigator.vibrate(8);
      }
    }
  } catch (e) {}
};

// =============================================================================
// CDN FETCH — Phase 2 read path
// =============================================================================
// Tries cdn.carpe-app.com first (R2 blob, edge-cached). Falls back to Supabase
// REST for uncovered metros. Validates schema_version on every fetch.
//
// Blob shape (mirrors generate-metro-blobs.js):
//   { schema_version, metro, metro_slug, state, center,
//     generated_at, counts, restaurants, activities, events }
const CDN_BASE = 'https://cdn.carpe-app.com';
const CDN_SCHEMA_VERSION = 1;
const CDN_TIMEOUT_MS = 4000;

// Slug must match generate-metro-blobs.js slugify() AND metro_cache_v2.js metroSlug().
const cdnSlug = (input) => {
  if (!input) return '';
  return String(input)
    .split(',')[0]
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// =============================================================================
// METRO CLUSTERS — fielding > labels
// Source of truth: metro_clusters.json (sibling file, also consumed by scraper).
// Cluster IDs are CDN blob filenames; singletons synthesized for uncovered metros.
// =============================================================================
const _metroToClusters = new Map(); // "Metro" -> [{cluster, state}]
const _aliasToCluster = new Map();   // lowercase alias -> cluster
for (const _c of metroClustersData.clusters) {
  for (const _a of _c.aliases || []) _aliasToCluster.set(_a.toLowerCase(), _c);
  for (const _m of _c.metros) {
    const _arr = _metroToClusters.get(_m.metro) || [];
    _arr.push({ cluster: _c, state: _m.state });
    _metroToClusters.set(_m.metro, _arr);
  }
}

// Resolve a metro name -> cluster. When the same metro name appears in multiple
// states (Portland ME/OR/NH, Hollywood CA/FL, Newark NJ/DE), disambiguate by
// anchor distance to the supplied coords. Synthesizes a singleton cluster for
// metros not in any explicit cluster — the singleton's id is just the metro slug.
const resolveCluster = (metroName, coords = null) => {
  if (!metroName) return null;
  const baseName = String(metroName).split(',')[0].trim();
  const matches = _metroToClusters.get(baseName) || [];
  if (matches.length === 1) return matches[0].cluster;
  if (matches.length > 1) {
    if (coords) {
      let best = matches[0], bestDist = Infinity;
      for (const m of matches) {
        const a = m.cluster.anchor;
        if (!a) continue;
        const d = (a.lat - coords.lat) ** 2 + (a.lng - coords.lng) ** 2;
        if (d < bestDist) { bestDist = d; best = m; }
      }
      return best.cluster;
    }
    return matches[0].cluster;
  }
  return { id: cdnSlug(baseName), label: baseName, metros: [{ metro: baseName, state: '' }], anchor: coords, singleton: true };
};

// Resolve a user-typed string (city OR alias like "NYC", "Bay Area") -> cluster.
// Used when the input came from a search box rather than a coord/metro lookup.
const resolveClusterByInput = (input, coords = null) => {
  if (!input) return null;
  const lower = String(input).split(',')[0].trim().toLowerCase();
  return _aliasToCluster.get(lower) || resolveCluster(input, coords);
};

// Module-level singletons — persist across re-renders of useSupabaseData.
const _cdnBlobCache = new Map(); // slug -> { blob, fetchedAt }
const _cdnInflight = new Map();  // slug -> Promise (single-flight per session)

// Returns the parsed blob, or null on miss / version mismatch / network error.
// Tries the cluster blob first (e.g. tampa-bay.json), falls back to the raw
// metro blob (e.g. clearwater.json) for backward compat while the scraper
// migrates to cluster-keyed blobs. Session in-memory cache holds blobs 10 min.
//   options.cluster — pre-resolved cluster (skip the resolveCluster lookup)
//   options.coords  — used to disambiguate same-named metros across states
const fetchMetroBlob = async (metroName, options = {}) => {
  const metroSlug = cdnSlug(metroName);
  if (!metroSlug) return null;

  const cluster = options.cluster || resolveCluster(metroName, options.coords);
  const clusterSlug = cluster?.id;

  // Slug order: cluster first (where the rich blob lives), then raw metro
  // (legacy fallback — still works until the scraper finishes the rebuild).
  const slugs = (clusterSlug && clusterSlug !== metroSlug)
    ? [clusterSlug, metroSlug]
    : [metroSlug];

  for (const slug of slugs) {
    const blob = await _fetchBlobBySlug(slug);
    if (blob) return blob;
  }
  return null;
};

const _fetchBlobBySlug = async (slug) => {
  if (!slug) return null;

  // Session-level in-memory cache (10 min)
  const cached = _cdnBlobCache.get(slug);
  if (cached && Date.now() - cached.fetchedAt < 10 * 60 * 1000) {
    return cached.blob;
  }

  // Single-flight: if a fetch for this slug is already in flight, await it.
  if (_cdnInflight.has(slug)) return _cdnInflight.get(slug);

  const promise = (async () => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), CDN_TIMEOUT_MS);
    try {
      const res = await fetch(`${CDN_BASE}/metros/v1/${slug}.json`, {
        signal: ctrl.signal,
      });
      if (!res.ok) return null;
      const blob = await res.json();
      if (!blob || blob.schema_version !== CDN_SCHEMA_VERSION) {
        console.warn(`CDN blob schema mismatch for ${slug}: got ${blob?.schema_version}, expected ${CDN_SCHEMA_VERSION}`);
        return null;
      }
      _cdnBlobCache.set(slug, { blob, fetchedAt: Date.now() });
      return blob;
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.warn(`CDN fetch failed for ${slug}:`, err.message);
      }
      return null;
    } finally {
      clearTimeout(timer);
      _cdnInflight.delete(slug);
    }
  })();

  _cdnInflight.set(slug, promise);
  return promise;
};

// Sort an array by distance to the user. Returns a copy with `distance` added.
// Items with no coords go to the end (Infinity distance).
const sortByDistance = (items, userLat, userLng) => {
  if (!userLat || !userLng) return items;
  return [...items]
    .map((item) => {
      if (!item.lat || !item.lng) return { item, distance: Infinity };
      return {
        item,
        distance: haversineDistance(userLat, userLng, item.lat, item.lng),
      };
    })
    .sort((a, b) => a.distance - b.distance)
    .map(({ item, distance }) => ({ ...item, distance }));
};

// =============================================================================
// DATA FETCHING HOOKS
// =============================================================================
const useSupabaseData = () => {
  const [streaming, setStreaming] = useState([]);
  const [activities, setActivities] = useState({});
  const [restaurants, setRestaurants] = useState({});
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedCities = useRef(new Set());
  const [dynamicMetroCoords, setDynamicMetroCoords] = useState(null);

  // Fetch all neighborhood-level metro coordinates from DB view (runs once on mount)
  const fetchMetroCoords = async () => {
    try {
      const data = await supabaseFetch('metro_coords_mat', {
        select: 'name,lat,lng',
        limit: 1000,
      });
      const coords = data.map(row => ({
        name: row.name,
        lat: parseFloat(row.lat),
        lng: parseFloat(row.lng),
      })).filter(m => m.name && !isNaN(m.lat) && !isNaN(m.lng));
      setDynamicMetroCoords(coords);
      return coords;
    } catch (err) {
      console.error('Error fetching metro coords:', err);
      return null;
    }
  };

  // Fetch streaming data (global, not city-specific)
  const fetchStreaming = async () => {
    try {
      // Calls the get_balanced_streaming RPC which returns a balanced sample:
      // top N rows per (platform × genre × media_type) cell ranked by
      // trending → quality (rating × ln(reviews+1)) → id, plus all currently
      // trending titles. Constant ~2-5K rows regardless of how big the DB grows.
      // Server-side ranking guarantees minimums per spin combination.
      const PER_CELL = 30;  // minimum titles per (platform × genre × media_type)

      const url = `${SUPABASE_URL}/rest/v1/rpc/get_balanced_streaming`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12000);
      let data = [];
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ per_cell: PER_CELL }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Supabase RPC error: ${response.status}`);
        data = await response.json();
      } finally {
        clearTimeout(timeoutId);
      }

      // Normalize platform names to match wheel labels
      const PLATFORM_NORMALIZE = {
        'Amazon Prime Video': 'Prime',
        'Amazon Prime': 'Prime',
        'HBO Max': 'Max',
        'HBO': 'Max',
      };
      const mapped = (Array.isArray(data) ? data : []).map(item => ({
        name: item.name,
        genre: item.genre || 'Drama',
        length: item.length || 'TV Show',
        platform: PLATFORM_NORMALIZE[item.platform] || item.platform || 'Netflix',
        rating: item.rating != null ? parseFloat(item.rating) : null,
        reviews: item.reviews != null ? item.reviews : null,
        year: item.year,
        description: item.description || '',
        trending: item.trending || false,
        boost: item.boost || 1,
        sponsored: item.sponsored || false,
      }));

      setStreaming(mapped);
      return mapped;
    } catch (err) {
      console.error('Error fetching streaming:', err);
      setError(err.message);
      return [];
    }
  };

  // Fetch activities for a metro. CDN-first with Supabase fallback.
  // @protected — Data pipeline for activities. Modify only with full understanding.
  const fetchActivitiesForCity = async (cityName, coords = null) => {
    if (!cityName) return [];
    const city = cityName.split(',')[0].trim();

    // Resolve metro name (used for Supabase fallback only — CDN uses raw input)
    const metroList = dynamicMetroCoords || METRO_COORDS;

    // If no coords passed, try to resolve from metroList. Same rationale as restaurants:
    // enables proximity RPC fallback for cities with sparse/zero metro-tagged coverage.
    if (!coords && Array.isArray(metroList)) {
      const cityLower = city.toLowerCase();
      const hit = metroList.find(m => (m.name || '').toLowerCase() === cityLower);
      if (hit && Number.isFinite(hit.lat) && Number.isFinite(hit.lng)) {
        coords = { lat: hit.lat, lng: hit.lng };
      }
    }

    const metros = coords ? resolveMetros(coords.lat, coords.lng, metroList) : [];
    const metroName = metros[0] || cityToMetroSlug(city, coords);
    // Resolve to a destination cluster (e.g. Tampa -> tampa-bay covering both
    // Tampa and St. Petersburg metros). The cluster's CDN blob is the primary
    // fetch target; its metro list expands Supabase fallback queries beyond a
    // single metro tag.
    const cluster = resolveCluster(metroName, coords);
    const expandedMetros = cluster
      ? Array.from(new Set([...metros, ...cluster.metros.map((m) => m.metro)]))
      : metros;

    // Cache key is now slug-only — CDN blob covers the whole metro,
    // distance sort happens client-side.
    const cacheKey = cdnSlug(metroName);

    if (activities[cacheKey]?.length > 0) {
      const cached = activities[cacheKey];
      return coords ? sortByDistance(cached, coords.lat, coords.lng) : cached;
    }

    // Path A — CDN blob (covered metro)
    const blob = await fetchMetroBlob(metroName, { cluster, coords });
    if (blob && Array.isArray(blob.activities) && blob.activities.length > 0) {
      setActivities((prev) => ({ ...prev, [cacheKey]: blob.activities }));
      fetchedCities.current.add(cacheKey);
      metroCacheSet(metroName, 'activities', blob.activities); // fire-and-forget IDB write
      return coords
        ? sortByDistance(blob.activities, coords.lat, coords.lng)
        : blob.activities;
    }

    // Map helper — shared between proximity-RPC and metro-fetch paths.
    const _mapActivityRow = (item) => ({
      id: item.id ?? null,
      name: item.name,
      indoor: item.indoor ?? true,
      category: item.category || 'Entertainment',
      subcategory: item.subcategory || null,
      priceRange: item.price_range || 'Free',
      description: item.description || 'A great local experience.',
      rating: item.rating != null ? parseFloat(item.rating) : null,
      reviews: item.reviews != null ? item.reviews : null,
      lat: parseFloat(item.lat) || null,
      lng: parseFloat(item.lng) || null,
      trending: item.trending || false,
      boost: item.boost || 1,
      sponsored: item.sponsored || false,
      url: item.url,
      image_url: item.image_url,
      source: item.source,
    });

    // Path B — Supabase fallback for uncovered metros
    try {
      let mapped = [];

      // Prefer proximity when coords available — handles metros with bad city tagging.
      // max_results=500 to match restaurants — see comment there for rationale.
      if (coords) {
        const data = await supabaseRpc('get_nearest_activities', {
          user_lat: coords.lat,
          user_lng: coords.lng,
          metro_name: metroName,
          max_results: 2000,
        });
        mapped = (data || []).map(_mapActivityRow);
      }

      // If proximity returned nothing OR no coords, fall back to metro filter.
      if (mapped.length === 0) {
        const metroFilter =
          expandedMetros.length > 1
            ? { column: 'metro', operator: 'in', value: expandedMetros }
            : { column: 'metro', operator: 'eq', value: metroName };

        const data = await supabaseFetch('activities', {
          filters: [
            metroFilter,
            { column: 'business_status', operator: 'eq', value: 'OPERATIONAL' },
          ],
          limit: 5000,
        });

        mapped = fisherYatesShuffle(data).map(_mapActivityRow);
      }

      if (mapped.length > 0) {
        setActivities((prev) => ({ ...prev, [cacheKey]: mapped }));
        fetchedCities.current.add(cacheKey);
        metroCacheSet(metroName, 'activities', mapped);
      }
      return coords ? sortByDistance(mapped, coords.lat, coords.lng) : mapped;
    } catch (err) {
      console.error(`Error fetching activities for ${city}:`, err);
      return [];
    }
  };

  // Fetch restaurants for a metro. CDN-first with Supabase fallback.
  // @protected — Data pipeline for restaurants. Modify only with full understanding.
  const fetchRestaurantsForCity = async (cityName, coords = null) => {
    if (!cityName) return [];
    const city = cityName.split(',')[0].trim();

    const metroList = dynamicMetroCoords || METRO_COORDS;

    // If no coords passed (e.g. CitySearch autocomplete suggestion click), try to
    // resolve them from the metro lookup table. Keeps the proximity RPC path viable
    // for cities whose metro tag has zero rows in the DB (sparse coverage areas).
    if (!coords && Array.isArray(metroList)) {
      const cityLower = city.toLowerCase();
      const hit = metroList.find(m => (m.name || '').toLowerCase() === cityLower);
      if (hit && Number.isFinite(hit.lat) && Number.isFinite(hit.lng)) {
        coords = { lat: hit.lat, lng: hit.lng };
      }
    }

    const metros = coords ? resolveMetros(coords.lat, coords.lng, metroList) : [];
    const metroName = metros[0] || cityToMetroSlug(city, coords);
    // Resolve to cluster — same fielding logic as activities.
    const cluster = resolveCluster(metroName, coords);
    const expandedMetros = cluster
      ? Array.from(new Set([...metros, ...cluster.metros.map((m) => m.metro)]))
      : metros;
    const cacheKey = cdnSlug(metroName);

    if (restaurants[cacheKey]?.length > 0) {
      const cached = restaurants[cacheKey];
      return coords ? sortByDistance(cached, coords.lat, coords.lng) : cached;
    }

    // CDN first
    const blob = await fetchMetroBlob(metroName, { cluster, coords });
    if (blob && Array.isArray(blob.restaurants) && blob.restaurants.length > 0) {
      setRestaurants((prev) => ({ ...prev, [cacheKey]: blob.restaurants }));
      fetchedCities.current.add(cacheKey);
      metroCacheSet(metroName, 'restaurants', blob.restaurants);
      return coords
        ? sortByDistance(blob.restaurants, coords.lat, coords.lng)
        : blob.restaurants;
    }

    // Supabase fallback
    try {
      let mapped = [];

      // Prefer proximity when we have coords — handles cities whose metro tag
      // has 0 rows but whose lat/lng surroundings have real coverage (e.g. NSB).
      // max_results=500 (not 200): NSB's bounding box has ~200 close American
      // restaurants that crowd out sparser cuisines (Japanese, Indian, Korean,
      // etc.) sitting 20-30mi away in Sanford/Daytona suburbs. 500 lets them
      // through. Payload still well under 500KB.
      if (coords) {
        const data = await supabaseRpc('get_nearest_restaurants', {
          user_lat: coords.lat,
          user_lng: coords.lng,
          metro_name: metroName,
          max_results: 2000,
        });
        mapped = (data || []).map(_mapRestaurantRow);
      }

      // If proximity returned nothing OR we never had coords, try metro filter.
      if (mapped.length === 0) {
        const metroFilter =
          expandedMetros.length > 1
            ? { column: 'metro', operator: 'in', value: expandedMetros }
            : { column: 'metro', operator: 'eq', value: metroName };
        const data = await supabaseFetch('restaurants', {
          filters: [
            metroFilter,
            { column: 'business_status', operator: 'eq', value: 'OPERATIONAL' },
          ],
          limit: 5000,
        });
        mapped = fisherYatesShuffle(data).map(_mapRestaurantRow);
      }

      if (mapped.length > 0) {
        setRestaurants((prev) => ({ ...prev, [cacheKey]: mapped }));
        fetchedCities.current.add(cacheKey);
        metroCacheSet(metroName, 'restaurants', mapped);
      }
      return coords ? sortByDistance(mapped, coords.lat, coords.lng) : mapped;
    } catch (err) {
      console.error(`Error fetching restaurants for ${city}:`, err);
      return [];
    }
  };

  // Extracted to share between proximity-RPC and metro-fetch paths.
  const _mapRestaurantRow = (item) => ({
    id: item.id ?? null,
    name: item.name,
    cuisine: item.cuisine || 'American',
    style: item.style || 'Casual',
    ambiance: item.ambiance || 'Relaxed',
    price: item.price || '$$',
    rating: item.rating != null ? parseFloat(item.rating) : null,
    reviews: item.reviews != null ? item.reviews : null,
    description: item.description || '',
    lat: parseFloat(item.lat) || null,
    lng: parseFloat(item.lng) || null,
    address: item.address || null,
    phone: item.phone || null,
    website: item.website || null,
    hours: item.hours || null,
    image_url: item.image_url || null,
    city: item.city || null,
    state: item.state || null,
    trending: item.trending || false,
    boost: item.boost || 1,
    sponsored: item.sponsored || false,
    url: item.affiliate_url || item.url || null,
    google_id: item.google_id || null,
    on_opentable: item.on_opentable || false,
    on_doordash: item.on_doordash || false,
    on_ubereats: item.on_ubereats || false,
  });

  // Targeted proximity fetch centered on an arbitrary point (e.g. the chosen
  // Eat & Play activity). The metro-level pool is centered on the city search
  // point, which can under-cover an activity's neighborhood at walking radii —
  // this backfills it. Read-only with respect to the metro cache; callers
  // merge the results into their own pool.
  const fetchRestaurantsNearPoint = async (lat, lng, cityName = '') => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
    const metroList = dynamicMetroCoords || METRO_COORDS;
    const metros = resolveMetros(lat, lng, metroList);
    const metroName = metros[0] || cityToMetroSlug((cityName || '').split(',')[0].trim(), { lat, lng });
    try {
      const data = await supabaseRpc('get_nearest_restaurants', {
        user_lat: lat,
        user_lng: lng,
        metro_name: metroName,
        max_results: 500,
      });
      return (data || []).map(_mapRestaurantRow);
    } catch (err) {
      console.warn('Nearby restaurant fetch failed:', err?.message || err);
      return [];
    }
  };

  // Fetch events for a metro. CDN-first with Supabase fallback. Filters to future events client-side.
  // @protected — DO NOT MODIFY. Data pipeline for events.
  const fetchEventsForCity = async (cityName, coords = null) => {
    if (!cityName) return [];
    const city = cityName.split(',')[0].trim();

    const metroList = dynamicMetroCoords || METRO_COORDS;
    const metros = coords ? resolveMetros(coords.lat, coords.lng, metroList) : [];
    const metroName = metros[0] || cityToMetroSlug(city, coords);
    const cluster = resolveCluster(metroName, coords);
    const cacheKey = cdnSlug(metroName);

    if (events[cacheKey]?.length > 0) {
      return _filterFutureEvents(events[cacheKey], coords);
    }

    // Date filter helpers — applied AFTER fetch (CDN blob includes recent past too).
    // Use local date components — toISOString() returns UTC, which causes
    // yesterday's events to pass the filter during early-morning hours in US
    // timezones (midnight to ~4am EST is still yesterday in UTC).
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const today = `${yyyy}-${mm}-${dd}`;
    const nowISO = now.toISOString();

    // CDN first
    const blob = await fetchMetroBlob(metroName, { cluster, coords });
    if (blob && Array.isArray(blob.events) && blob.events.length > 0) {
      const future = blob.events.filter((e) => {
        if (e.start_date) return e.start_date >= nowISO;
        if (e.event_date) return e.event_date >= today;
        return false;
      });
      if (future.length > 0) {
        setEvents((prev) => ({ ...prev, [cacheKey]: future }));
        fetchedCities.current.add(cacheKey);
        metroCacheSet(metroName, 'events', future);
      }
      return _filterFutureEvents(future, coords);
    }

    // Supabase fallback — proximity: all metros within 50 miles when coords
    // available, plus the resolved cluster's full metro list (in case cluster
    // members sit beyond the 50mi window).
    let metroFilter;
    const clusterMetroNames = cluster?.metros?.map((m) => m.metro) || [];
    if (coords) {
      const nearbyMetros = metroList
        .filter((m) => haversineDistance(coords.lat, coords.lng, m.lat, m.lng) <= 50)
        .map((m) => m.name);
      const baseList =
        nearbyMetros.length > 0
          ? nearbyMetros
          : resolveMetros(coords.lat, coords.lng, metroList);
      const metroNames = Array.from(new Set([...baseList, ...clusterMetroNames]));
      metroFilter =
        metroNames.length > 1
          ? { column: 'metro', operator: 'in', value: metroNames }
          : {
              column: 'metro',
              operator: 'eq',
              value: metroNames[0] || cityToMetroSlug(city, coords),
            };
    } else {
      const baseValue = cityToMetroSlug(city, coords);
      const metroNames = Array.from(new Set([baseValue, ...clusterMetroNames].filter(Boolean)));
      metroFilter =
        metroNames.length > 1
          ? { column: 'metro', operator: 'in', value: metroNames }
          : { column: 'metro', operator: 'eq', value: baseValue };
    }

    try {
      // Fetch without server-side date filter — handle both event_date and start_date client-side
      const data = await supabaseFetch('events', {
        filters: [metroFilter],
        limit: 300,
        order: { column: 'event_date', ascending: true },
      });

      // Keep future events. Prefer start_date (full timestamp) over event_date
      // (date-only) to correctly exclude events that already started earlier today.
      const dated = data.filter((item) => {
        if (item.start_date) return item.start_date >= nowISO;
        if (item.event_date) return item.event_date >= today;
        return false;
      });

      // Map to app format (events also work as activities)
      const mapped = dated.map((item) => ({
        name: item.name,
        indoor: true,
        category: item.category || 'Entertainment',
        priceRange: item.price_range || '$25-50',
        description: item.description || (item.venue_name ? `at ${item.venue_name}` : 'Live event'),
        lat: parseFloat(item.lat) || null,
        lng: parseFloat(item.lng) || null,
        trending: item.trending || false,
        boost: item.boost || 1,
        sponsored: item.sponsored || false,
        url: item.affiliate_url || item.url,
        event_date: item.event_date,
        event_time: item.event_time,
        start_date: item.start_date,
        venue_name: item.venue_name,
        source: item.source,
        state: item.state,
        metro: item.metro,
      }));

      if (mapped.length > 0) {
        setEvents((prev) => ({ ...prev, [cacheKey]: mapped }));
        fetchedCities.current.add(cacheKey);
        metroCacheSet(metroName, 'events', mapped);
      }
      return _filterFutureEvents(mapped, coords);
    } catch (err) {
      console.error(`Error fetching events for ${city}:`, err);
      return [];
    }
  };

  // Apply 50-mile haversine and distance-sort if coords are present.
  // Items without coords pass through unchanged.
  const _filterFutureEvents = (list, coords) => {
    if (!coords) return list;
    const nearby = list.filter((item) => {
      const lat = parseFloat(item.lat);
      const lng = parseFloat(item.lng);
      if (!lat || !lng) return true;
      return haversineDistance(coords.lat, coords.lng, lat, lng) <= 50;
    });
    return sortByDistance(nearby, coords.lat, coords.lng);
  };

  // Initial fetch + homebase hydration
  useEffect(() => {
    const init = async () => {
      // Self-healing cache: if version mismatch, wipes IDB before anything
      // else can read from it. Awaited so hydrateHomebase below sees a clean
      // slate on a version bump.
      await migrateCacheIfStale();
      setLoading(true);
      await Promise.all([fetchStreaming(), fetchMetroCoords()]);
      setLoading(false);
      hydrateHomebase();
    };

    // Hydrate homebase from IDB (instant), then refresh from CDN if stale.
    const hydrateHomebase = async () => {
      try {
        const stored = loadFromStorage();
        const homeCity = stored?.user?.homeCity;
        if (!homeCity) return;
        const cacheKey = cdnSlug(homeCity);

        const [r, a, e] = await Promise.all([
          metroCacheGet(homeCity, 'restaurants'),
          metroCacheGet(homeCity, 'activities'),
          metroCacheGet(homeCity, 'events'),
        ]);

        // Hydrate state from any IDB slice we have.
        // Restaurants and activities must have an `id` on every row — pre-fix
        // caches lacked it, and feeding them into state poisons spin_analytics
        // (listing_id falls back to name instead of integer DB id). Reject the
        // whole slice if any row is id-less; the background CDN refresh below
        // will repopulate it cleanly.
        const rValid = r?.data?.length && r.data.every((x) => x && x.id != null);
        if (rValid) {
          setRestaurants((prev) => ({ ...prev, [cacheKey]: r.data }));
          fetchedCities.current.add(cacheKey);
        } else if (r?.data?.length) {
          console.warn('[cache] dropped stale restaurants slice for', homeCity, '— missing id');
        }

        const aValid = a?.data?.length && a.data.every((x) => x && x.id != null);
        if (aValid) {
          setActivities((prev) => ({ ...prev, [cacheKey]: a.data }));
          fetchedCities.current.add(cacheKey);
        } else if (a?.data?.length) {
          console.warn('[cache] dropped stale activities slice for', homeCity, '— missing id');
        }

        if (e?.data?.length) {
          setEvents((prev) => ({ ...prev, [cacheKey]: e.data }));
          fetchedCities.current.add(cacheKey);
        }

        // If anything is stale OR missing, kick a CDN fetch in background.
        // The CDN call is single-flight per slug, so this won't double up.
        const needsRefresh =
          !r || !a || !e ||
          !metroCacheIsFresh(r.cachedAt) ||
          !metroCacheIsFresh(a.cachedAt) ||
          !metroCacheIsFresh(e.cachedAt);
        if (needsRefresh) {
          // Resolve homeCity through the alias-aware path — handles typed inputs
          // like "NYC" or "Bay Area" that map to clusters, not raw metros.
          const homeCluster = resolveClusterByInput(homeCity);
          fetchMetroBlob(homeCity, { cluster: homeCluster }).then((blob) => {
            if (!blob) return;
            // Update state and IDB with fresh blob data.
            if (blob.restaurants?.length) {
              setRestaurants((prev) => ({ ...prev, [cacheKey]: blob.restaurants }));
              metroCacheSet(homeCity, 'restaurants', blob.restaurants);
            }
            if (blob.activities?.length) {
              setActivities((prev) => ({ ...prev, [cacheKey]: blob.activities }));
              metroCacheSet(homeCity, 'activities', blob.activities);
            }
            if (blob.events?.length) {
              // Apply same future-events filter as fetchEventsForCity.
              const nowISO = new Date().toISOString();
              const future = blob.events.filter((ev) =>
                ev.start_date ? ev.start_date >= nowISO : true
              );
              setEvents((prev) => ({ ...prev, [cacheKey]: future }));
              metroCacheSet(homeCity, 'events', future);
            }
          });
        }
      } catch (err) {
        console.warn('Homebase hydration skipped:', err?.message || err);
      }
    };

    init();
  }, []);

  return {
    streaming,
    activities,
    restaurants,
    events,
    loading,
    error,
    dynamicMetroCoords,
    fetchActivitiesForCity,
    fetchRestaurantsForCity,
    fetchRestaurantsNearPoint,
    fetchEventsForCity,
    fetchStreaming,
  };
};

// LocalStorage helpers
const STORAGE_KEY = 'carpeDiscover';
const TUTORIAL_STORAGE_KEY = 'carpeHasSeenTutorial';

const loadFromStorage = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

const saveToStorage = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
};

// =============================================================================
// ANONYMOUS ANALYTICS
// =============================================================================
const SESSION_KEY = 'carpeSession';

const getSessionId = () => {
  try {
    let sessionId = localStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = 'ses_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
      localStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return 'ses_anonymous';
  }
};

// @protected — Analytics infrastructure. Schema is locked.
// Direct write to Supabase (bypasses api.carpe-app.com CDN + the dead carpe-api Worker).
// Fire-and-forget. Silent failure by design — analytics must never break the app.
//
// Live schema (spin_analytics):
//   listing_id TEXT NOT NULL, listing_type TEXT NOT NULL (restaurant|activity|event),
//   event_type TEXT NOT NULL (spin|spin_empty|click|save|share),
//   source_tab TEXT (restaurant|activity|combo|stayin),
//   city TEXT, user_hash TEXT, user_id UUID, metadata JSONB.

const SUPABASE_DIRECT_URL = 'https://ioqxcbbaizbpiehqirsj.supabase.co'; // direct-write: analytics bypass

const SPIN_TYPE_MAP = {
  restaurant:       { source_tab: 'restaurant', listing_type: 'restaurant' },
  activity:         { source_tab: 'activity',   listing_type: 'activity' },
  combo_activity:   { source_tab: 'combo',      listing_type: 'activity' },
  combo_restaurant: { source_tab: 'combo',      listing_type: 'restaurant' },
  order:            { source_tab: 'stayin',     listing_type: 'restaurant' },
  binge:            { source_tab: 'stayin',     listing_type: 'restaurant' },
  event:            { source_tab: 'activity',   listing_type: 'event' }
};

const ANALYTICS_HEADERS = {
  'apikey': SUPABASE_ANON_KEY,
  'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
  'Content-Type': 'application/json',
  'Prefer': 'return=minimal'
};

// Returns auth.users.id if signed in via Supabase, else null.
const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem('sb-ioqxcbbaizbpiehqirsj-auth-token');
    if (!raw) return null;
    const s = JSON.parse(raw);
    return s?.user?.id || s?.currentSession?.user?.id || null;
  } catch {
    return null;
  }
};

const postAnalytics = (payload) => {
  try {
    fetch(`${SUPABASE_DIRECT_URL}/rest/v1/spin_analytics`, {
      method: 'POST',
      headers: ANALYTICS_HEADERS,
      body: JSON.stringify(payload)
    }).catch(() => {});
  } catch {}
};

const trackSpin = (data) => {
  const map = SPIN_TYPE_MAP[data.spinType] || { source_tab: null, listing_type: 'restaurant' };
  postAnalytics({
    listing_id: String(data.resultId || data.resultName || 'unknown'),
    listing_type: map.listing_type,
    event_type: 'spin',
    source_tab: map.source_tab,
    city: data.metro || null,
    user_hash: getSessionId(),
    user_id: getCurrentUserId(),
    metadata: {
      state: data.state || null,
      filters: data.filters || {},
      result_name: data.resultName || null,
      result_category: data.resultCategory || null,
      result_price: data.resultPrice || null,
      result_sponsored: data.resultSponsored || false,
      result_boost: data.resultBoost || 0
    }
  });
};

// Wish capture: filter combo that returned ZERO results in a given metro.
// This is the high-value signal for CRE / PE data product.
const trackSpinEmpty = (data) => {
  const map = SPIN_TYPE_MAP[data.spinType] || { source_tab: null, listing_type: 'restaurant' };
  postAnalytics({
    listing_id: 'NONE',
    listing_type: map.listing_type,
    event_type: 'spin_empty',
    source_tab: map.source_tab,
    city: data.metro || null,
    user_hash: getSessionId(),
    user_id: getCurrentUserId(),
    metadata: {
      state: data.state || null,
      filters: data.filters || {},
      reason: data.reason || 'no_results'
    }
  });
};

const trackEngagement = (resultName, action, sourceTab = null) => {
  const eventType = ['click', 'save', 'share'].includes(action) ? action : 'click';
  postAnalytics({
    listing_id: String(resultName || 'unknown'),
    listing_type: 'restaurant',
    event_type: eventType,
    source_tab: sourceTab,
    city: null,
    user_hash: getSessionId(),
    user_id: getCurrentUserId(),
    metadata: { action, result_name: resultName }
  });
};

// Radius options for Eat & Play (walking-friendly)
const RADIUS_OPTIONS = [
  { value: 0.5, label: 'Quick walk', description: '< 0.5 mi (10 min)' },
  { value: 1, label: 'Walking', description: '< 1 mi (20 min)' },
  { value: 3, label: 'Short drive', description: '< 3 mi' },
  { value: 10, label: 'Driving', description: '< 10 mi' },
];

// Japan variant: identical mile values (all distance math stays in miles),
// metric descriptions, and "Train ride" instead of "Driving".
const JAPAN_RADIUS_OPTIONS = [
  { value: 0.5, label: 'Quick walk', description: '< 800 m (10 min)' },
  { value: 1, label: 'Walking', description: '< 1.6 km (20 min)' },
  { value: 3, label: 'Short drive', description: '< 5 km' },
  { value: 10, label: 'Train ride', description: '< 16 km' },
];

// Minimum dining options an activity must have within the dining radius to be
// eligible for an Eat & Play spin. Keeps the combo from landing on activities
// in restaurant deserts where the step-2 spin would come up empty.
const MIN_COMBO_DINING_OPTIONS = 3;

// Major cities database
// Region-scoped city lists — the Region setting (Settings tab) decides which
// list CitySearch offers. JAPAN_CITIES entries must have data scraped by
// daily_scraper_japan.py (rows stamped metro='Tokyo'/'Sapporo', state='JP').
const JAPAN_CITIES = ["Tokyo, Japan", "Sapporo, Japan"];

// Result cards hide services with no Japan coverage (DoorDash, Grubhub, Lyft,
// Ticketmaster). Uber Eats, Uber (Uber Taxi), and Viator operate in Japan.
const isJapanCity = (c) => /,\s*japan$/i.test((c || '').trim());

// Covered Japanese metros for GPS/address resolution. The main nearestMetro()
// path is US-bounded, so Japanese coords must snap to these instead — otherwise
// a user standing in Shibuya resolves to metro='Shibuya' (zero rows).
const JAPAN_METRO_COORDS = [
  { name: 'Tokyo', lat: 35.6762, lng: 139.6503 },
  { name: 'Sapporo', lat: 43.0618, lng: 141.3545 },
];
// Nearest covered Japan metro within ~100 km, else null (honest no-coverage).
const nearestJapanMetro = (lat, lng) => {
  let best = null, bestDist = 62; // miles ≈ 100 km
  for (const m of JAPAN_METRO_COORDS) {
    const d = haversineDistance(lat, lng, m.lat, m.lng);
    if (d < bestDist) { bestDist = d; best = m.name; }
  }
  return best;
};

// Japan-mode filter taxonomy. These strings are stamped verbatim into the
// cuisine/category/subcategory columns by daily_scraper_japan.py — a label
// change here requires the same change there or the filter returns nothing.
const JAPAN_CUISINES = ['Ramen', 'Sushi', 'Izakaya', 'Yakiniku', 'Yakitori', 'Tempura', 'Udon & Soba', 'Tonkatsu', 'Japanese Curry', 'Okonomiyaki', 'Kaiseki', 'Yoshoku', 'Kissaten & Cafe', 'Sweets & Wagashi', 'Seafood'];
const JAPAN_STYLES = ['Casual', 'Izakaya', 'Kissaten & Cafe', 'Fine Dining', 'Street Food'];
const JAPAN_CATEGORIES = ['Shrines & Temples', 'Onsen & Sento', 'Gardens & Parks', 'Museums & Art', 'Landmarks & Views', 'Anime & Gaming', 'Karaoke & Nightlife', 'Markets & Shopping', 'Nature & Outdoors', 'Family Fun'];
const JAPAN_CATEGORY_SUBCATEGORY_MAP = {
  'Shrines & Temples': ['Shrine', 'Temple'],
  'Onsen & Sento': ['Onsen', 'Sento', 'Spa'],
  'Gardens & Parks': ['Garden', 'Park'],
};

const CITIES = [
  "New York, NY", "Los Angeles, CA", "Chicago, IL", "Houston, TX", "Phoenix, AZ",
  "Philadelphia, PA", "San Antonio, TX", "San Diego, CA", "Dallas, TX", "San Jose, CA",
  "Austin, TX", "Jacksonville, FL", "Fort Worth, TX", "Columbus, OH", "Indianapolis, IN",
  "Charlotte, NC", "San Francisco, CA", "Seattle, WA", "Denver, CO", "Boston, MA",
  "Nashville, TN", "Portland, OR", "Las Vegas, NV", "Detroit, MI", "Memphis, TN",
  "Louisville, KY", "Baltimore, MD", "Milwaukee, WI", "Albuquerque, NM", "Tucson, AZ",
  "Fresno, CA", "Sacramento, CA", "Atlanta, GA", "Miami, FL", "Oakland, CA",
  "Minneapolis, MN", "Cleveland, OH", "New Orleans, LA", "Tampa, FL", "Pittsburgh, PA",
  "Orlando, FL", "St. Petersburg, FL", "Fort Lauderdale, FL", "Sarasota, FL", "West Palm Beach, FL",
  "Daytona Beach, FL", "New Smyrna Beach, FL",
  "Raleigh, NC", "Washington, DC", "St. Louis, MO", "Kansas City, MO", "Cincinnati, OH",
  "Salt Lake City, UT", "Honolulu, HI", "Savannah, GA", "Charleston, SC", "Asheville, NC",
  "Birmingham, AL", "Oklahoma City, OK", "Tulsa, OK", "Omaha, NE", "Des Moines, IA",
  "Little Rock, AR", "Boise, ID", "Anchorage, AK", "Durham, NC", "Scottsdale, AZ",
  "London, UK", "Paris, France", "Tokyo, Japan", "Sydney, Australia", "Toronto, Canada",
  "Vancouver, Canada", "Berlin, Germany", "Madrid, Spain", "Rome, Italy", "Amsterdam, Netherlands",
  "Barcelona, Spain", "Vienna, Austria", "Prague, Czech Republic", "Dublin, Ireland", "Lisbon, Portugal",
  "Copenhagen, Denmark", "Stockholm, Sweden", "Oslo, Norway", "Helsinki, Finland", "Brussels, Belgium",
  "Singapore", "Hong Kong", "Seoul, South Korea", "Bangkok, Thailand", "Mumbai, India",
  "Dubai, UAE", "Cape Town, South Africa", "Buenos Aires, Argentina", "Mexico City, Mexico", "Lima, Peru"
];

// USA region shows US metros only — the international tail (starting at
// "London, UK") is excluded from suggestions; Japan region uses JAPAN_CITIES.
const US_CITIES = CITIES.slice(0, CITIES.indexOf("London, UK"));

// Ambiguous city names that exist in multiple states in our DB
const AMBIGUOUS_CITIES = {
  'arlington': [{ state: 'TX', label: 'Arlington, TX' }, { state: 'VA', label: 'Arlington, VA' }],
  'hyde park': [{ state: 'IL', label: 'Hyde Park, Chicago IL' }, { state: 'TX', label: 'Hyde Park, Austin TX' }],
  'oakland': [{ state: 'CA', label: 'Oakland, CA' }, { state: 'PA', label: 'Oakland, Pittsburgh PA' }],
  'hollywood': [{ state: 'CA', label: 'Hollywood, CA' }, { state: 'FL', label: 'Hollywood, FL' }],
  'lakewood': [{ state: 'CO', label: 'Lakewood, CO' }, { state: 'OH', label: 'Lakewood, OH' }],
  'bellevue': [{ state: 'NE', label: 'Bellevue, NE' }, { state: 'WA', label: 'Bellevue, WA' }],
  'decatur': [{ state: 'AL', label: 'Decatur, AL' }, { state: 'GA', label: 'Decatur, GA (Atlanta)' }],
  'georgetown': [{ state: 'DC', label: 'Georgetown, Washington DC' }, { state: 'TX', label: 'Georgetown, TX' }],
  'portland': [{ state: 'OR', label: 'Portland, OR' }, { state: 'ME', label: 'Portland, ME' }],
  'pearl district': [{ state: 'OR', label: 'Pearl District, Portland OR' }, { state: 'TX', label: 'Pearl District, San Antonio TX' }],
  'jackson': [{ state: 'MS', label: 'Jackson, MS' }, { state: 'WY', label: 'Jackson Hole, WY' }],
  'charleston': [{ state: 'SC', label: 'Charleston, SC' }, { state: 'WV', label: 'Charleston, WV' }],
};

// City Search Component
const CitySearch = ({ city, setCity, onLocationDetected, onCitySelect, onAddressGeocoded, onNoCoverage, metroCoords, region = 'usa' }) => {
  // Region scopes the suggestion pool: Japan mode offers only covered JP cities.
  const cityList = region === 'japan' ? JAPAN_CITIES : US_CITIES;
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [matchedCity, setMatchedCity] = useState(null);
  const [uncoveredCity, setUncoveredCity] = useState(null);
  const [requestStatus, setRequestStatus] = useState(null); // null, 'submitting', 'queued', 'downloading'
  const [geoResults, setGeoResults] = useState([]); // Nominatim disambiguation
  const [disambigOptions, setDisambigOptions] = useState([]); // AMBIGUOUS_CITIES pills
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const handleCityRequest = async () => {
    if (!uncoveredCity || requestStatus) return;

    setRequestStatus('submitting');
    try {
      // Fetch existing row to get current count (if any)
      const existing = await supabaseFetch('city_requests', {
        filters: [{ column: 'city_name', operator: 'eq', value: uncoveredCity }],
        select: 'request_count',
        limit: 1,
      });
      const currentCount = existing.length > 0 ? (existing[0].request_count || 0) : 0;

      // Upsert: insert new row or update existing (on_conflict=city_name)
      await supabasePost('city_requests', {
        city_name: uncoveredCity,
        request_count: currentCount + 1,
        last_requested_at: new Date().toISOString(),
        ...(currentCount === 0 && { requested_at: new Date().toISOString() }),
      }, {
        query: 'on_conflict=city_name',
        prefer: 'resolution=merge-duplicates,return=representation',
      });

      if (currentCount + 1 >= 10) {
        setRequestStatus('downloading');
      } else {
        setRequestStatus('queued');
      }
      setLocationError(null);
    } catch (err) {
      console.error('Failed to submit city request:', err);
      setRequestStatus('queued');
      setLocationError(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setCity(value);
    setLocationError(null);
    setMatchedCity(null);
    setUncoveredCity(null);
    setRequestStatus(null);
    setGeoResults([]);
    setDisambigOptions([]);

    if (value.length > 0) {
      const filtered = cityList.filter(c =>
        c.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 6);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setCity(suggestion);
    setShowSuggestions(false);
    setMatchedCity(suggestion);
    // Immediately trigger data fetch - no debounce needed for explicit selection
    onCitySelect?.(suggestion);
  };

  // Resolve a single geocoded result (used by handleKeyDown and disambiguation picker)
  const resolveGeoResult = (result) => {
    const addr = result.address || {};
    const cityName = addr.city || addr.town || addr.village || addr.municipality || '';
    const state = addr.state || '';
    const coords = { lat: parseFloat(result.lat), lng: parseFloat(result.lon) };
    const searchCity = cityName && state ? `${cityName}, ${state}` : cityName;

    // Helper: find a display label from CITIES, or construct one from the metro name
    const labelForMetro = (metroName) => {
      // Try exact match in CITIES first (e.g. "Orlando" → "Orlando, FL")
      const exact = cityList.find(c => c.split(',')[0].trim().toLowerCase() === metroName.toLowerCase());
      if (exact) return exact;
      // Construct label from geocoded state if available
      if (state) return `${metroName}, ${state}`;
      return metroName;
    };

    // Japan region: coordinate-snap to a covered JP metro before the US-bounded
    // nearestMetro paths (which return null for Japanese coords).
    if (region === 'japan') {
      const jpMetro = nearestJapanMetro(coords.lat, coords.lng);
      if (jpMetro) {
        const label = `${jpMetro}, Japan`;
        setMatchedCity(label);
        setUncoveredCity(null);
        setGeoResults([]);
        onCitySelect?.(label, coords);
        onAddressGeocoded?.(coords);
        return;
      }
    }

    // Guard: empty cityName — use coordinates to find nearest metro
    if (!cityName) {
      const nearest = nearestMetro(coords.lat, coords.lng, metroCoords);
      if (nearest) {
        const label = labelForMetro(nearest);
        setCity(label);
        setMatchedCity(label);
        setGeoResults([]);
        onCitySelect?.(label, coords);
        onAddressGeocoded?.(coords);
        return;
      }
      setLocationError('Could not determine city. Try "City, State" format.');
      return;
    }

    // Always use coordinate-based resolution first — handles ambiguous cities
    // (e.g. Portland OR vs ME) by matching the nearest metro to the geocoded coords
    const nearest = nearestMetro(coords.lat, coords.lng, metroCoords);
    if (nearest) {
      const label = labelForMetro(nearest);
      // Keep the user's typed input (address or suburb) — don't overwrite with metro name
      // The "Searching in: Jacksonville, FL" subtitle handles the metro context
      setMatchedCity(label);
      setUncoveredCity(null);
      setGeoResults([]);
      onCitySelect?.(label, coords);
      onAddressGeocoded?.(coords);
      return;
    }

    // Fallback: string match if coord resolution missed (e.g. international cities)
    const matchedCityFound = cityList.find(c => c.toLowerCase().includes(cityName.toLowerCase()));
    if (matchedCityFound) {
      setMatchedCity(matchedCityFound);
      setUncoveredCity(null);
      setGeoResults([]);
      onCitySelect?.(matchedCityFound, coords);
      onAddressGeocoded?.(coords);
    } else {
      setMatchedCity(null);
      setUncoveredCity(searchCity);
      setGeoResults([]);
      setLocationError(`We don't have coverage in ${searchCity} yet.`);
      onNoCoverage?.(searchCity);
    }
  };

  // Geocode any input (address or city) when Enter is pressed
  const handleKeyDown = async (e) => {
    if (e.key === 'Enter' && city.trim()) {
      setShowSuggestions(false);
      setGeoResults([]);
      setDisambigOptions([]);
      setIsLocating(true);
      setMatchedCity(null);

      // Check if it's a known city first (exact match like "Portland, OR")
      const knownCity = cityList.find(c => c.toLowerCase() === city.toLowerCase());
      if (knownCity) {
        setCity(knownCity);
        setMatchedCity(knownCity);
        onCitySelect?.(knownCity);
        setIsLocating(false);
        return;
      }

      // Check for ambiguous bare city name (e.g. "Portland" without state)
      const ambig = AMBIGUOUS_CITIES[city.trim().toLowerCase()];
      if (ambig) {
        setDisambigOptions(ambig);
        setIsLocating(false);
        return;
      }

      // Otherwise geocode it
      try {
        const encoded = encodeURIComponent(city);
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encoded}&limit=5&addressdetails=1`,
          { headers: { 'User-Agent': 'CarpeApp/1.0' } }
        );
        const data = await response.json();

        if (data && data.length > 1) {
          // Deduplicate by geographic cluster — keep one result per 50mi radius
          const unique = [];
          for (const r of data) {
            const lat = parseFloat(r.lat), lng = parseFloat(r.lon);
            const tooClose = unique.some(u => haversineDistance(lat, lng, parseFloat(u.lat), parseFloat(u.lon)) < 50);
            if (!tooClose) unique.push(r);
          }
          if (unique.length === 1) {
            // All results were the same city — resolve directly
            resolveGeoResult(unique[0]);
          } else {
            setGeoResults(unique);
            setLocationError(null);
          }
        } else if (data && data.length === 1) {
          resolveGeoResult(data[0]);
        } else {
          setLocationError('Address not found. Try a different format.');
        }
      } catch (err) {
        console.error('Geocoding error:', err);
        setLocationError('Error looking up address.');
      }
      setIsLocating(false);
    }
  };

  const handleUseLocation = async () => {
    setIsLocating(true);
    setLocationError(null);

    const handleCoords = async (latitude, longitude) => {
      const coords = { lat: latitude, lng: longitude };
      try {
        // Display label: reverse-geocode for the user's ACTUAL city (e.g. "New Smyrna Beach"),
        // not the nearest metro it rolls up to (e.g. "Daytona Beach"). Downstream data
        // fetching (fetchRestaurantsForCity etc.) routes via coords → resolveMetros →
        // cluster, so the metro partition is still resolved correctly regardless of label.
        let finalCity;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&zoom=12`,
            { headers: { 'User-Agent': 'CarpeApp/1.0' } }
          );
          const data = await response.json();
          const cityName = data.address?.city
            || data.address?.town
            || data.address?.village
            || data.address?.hamlet
            || data.address?.suburb
            || data.address?.county;
          const stateAbbr = data.address?.['ISO3166-2-lvl4']?.split('-')[1];
          const state = stateAbbr || data.address?.state;
          const country = data.address?.country;
          // Japan region: snap to the nearest covered JP metro. A raw reverse
          // geocode ("Shibuya, Japan") would query an empty metro tag.
          if (region === 'japan') {
            const jpMetro = nearestJapanMetro(latitude, longitude);
            if (jpMetro) finalCity = `${jpMetro}, Japan`;
          }
          if (!finalCity && cityName) {
            if (state && country === 'United States') {
              finalCity = `${cityName}, ${state}`;
            } else if (country && country !== cityName) {
              finalCity = `${cityName}, ${country}`;
            } else {
              finalCity = cityName;
            }
          }
        } catch (err) {
          console.warn('Reverse geocode failed, falling back to nearest metro:', err);
        }

        // Fallback only if reverse geocode failed entirely
        if (!finalCity && region === 'japan') {
          const jpMetro = nearestJapanMetro(latitude, longitude);
          if (jpMetro) finalCity = `${jpMetro}, Japan`;
        }
        if (!finalCity) {
          const nearest = nearestMetro(latitude, longitude, metroCoords);
          if (nearest) {
            const cityLabel = cityList.find(c => c.split(',')[0].trim().toLowerCase() === nearest.toLowerCase());
            finalCity = cityLabel || nearest;
          } else {
            finalCity = `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`;
          }
        }

        setCity(finalCity);
        onLocationDetected?.({ latitude, longitude, name: finalCity });
        onAddressGeocoded?.(coords);
        onCitySelect?.(finalCity, coords);
      } catch {
        setCity(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
      }
      setIsLocating(false);
    };

    if (Capacitor.isNativePlatform()) {
      try {
        const permission = await Geolocation.requestPermissions({ permissions: ['location'] });
        if (permission.location === 'granted') {
          const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
          await handleCoords(pos.coords.latitude, pos.coords.longitude);
        } else {
          setIsLocating(false);
          setLocationError('Enable location in Settings > Privacy > Location Services');
          setTimeout(() => inputRef.current?.focus(), 50);
        }
      } catch {
        setIsLocating(false);
        setLocationError('Unable to get location');
      }
    } else {
      if (!navigator.geolocation) {
        setIsLocating(false);
        setLocationError('Geolocation not supported');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          await handleCoords(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          setIsLocating(false);
          setLocationError(
            error.code === 1 ? 'Location access denied — type your city below' : 'Unable to get location'
          );
          setTimeout(() => inputRef.current?.focus(), 50);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    }
  };

  return (
    <div className="city-search" ref={containerRef}>
      <div className="search-input-wrapper">
        <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="input-field"
          placeholder="City or address..."
          value={city}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => city.length > 0 && suggestions.length > 0 && setShowSuggestions(true)}
        />
        {city.length > 0 && (
          <button
            className="location-clear-btn"
            onClick={() => {
              setCity('');
              setSuggestions([]);
              setShowSuggestions(false);
              setLocationError(null);
              setMatchedCity(null);
              setUncoveredCity(null);
              setRequestStatus(null);
              setGeoResults([]);
              setDisambigOptions([]);
              onAddressGeocoded?.(null);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            aria-label="Clear location"
            title="Clear location"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
        <button 
          className={`location-btn ${isLocating ? 'locating' : ''}`}
          onClick={handleUseLocation}
          disabled={isLocating}
          title="Use my location"
        >
          {isLocating ? (
            <svg className="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeLinecap="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="3 11 22 2 13 21 11 13 3 11" />
            </svg>
          )}
        </button>
      </div>
      {locationError && !requestStatus && (
        <div className="location-error">
          {locationError}
          {uncoveredCity && (
            <button 
              className="request-city-btn" 
              onClick={handleCityRequest}
            >
              Request this city
            </button>
          )}
        </div>
      )}
      {requestStatus === 'submitting' && (
        <div className="request-loading">
          <div className="loading-spinner"></div>
          Submitting request...
        </div>
      )}
      {requestStatus === 'queued' && (
        <div className="request-success">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          Request received. We'll add {uncoveredCity} soon.
        </div>
      )}
      {requestStatus === 'downloading' && (
        <div className="request-downloading">
          <div className="loading-spinner"></div>
          Downloading {uncoveredCity}... Check back in a few minutes.
        </div>
      )}
      {matchedCity && !locationError && city !== matchedCity && (
        <div className="matched-city-feedback">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          Searching in: {matchedCity}
        </div>
      )}
      {showSuggestions && (
        <ul className="suggestions-list">
          {suggestions.map((suggestion, idx) => (
            <li key={idx} className="suggestion-item" onClick={() => handleSuggestionClick(suggestion)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {suggestion}
            </li>
          ))}
        </ul>
      )}
      {disambigOptions.length > 0 && (
        <div className="disambig-picker">
          <span className="disambig-label">Did you mean:</span>
          <div className="disambig-pills">
            {disambigOptions.map((opt) => (
              <button key={opt.label} className="filter-pill" onClick={() => {
                setCity(opt.label);
                setDisambigOptions([]);
                setMatchedCity(opt.label);
                onCitySelect?.(opt.label);
              }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {geoResults.length > 0 && (
        <ul className="suggestions-list geo-disambiguation">
          <li className="suggestion-item suggestion-header">Which location did you mean?</li>
          {geoResults.map((result, idx) => {
            const addr = result.address || {};
            const cityName = addr.city || addr.town || addr.village || addr.municipality || '';
            const state = addr.state || '';
            const country = addr.country || '';
            const label = [cityName, state || country].filter(Boolean).join(', ') || result.display_name.split(',').slice(0, 2).join(',');
            return (
              <li key={idx} className="suggestion-item" onClick={() => { setIsLocating(false); resolveGeoResult(result); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// Reel-spin speed knob. All spin timings (fast interval, slowing ramp, land,
// per-reel stagger, result reveal) are divided by this. 1.43 = 1.43x faster.
// Bump/lower this single value to retune the whole spin sequence uniformly.
const SPIN_SPEED_MULTIPLIER = 1.43;

// Wheel Spinner Component
const WheelSpinner = ({ items, spinning, finalIndex, label, onComplete, stopDelay = 0, isLastWheel = false }) => {
  const [displayIndex, setDisplayIndex] = useState(0);
  const [tick, setTick] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle, spinning, slowing, settled
  const [speed, setSpeed] = useState('fast'); // fast, medium, slow, crawl
  const [animDuration, setAnimDuration] = useState(50 / SPIN_SPEED_MULTIPLIER);
  const timeoutRefs = useRef([]);

  // FIX: Capture props in refs to prevent stale closures during animation
  const itemsRef = useRef(items);
  const finalIndexRef = useRef(finalIndex);
  const onCompleteRef = useRef(onComplete);

  // Keep refs updated with latest props
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    finalIndexRef.current = finalIndex;
  }, [finalIndex]);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const clearAllTimeouts = () => {
    timeoutRefs.current.forEach(t => clearTimeout(t));
    timeoutRefs.current = [];
  };

  const addTimeout = (fn, delay) => {
    const t = setTimeout(fn, delay);
    timeoutRefs.current.push(t);
    return t;
  };

  useEffect(() => {
    if (spinning) {
      // FIX: Snapshot values at spin start for stable animation
      const spinItems = itemsRef.current;
      const spinFinalIndex = finalIndexRef.current;

      // Guard against empty items
      if (!spinItems || spinItems.length === 0) {
        onCompleteRef.current?.();
        return;
      }

      clearAllTimeouts();
      setPhase('spinning');
      setSpeed('fast');
      setAnimDuration(50 / SPIN_SPEED_MULTIPLIER);

      let spinCount = 0;
      const spin = () => {
        setDisplayIndex(prev => (prev + 1) % spinItems.length);
        setTick(t => t + 1);
        spinCount++;
        // Play ratchet click every 3rd spin during fast phase
        if (spinCount % 3 === 0) {
          playRatchetClick(0.08);
          triggerHaptic('tick');
        }
        addTimeout(spin, 50 / SPIN_SPEED_MULTIPLIER);
      };

      spin();

      // After stopDelay, begin smooth rolling stop
      addTimeout(() => {
        clearAllTimeouts();
        setPhase('slowing');

        const totalTicks = 18;
        let tickCount = 0;

        const slowSpin = () => {
          tickCount++;

          // Update speed phase for visual effects
          const progress = tickCount / totalTicks;
          if (progress < 0.3) setSpeed('fast');
          else if (progress < 0.6) setSpeed('medium');
          else if (progress < 0.85) setSpeed('slow');
          else setSpeed('crawl');

          // Cubic ease-out curve
          const eased = 1 - Math.pow(1 - progress, 3);
          const interval = (60 + eased * 340) / SPIN_SPEED_MULTIPLIER;
          setAnimDuration(interval);

          setDisplayIndex(prev => (prev + 1) % spinItems.length);
          setTick(t => t + 1);

          // Heavier clunks as it slows, pitch drops
          const volume = 0.15 + progress * 0.2;
          const pitch = 1.2 - progress * 0.4;
          playSlowingClunk(volume, pitch);
          triggerHaptic('tick');

          if (tickCount >= totalTicks) {
            addTimeout(() => {
              setAnimDuration(500 / SPIN_SPEED_MULTIPLIER);
              setSpeed('landing');
              setDisplayIndex(spinFinalIndex);  // FIX: Use captured value
              setTick(t => t + 1);
              setPhase('settled');

              // Play lock sound, or final sound if last wheel
              if (isLastWheel) {
                playFinalSettle();
                triggerHaptic('final');
              } else {
                playWheelLock();
                triggerHaptic('lock');
              }

              addTimeout(() => {
                onCompleteRef.current?.();  // FIX: Use ref to get latest callback
              }, 100 / SPIN_SPEED_MULTIPLIER);
            }, interval);
          } else {
            addTimeout(slowSpin, interval);
          }
        };

        slowSpin();
      }, (400 + stopDelay) / SPIN_SPEED_MULTIPLIER);

    } else {
      if (phase !== 'idle') {
        addTimeout(() => {
          setPhase('idle');
          setSpeed('fast');
        }, 500 / SPIN_SPEED_MULTIPLIER);
      }
    }

    return () => {
      clearAllTimeouts();
    };
  }, [spinning]);

  // Reconcile displayed value if finalIndex changes after the reel has settled
  useEffect(() => {
    if (phase === 'settled') {
      setDisplayIndex(finalIndex);
    }
  }, [finalIndex]);

  const getItemAtOffset = (offset) => {
    const idx = (displayIndex + offset + items.length) % items.length;
    return items[idx] || '—';
  };

  return (
    <div className="wheel-container">
      <span className="wheel-label">{label}</span>
      <div className={`wheel-display phase-${phase} speed-${speed}`}>
        <div
          className="wheel-reel"
          key={tick}
          style={{ animationDuration: `${animDuration}ms` }}
        >
          <span className="wheel-item far">{getItemAtOffset(-2)}</span>
          <span className="wheel-item prev">{getItemAtOffset(-1)}</span>
          <span className="wheel-item current">{getItemAtOffset(0)}</span>
          <span className="wheel-item next">{getItemAtOffset(1)}</span>
          <span className="wheel-item far">{getItemAtOffset(2)}</span>
        </div>
      </div>
    </div>
  );
};

// Filter Pill
const FilterPill = ({ label, active, onClick }) => {
  const isTrending = label === 'Trending';
  return (
    <button 
      className={`filter-pill ${active ? 'active' : ''} ${isTrending ? 'trending' : ''}`} 
      onClick={onClick}
    >
      {label}
    </button>
  );
};

// Profile Panel
const ProfilePanel = ({ user, history, favorites, onUpdateUser, onDeleteHistory, onClearHistory, onDeleteFavorite, onToggleFavorite, isFavorited, onItemClick, onClose, onLogout, onUpdateHomeCity, onDeleteAccount, isAuthUser, authSession, region, onSetRegion }) => {
  // Handle both old local user format and new Supabase auth format
  const userName = isAuthUser 
    ? (user.user_metadata?.full_name || user.email?.split('@')[0] || 'Explorer')
    : (user.name || 'Explorer');
  const userEmail = isAuthUser ? user.email : null;
  const userHomeCity = isAuthUser ? (user.user_metadata?.home_city || '') : (user.homeCity || '');
  const userJoinedAt = isAuthUser ? user.created_at : user.joinedAt;
  
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(userName);
  const [editingHomeCity, setEditingHomeCity] = useState(false);
  const [homeCityInput, setHomeCityInput] = useState(userHomeCity);
  const [homeCityError, setHomeCityError] = useState('');
  const [activeTab, setActiveTab] = useState('favorites');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [cpNewPassword, setCpNewPassword] = useState('');
  const [cpConfirmPassword, setCpConfirmPassword] = useState('');
  const [cpError, setCpError] = useState('');
  const [cpSuccess, setCpSuccess] = useState(false);
  const [mapsPreference, setMapsPreference] = useState(() => {
    try { return localStorage.getItem('carpe_maps_preference') || 'google'; }
    catch (_) { return 'google'; }
  });
  const handleSetMapsPreference = (pref) => {
    setMapsPreference(pref);
    try { localStorage.setItem('carpe_maps_preference', pref); } catch (_) {}
  };
  const isIOS = Capacitor.getPlatform() === 'ios';

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setCpError('');
    if (cpNewPassword !== cpConfirmPassword) {
      setCpError('Passwords do not match');
      return;
    }
    if (cpNewPassword.length < 6) {
      setCpError('Password must be at least 6 characters');
      return;
    }
    try {
      await supabaseUpdatePassword(authSession.access_token, cpNewPassword);
      setCpSuccess(true);
      setCpNewPassword('');
      setCpConfirmPassword('');
      setTimeout(() => {
        setCpSuccess(false);
        setShowChangePassword(false);
      }, 2000);
    } catch (err) {
      setCpError(err.message);
    }
  };

  const handleSaveName = () => {
    if (isAuthUser) {
      // For auth users, we'd need to update user_metadata - for now just local
      onUpdateUser({ ...user, user_metadata: { ...user.user_metadata, full_name: nameInput.trim() || 'Explorer' } });
    } else {
      onUpdateUser({ ...user, name: nameInput.trim() || 'Explorer' });
    }
    setEditingName(false);
  };

  const handleSaveHomeCity = async () => {
    const newHomeCity = homeCityInput.trim();

    if (newHomeCity.length < 3 || !/[a-zA-Z]/.test(newHomeCity)) {
      setHomeCityError('Please enter a valid city name');
      return;
    }

    try {
      const encoded = encodeURIComponent(newHomeCity);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encoded}&limit=1&addressdetails=1`,
        { headers: { 'User-Agent': 'CarpeApp/1.0' } }
      );
      const data = await response.json();
      if (!data || data.length === 0) {
        setHomeCityError('Please enter a valid city name');
        return;
      }
    } catch {
      // Network error — allow save rather than blocking user
    }

    setHomeCityError('');
    if (isAuthUser) {
      onUpdateUser({ ...user, user_metadata: { ...user.user_metadata, home_city: newHomeCity } });
    } else {
      onUpdateUser({ ...user, homeCity: newHomeCity });
    }
    onUpdateHomeCity?.(newHomeCity);
    setEditingHomeCity(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-panel" onClick={e => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        
        <div className="profile-header">
          <div className="avatar">{getInitials(userName)}</div>
          <div className="profile-info">
            {editingName ? (
              <div className="name-edit">
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                  autoFocus
                />
                <button onClick={handleSaveName}>Save</button>
              </div>
            ) : (
              <h2 onClick={() => setEditingName(true)}>
                {userName}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </h2>
            )}
            {userEmail && <p className="user-email">{userEmail}</p>}
            <p className="member-since">Exploring since {formatDate(userJoinedAt)}</p>
          </div>
        </div>

        <div className="profile-stats">
          <div className="stat" onClick={() => setActiveTab('history')}>
            <span className="stat-value">{history.filter(h => h.type === 'restaurant' || h.type === 'delivery').length}</span>
            <span className="stat-label">Meals</span>
          </div>
          <div className="stat" onClick={() => setActiveTab('history')}>
            <span className="stat-value">{history.filter(h => h.type === 'activity' || h.type === 'combo' || h.type === 'binge').length}</span>
            <span className="stat-label">Adventures</span>
          </div>
          <div className="stat" onClick={() => setActiveTab('favorites')}>
            <span className="stat-value">{favorites.length}</span>
            <span className="stat-label">Favorites</span>
          </div>
        </div>

        <div className="profile-tabs">
          <button className={`profile-tab ${activeTab === 'favorites' ? 'active' : ''}`} onClick={() => setActiveTab('favorites')}>Favorites</button>
          <button className={`profile-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>History</button>
          <button className={`profile-tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>
        </div>

        {activeTab === 'history' && (
          <div className="history-section">
            {history.length === 0 ? (
              <div className="empty-history">
                <p>No history yet.</p>
                <p className="empty-sub">Start exploring to build your history!</p>
              </div>
            ) : (
              <>
                <div className="history-list">
                  {history.slice().reverse().map((item) => {
                    // Generate URLs based on type
                    let url = null;
                    let urlLabel = '';
                    let uberUrl = null;
                    let lyftUrl = null;
                    
                    if (item.type === 'restaurant') {
                      url = getMapsUrl(`${item.name} restaurant ${item.city}`);
                      urlLabel = 'View on Maps';
                      const dest = encodeURIComponent(`${item.name}, ${item.city}`);
                      uberUrl = `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${dest}`;
                      lyftUrl = `https://lyft.com/ride?destination[address]=${dest}`;
                    } else if (item.type === 'activity') {
                      url = getMapsUrl(`${item.name} ${item.city}`);
                      urlLabel = 'View on Maps';
                      const dest = encodeURIComponent(`${item.name}, ${item.city}`);
                      uberUrl = `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${dest}`;
                      lyftUrl = `https://lyft.com/ride?destination[address]=${dest}`;
                    } else if (item.type === 'combo') {
                      url = getMapsUrl(`${item.activity.name} ${item.city}`);
                      urlLabel = 'View on Maps';
                      const dest = encodeURIComponent(`${item.activity.name}, ${item.city}`);
                      uberUrl = `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${dest}`;
                      lyftUrl = `https://lyft.com/ride?destination[address]=${dest}`;
                    } else if (item.type === 'binge') {
                      const searchName = encodeURIComponent(item.name);
                      if (item.platform === 'Netflix') url = `https://www.netflix.com/search?q=${searchName}`;
                      else if (item.platform === 'Hulu') url = `https://www.hulu.com/search?q=${searchName}`;
                      else if (item.platform === 'Disney+') url = `https://www.disneyplus.com/search/${searchName}`;
                      else if (item.platform === 'Max') url = `https://play.max.com/search?q=${searchName}`;
                      else if (item.platform === 'Prime') url = `https://www.amazon.com/s?k=${searchName}&i=instant-video`;
                      else if (item.platform === 'Apple TV+') url = `https://tv.apple.com/search?term=${searchName}`;
                      else if (item.platform === 'Peacock') url = `https://www.peacocktv.com/search?q=${searchName}`;
                      else if (item.platform === 'Paramount+') url = `https://www.paramountplus.com/search/?q=${searchName}`;
                      else if (item.platform === 'Crunchyroll') url = `https://www.crunchyroll.com/search?q=${searchName}`;
                      urlLabel = `Watch on ${item.platform}`;
                    } else if (item.type === 'delivery') {
                      url = `https://www.google.com/search?q=${encodeURIComponent(`${item.name} delivery ${item.city}`)}`;
                      urlLabel = 'Order Now';
                    }
                    
                    const imdbUrl = item.type === 'binge' ? `https://www.imdb.com/find/?q=${encodeURIComponent(item.name)}&s=tt&ttype=tv,ft` : null;
                    
                    return (
                      <div key={item.id} className="history-item" onClick={() => onItemClick && onItemClick(item)} style={{ cursor: 'pointer' }}>
                        <button
                          className={`history-heart-btn ${isFavorited && isFavorited(item.type === 'combo' ? `${item.activity?.name} + ${item.restaurant?.name}` : item.name) ? 'favorited' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onToggleFavorite) {
                              // For combo items, build a flat favorite shape that matches existing logic
                              if (item.type === 'combo') {
                                onToggleFavorite({
                                  ...item,
                                  name: `${item.activity?.name} + ${item.restaurant?.name}`,
                                });
                              } else {
                                onToggleFavorite(item);
                              }
                            }
                          }}
                          title={isFavorited && isFavorited(item.type === 'combo' ? `${item.activity?.name} + ${item.restaurant?.name}` : item.name) ? 'Remove from Favorites' : 'Add to Favorites'}
                          aria-label="Toggle favorite"
                        >
                          <svg viewBox="0 0 24 24" width="18" height="18" fill={isFavorited && isFavorited(item.type === 'combo' ? `${item.activity?.name} + ${item.restaurant?.name}` : item.name) ? '#E91E63' : 'none'} stroke={isFavorited && isFavorited(item.type === 'combo' ? `${item.activity?.name} + ${item.restaurant?.name}` : item.name) ? '#E91E63' : 'currentColor'} strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                          </svg>
                        </button>
                        <div className="history-icon">
                          {item.type === 'restaurant' && '🍴'}
                          {item.type === 'activity' && '🧭'}
                          {item.type === 'combo' && '✦'}
                          
                          
                        </div>
                        <div className="history-content">
                          <div className="history-name">
                            {item.type === 'combo' ? `${item.activity.name} + ${item.restaurant.name}` : item.name}
                          </div>
                          <div className="history-meta">
                            {item.type === 'binge' ? item.platform : item.city} · {formatDate(item.date)}
                          </div>
                          <div className="history-actions" onClick={(e) => e.stopPropagation()}>
                            {url && (
                              <a href={url} target="_blank" rel="noopener noreferrer" className="history-link">
                                {urlLabel} →
                              </a>
                            )}
                            {imdbUrl && (
                              <a href={imdbUrl} target="_blank" rel="noopener noreferrer" className="history-ride-link imdb">
                                IMDb
                              </a>
                            )}
                            {uberUrl && (
                              <a href={uberUrl} target="_blank" rel="noopener noreferrer" className="history-ride-link uber">
                                Uber
                              </a>
                            )}
                            {lyftUrl && !isJapanCity(item.city) && (
                              <a href={lyftUrl} target="_blank" rel="noopener noreferrer" className="history-ride-link lyft">
                                Lyft
                              </a>
                            )}
                          </div>
                        </div>
                        <button className="delete-btn" onClick={(e) => { e.stopPropagation(); onDeleteHistory(item.id); }} title="Remove">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6 6 18M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
                {history.length > 0 && (
                  <button className="clear-history-btn" onClick={onClearHistory}>Clear History</button>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div className="history-section">
            {favorites.length === 0 ? (
              <div className="empty-history">
                <p>Nothing saved yet.</p>
                <p className="empty-sub">Tap the heart on any spin result to save it.</p>
              </div>
            ) : (
              <div className="history-list">
                {favorites.slice().reverse().map((item) => {
                  let url = null;
                  let urlLabel = '';
                  let uberUrl = null;
                  let lyftUrl = null;
                  
                  if (item.type === 'combo' && item.activity) {
                    // For combo items, link to the activity location
                    url = getMapsUrl(`${item.activity.name} ${item.city || ''}`);
                    urlLabel = 'View on Maps';
                    const dest = encodeURIComponent(`${item.activity.name}, ${item.city || ''}`);
                    uberUrl = `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${dest}`;
                    lyftUrl = `https://lyft.com/ride?destination[address]=${dest}`;
                  } else if (item.type === 'restaurant' || item.cuisine) {
                    url = getMapsUrl(`${item.name} restaurant ${item.city || ''}`);
                    urlLabel = 'View on Maps';
                    const dest = encodeURIComponent(`${item.name}, ${item.city || ''}`);
                    uberUrl = `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${dest}`;
                    lyftUrl = `https://lyft.com/ride?destination[address]=${dest}`;
                  } else if (item.type === 'activity' || item.category) {
                    url = getMapsUrl(`${item.name} ${item.city || ''}`);
                    urlLabel = 'View on Maps';
                    const dest = encodeURIComponent(`${item.name}, ${item.city || ''}`);
                    uberUrl = `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${dest}`;
                    lyftUrl = `https://lyft.com/ride?destination[address]=${dest}`;
                  } else if (item.type === 'binge' || item.platform) {
                    const searchName = encodeURIComponent(item.name);
                    if (item.platform === 'Netflix') url = `https://www.netflix.com/search?q=${searchName}`;
                    else if (item.platform === 'Hulu') url = `https://www.hulu.com/search?q=${searchName}`;
                    else if (item.platform === 'Disney+') url = `https://www.disneyplus.com/search/${searchName}`;
                    else if (item.platform === 'Max') url = `https://play.max.com/search?q=${searchName}`;
                    else if (item.platform === 'Prime') url = `https://www.amazon.com/s?k=${searchName}&i=instant-video`;
                    else if (item.platform === 'Apple TV+') url = `https://tv.apple.com/search?term=${searchName}`;
                    else if (item.platform === 'Peacock') url = `https://www.peacocktv.com/search?q=${searchName}`;
                    else if (item.platform === 'Paramount+') url = `https://www.paramountplus.com/search/?q=${searchName}`;
                    else if (item.platform === 'Crunchyroll') url = `https://www.crunchyroll.com/search?q=${searchName}`;
                    urlLabel = `Watch on ${item.platform}`;
                  } else if (item.type === 'delivery') {
                    url = `https://www.google.com/search?q=${encodeURIComponent(`${item.name} delivery ${item.city || ''}`)}`;
                    urlLabel = 'Order Now';
                  }
                  
                  const imdbUrl = (item.type === 'binge' || item.platform) ? `https://www.imdb.com/find/?q=${encodeURIComponent(item.name)}&s=tt&ttype=tv,ft` : null;
                  
                  return (
                    <div key={item.id} className="history-item favorite-item" onClick={() => onItemClick && onItemClick(item)} style={{ cursor: 'pointer' }}>
                      <div className="history-icon favorite-icon">
                        {item.type === 'restaurant' && '🍴'}
                        {item.type === 'activity' && '🧭'}
                        {item.type === 'combo' && '✦'}
                        
                        
                        {!item.type && (item.cuisine ? '🍴' : '🧭')}
                      </div>
                      <div className="history-content">
                        <div className="history-name">
                          {item.type === 'combo' && item.activity ? `${item.activity.name} + ${item.restaurant.name}` : item.name}
                          {item.trending && <span className="trending-badge-xs">Trending</span>}
                        </div>
                        <div className="history-meta">
                          {item.type === 'combo' && item.activity && `${item.activity.category} + ${item.restaurant.cuisine}`}
                          {item.type !== 'combo' && item.cuisine && `${item.cuisine} · ${item.price}`}
                          {item.type !== 'combo' && item.category && `${item.category} · ${item.priceRange}`}
                          {item.platform && `${item.genre} · ${item.platform}`}
                          {item.city && ` · ${item.city}`}
                        </div>
                        <div className="history-actions" onClick={(e) => e.stopPropagation()}>
                          {url && (
                            <a href={url} target="_blank" rel="noopener noreferrer" className="history-link">
                              {urlLabel} →
                            </a>
                          )}
                          {imdbUrl && (
                            <a href={imdbUrl} target="_blank" rel="noopener noreferrer" className="history-ride-link imdb">
                              IMDb
                            </a>
                          )}
                          {uberUrl && (
                            <a href={uberUrl} target="_blank" rel="noopener noreferrer" className="history-ride-link uber">
                              Uber
                            </a>
                          )}
                          {lyftUrl && !isJapanCity(item.city) && (
                            <a href={lyftUrl} target="_blank" rel="noopener noreferrer" className="history-ride-link lyft">
                              Lyft
                            </a>
                          )}
                        </div>
                      </div>
                      <button className="delete-btn" onClick={(e) => { e.stopPropagation(); onDeleteFavorite(item.id); }} title="Remove from favorites">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-section">
            <div className="home-base-row">
              <span className="home-base-label">Home Base:</span>
              {editingHomeCity ? (
                <div className="home-city-edit-wrapper">
                  <div className="home-city-edit-inline">
                    <input
                      type="text"
                      value={homeCityInput}
                      onChange={(e) => setHomeCityInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveHomeCity()}
                      placeholder="City, State"
                      autoFocus
                    />
                    <button onClick={handleSaveHomeCity}>✓</button>
                    <button className="cancel-btn" onClick={() => { setEditingHomeCity(false); setHomeCityInput(userHomeCity); setHomeCityError(''); }}>✕</button>
                  </div>
                  {homeCityError && <div className="home-city-error">{homeCityError}</div>}
                </div>
              ) : (
                <span className="home-base-value" onClick={() => setEditingHomeCity(true)}>
                  {userHomeCity || 'Tap to set'}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </span>
              )}
            </div>

            <div className="home-base-row maps-pref-row">
              <span className="home-base-label">Region:</span>
              <div className="maps-pref-toggle">
                <button
                  type="button"
                  className={`maps-pref-btn ${region === 'usa' ? 'active' : ''}`}
                  onClick={() => onSetRegion?.('usa')}
                >
                  USA
                </button>
                <button
                  type="button"
                  className={`maps-pref-btn ${region === 'japan' ? 'active' : ''}`}
                  onClick={() => onSetRegion?.('japan')}
                >
                  Japan
                </button>
              </div>
            </div>

            {isIOS && (
              <div className="home-base-row maps-pref-row">
                <span className="home-base-label">Default Maps:</span>
                <div className="maps-pref-toggle">
                  <button
                    type="button"
                    className={`maps-pref-btn ${mapsPreference === 'google' ? 'active' : ''}`}
                    onClick={() => handleSetMapsPreference('google')}
                  >
                    Google
                  </button>
                  <button
                    type="button"
                    className={`maps-pref-btn ${mapsPreference === 'apple' ? 'active' : ''}`}
                    onClick={() => handleSetMapsPreference('apple')}
                  >
                    Apple
                  </button>
                </div>
              </div>
            )}
            
            {isAuthUser && (
              <div className="sync-status">
                <span className="sync-icon">☁️</span>
                <span>Your favorites and history sync across devices</span>
              </div>
            )}

            {isAuthUser && authSession && (
              <div className="change-password-section">
                {!showChangePassword ? (
                  <button className="auth-link-btn" style={{ textAlign: 'left', padding: '4px 0' }} onClick={() => { setShowChangePassword(true); setCpError(''); setCpSuccess(false); }}>
                    Change Password
                  </button>
                ) : (
                  <div className="change-password-form">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>Change Password</span>
                      <button className="cancel-btn" onClick={() => { setShowChangePassword(false); setCpError(''); setCpNewPassword(''); setCpConfirmPassword(''); }}>✕</button>
                    </div>
                    {cpSuccess ? (
                      <div className="auth-success" style={{ padding: '8px 0' }}>
                        <span>✓ Password updated!</span>
                      </div>
                    ) : (
                      <form onSubmit={handleChangePassword}>
                        {cpError && <div className="auth-error" style={{ marginBottom: 8 }}>{cpError}</div>}
                        <div className="auth-field">
                          <label>New Password</label>
                          <input
                            type="password"
                            value={cpNewPassword}
                            onChange={e => setCpNewPassword(e.target.value)}
                            placeholder="Min 6 characters"
                            minLength={6}
                            required
                            autoFocus
                          />
                        </div>
                        <div className="auth-field">
                          <label>Confirm Password</label>
                          <input
                            type="password"
                            value={cpConfirmPassword}
                            onChange={e => setCpConfirmPassword(e.target.value)}
                            placeholder="••••••••"
                            minLength={6}
                            required
                          />
                        </div>
                        <button type="submit" className="auth-submit-btn" style={{ marginTop: 4 }}>Update Password</button>
                      </form>
                    )}
                  </div>
                )}
              </div>
            )}

            <a
              href="https://carpe-app.com/#biz-login" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="business-link"
            >
              <div className="business-link-content">
                <span className="business-link-icon">✦</span>
                <div>
                  <div className="business-link-title">Have a business?</div>
                  <div className="business-link-desc">Get featured on Carpe</div>
                </div>
              </div>
              <span className="business-link-arrow">→</span>
            </a>
            
            <button className="logout-btn" onClick={onLogout}>Sign Out</button>
            
            {isAuthUser && onDeleteAccount && (
              <button className="delete-account-btn" onClick={onDeleteAccount}>
                Delete Account
              </button>
            )}
            
            <p className="settings-footer">
              Carpe may earn commissions from partner links at no extra cost to you.
            </p>
            <p className="settings-footer">
              Place data from Google. Streaming data from TMDB — this product uses the
              TMDB API but is not endorsed or certified by TMDB.
            </p>
            <p className="settings-footer" style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Build: 2026-04-26-A
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Onboarding Modal
// First-launch tutorial: 4-slide walkthrough explaining the app
const BrandSparkle = ({ size = 64 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="carpeTutSparkleGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#D86E50" />
        <stop offset="100%" stopColor="#A8482E" />
      </linearGradient>
    </defs>
    <path
      d="M50 2 C 53 36, 64 47, 98 50 C 64 53, 53 64, 50 98 C 47 64, 36 53, 2 50 C 36 47, 47 36, 50 2 Z"
      fill="url(#carpeTutSparkleGrad)"
    />
  </svg>
);

const HowToUseModal = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const touchStartX = useRef(null);

  const slides = [
    {
      key: 'location',
      visual: (
        <div className="tut-phone">
          <img src="/tutorial/tutorial-1.png" alt="Choose your location" />
        </div>
      ),
      title: 'Choose your location',
      body: 'Type a city or tap the arrow to use your current spot.'
    },
    {
      key: 'filters',
      visual: (
        <div className="tut-phone">
          <img src="/tutorial/tutorial-2.png" alt="Pick your filters" />
        </div>
      ),
      title: 'Pick your filters',
      body: 'Dial in price, cuisine, and vibe — or skip them all and let fate decide.'
    },
    {
      key: 'spin',
      visual: (
        <div className="tut-phone">
          <img src="/tutorial/tutorial-3.png" alt="Spin the wheel" />
        </div>
      ),
      title: 'Spin the wheel',
      body: 'Hit Discover and three reels line up your next pick in seconds.'
    },
    {
      key: 'carpe',
      visual: (
        <div className="tut-phone">
          <img src="/tutorial/tutorial-4.png" alt="Carpe your city" />
        </div>
      ),
      title: 'Carpe your city!',
      body: 'Save, share, or spin again. Never stop exploring.'
    }
  ];

  const isLast = step === slides.length - 1;
  const next = () => isLast ? onComplete() : setStep(s => s + 1);
  const prev = () => setStep(s => Math.max(0, s - 1));
  const skip = () => onComplete();

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx < -50) next();
    else if (dx > 50) prev();
    touchStartX.current = null;
  };

  const current = slides[step];

  return (
    <div className="tut-overlay">
      <div
        className="tut-modal"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <button className="tut-skip-corner" onClick={skip} aria-label="Skip tutorial">Skip</button>

        <div className="tut-visual" key={current.key}>{current.visual}</div>

        <h2 className="tut-title">{current.title}</h2>
        <p className="tut-body">{current.body}</p>

        <div className="tut-dots" role="tablist">
          {slides.map((s, i) => (
            <button
              key={s.key}
              className={`tut-dot ${i === step ? 'active' : ''}`}
              onClick={() => setStep(i)}
              aria-label={`Go to slide ${i + 1}`}
              role="tab"
              aria-selected={i === step}
            />
          ))}
        </div>

        <div className="tut-actions">
          <button className="tut-next" onClick={next}>
            {isLast ? "Let’s go" : 'Next'}
          </button>
          {step > 0 ? (
            <button className="tut-back" onClick={prev}>Back</button>
          ) : <span className="tut-back-spacer" />}
        </div>
      </div>
    </div>
  );
};

const OnboardingModal = ({ onComplete }) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');

  const handleSubmit = () => {
    const userData = {
      name: name.trim() || 'Explorer',
      email: email.trim(),
      homeCity: homeCity.trim(),
      age: age ? parseInt(age, 10) : null,
      gender: gender || null,
      joinedAt: new Date().toISOString()
    };

    // Fire-and-forget: send demographics to Supabase for aggregate analytics
    if (userData.age || userData.gender) {
      const userHash = btoa(
        (userData.email || userData.name || 'anon') + '-' + navigator.userAgent.slice(0, 20)
      ).replace(/[^a-zA-Z0-9]/g, '').slice(0, 32);

      fetch(`${SUPABASE_URL}/rest/v1/user_demographics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Prefer': 'resolution=merge-duplicates'
        },
        body: JSON.stringify({
          user_hash: userHash,
          age: userData.age,
          gender: userData.gender,
          home_city: userData.homeCity || null
        })
      }).catch(() => {}); // silent — don't block onboarding
    }

    onComplete(userData);
  };

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-modal">
        <div className="onboarding-brand">
          <span className={`brand-icon ${isSpinning ? 'spinning' : ''}`} onClick={() => { setIsSpinning(true); setTimeout(() => setIsSpinning(false), 500); }}>✦</span>
          <h1>Carpe</h1>
        </div>
        <p className="onboarding-tagline">Seize the day, one spin at a time.</p>
        
        <div className="onboarding-form">
          <div className="form-group">
            <label>What should we call you?</label>
            <input type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Email (optional)</label>
            <input type="email" placeholder="hello@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Where's home?</label>
            <input type="text" placeholder="City (optional)" value={homeCity} onChange={(e) => setHomeCity(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Age (optional)</label>
            <input
              type="number"
              placeholder="25"
              min="13"
              max="120"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              style={{ MozAppearance: 'textfield' }}
            />
          </div>
          <div className="form-group">
            <label>Gender (optional)</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              style={{
                appearance: 'none',
                WebkitAppearance: 'none',
                background: `var(--bg-card) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' fill='none' stroke='%236B6560' stroke-width='2'/%3E%3C/svg%3E") no-repeat right 14px center`,
                paddingRight: '36px'
              }}
            >
              <option value="">Prefer not to say</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
        </div>
        
        <button className="onboarding-btn" onClick={handleSubmit}>Begin Exploring</button>
        <button className="skip-btn" onClick={() => onComplete({ name: 'Explorer', joinedAt: new Date().toISOString() })}>Skip for now</button>
      </div>
    </div>
  );
};

// Sponsor Portal Modal (In-App Business Signup)
const SponsorPortalModal = ({ onClose }) => {
  const [step, setStep] = useState('intro'); // intro, form, verify, success
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    listingName: '',
    city: '',
    state: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.businessName || !formData.email || !formData.listingName) {
      setError('Please fill in all required fields.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // 1. Create sponsor record
      const [sponsor] = await supabasePost('sponsors', {
        email: formData.email.toLowerCase().trim(),
        business_name: formData.businessName.trim(),
        contact_name: formData.contactName.trim() || null,
        phone: formData.phone.trim() || null,
        website: formData.website.trim() || null,
        plan: 'free',
      });
      
      if (!sponsor || !sponsor.id) {
        throw new Error('Failed to create sponsor record');
      }
      
      // 2. Create listing claim
      await supabasePost('sponsor_listings', {
        sponsor_id: sponsor.id,
        listing_name: formData.listingName.trim(),
        listing_type: 'restaurant', // Default, can be changed later
        city: formData.city.trim() || null,
        state: formData.state.trim().toUpperCase() || null,
        claim_status: 'pending',
        boost_level: 1,
        promoted: false,
      });
      
      setStep('success');
    } catch (err) {
      console.error('Sponsor submission error:', err);
      if (err.message.includes('duplicate') || err.message.includes('unique')) {
        setError('An account with this email already exists. Please use a different email or contact support.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  };

  return (
    <div className="sponsor-modal-overlay" onClick={onClose}>
      <div className="sponsor-modal" onClick={e => e.stopPropagation()}>
        <button className="sponsor-modal-close" onClick={onClose}>×</button>
        
        {step === 'intro' && (
          <div className="sponsor-intro">
            <div className="sponsor-icon">✦</div>
            <h2>Get Your Business on Carpe</h2>
            <p>Reach thousands of locals looking for their next adventure. Carpe helps people discover restaurants, activities, and events in their area.</p>
            
            <div className="sponsor-tiers">
              <div className="sponsor-tier">
                <div className="tier-name">Free</div>
                <div className="tier-price">$0</div>
                <div className="tier-desc">Standard listing visibility</div>
              </div>
              <div className="sponsor-tier featured">
                <div className="tier-badge">Popular</div>
                <div className="tier-name">Basic</div>
                <div className="tier-price">$29<span>/mo</span></div>
                <div className="tier-desc">3x visibility + Sponsored badge</div>
              </div>
              <div className="sponsor-tier">
                <div className="tier-name">Premium</div>
                <div className="tier-price">$99<span>/mo</span></div>
                <div className="tier-desc">10x visibility + Featured badge</div>
              </div>
            </div>
            
            <button className="sponsor-cta" onClick={() => setStep('form')}>Get Started — It's Free</button>
            <p className="sponsor-note">Start with a free listing, upgrade anytime.</p>
          </div>
        )}
        
        {step === 'form' && (
          <div className="sponsor-form">
            <h2>Claim Your Business</h2>
            <p>Tell us about your business and the listing you want to claim.</p>
            
            {error && <div className="sponsor-error">{error}</div>}
            
            <div className="sponsor-form-grid">
              <div className="form-group">
                <label>Business Name *</label>
                <input 
                  type="text" 
                  placeholder="Your Business Name"
                  value={formData.businessName}
                  onChange={e => handleInputChange('businessName', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Your Name</label>
                <input 
                  type="text" 
                  placeholder="Contact Name"
                  value={formData.contactName}
                  onChange={e => handleInputChange('contactName', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input 
                  type="email" 
                  placeholder="hello@business.com"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input 
                  type="tel" 
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={e => handleInputChange('phone', e.target.value)}
                />
              </div>
              <div className="form-group full-width">
                <label>Website</label>
                <input 
                  type="url" 
                  placeholder="https://yourbusiness.com"
                  value={formData.website}
                  onChange={e => handleInputChange('website', e.target.value)}
                />
              </div>
              <div className="form-group full-width">
                <label>Listing Name on Carpe *</label>
                <input 
                  type="text" 
                  placeholder="The name as it appears in Carpe"
                  value={formData.listingName}
                  onChange={e => handleInputChange('listingName', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>City</label>
                <input 
                  type="text" 
                  placeholder="City"
                  value={formData.city}
                  onChange={e => handleInputChange('city', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input 
                  type="text" 
                  placeholder="State"
                  value={formData.state}
                  onChange={e => handleInputChange('state', e.target.value)}
                />
              </div>
            </div>
            
            <div className="sponsor-form-actions">
              <button className="sponsor-back" onClick={() => setStep('intro')}>Back</button>
              <button className="sponsor-cta" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Claim'}
              </button>
            </div>
          </div>
        )}
        
        {step === 'success' && (
          <div className="sponsor-success">
            <div className="success-icon">✓</div>
            <h2>Claim Submitted</h2>
            <p>We've received your business claim. Our team will verify your ownership and reach out within 24-48 hours.</p>
            <p className="success-email">Confirmation sent to <strong>{formData.email}</strong></p>
            <button className="sponsor-cta" onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
};

// Result Cards
const RestaurantCard = ({ restaurant, city, distance, onDismiss, onSpinAgain, onSave, onShare, isSaved }) => {
  if (!restaurant) return null;
  
  // Always use business name + city for Google Maps (shows actual listing, not just a pin)
  const mapsQuery = restaurant.address 
    ? `${restaurant.name}, ${restaurant.address}`
    : `${restaurant.name} restaurant ${city}`;
  const mapsUrl = getMapsUrl(mapsQuery);
  
  const destination = restaurant.address || `${restaurant.name}, ${city}`;
  const uberUrl = `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${encodeURIComponent(destination)}`;
  const lyftUrl = `https://lyft.com/ride?destination[address]=${encodeURIComponent(destination)}`;
  const doordashUrl = `https://www.doordash.com/search/store/${encodeURIComponent(restaurant.name)}`;
  const ubereatsUrl = `https://www.ubereats.com/search?q=${encodeURIComponent(restaurant.name)}`;
  const grubhubUrl = `https://www.grubhub.com/search?queryText=${encodeURIComponent(restaurant.name)}`;
  
  // Map style to "upscale", "casual", etc.
  const styleLabel = restaurant.style || restaurant.ambiance || 'Restaurant';
  
  // Generate description if not present
  const description = restaurant.description || 
    `${restaurant.cuisine} cuisine in a ${(restaurant.ambiance || restaurant.style || 'welcoming').toLowerCase()} setting. ${restaurant.trending ? 'A local favorite.' : ''}`;
  
  return (
    <div className="result-overlay" onClick={onDismiss}>
      <div className="result-card-v2 restaurant-card-v2" onClick={e => e.stopPropagation()}>
        {/* Gradient background */}
        <div className="card-gradient-bg"></div>

        {/* Close button */}
        <button className="card-close-x" onClick={onDismiss} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="card-header-v2">
          <span className="card-brand">carpe</span>
          <span className="card-tab-badge">EAT</span>
        </div>
        
        {/* Category pill */}
        <div className="card-category-pill">
          {restaurant.cuisine?.toUpperCase()} • {styleLabel?.toUpperCase()}
          {restaurant.sponsored && <span className="sponsored-dot">★</span>}
        </div>
        
        {/* Content */}
        <div className="card-content-v2">
          <h2 className="card-name-v2">{restaurant.name}</h2>
          
          <div className="card-meta-v2">
            <span>{restaurant.cuisine}</span>
            <span className="meta-dot">·</span>
            <span>{restaurant.price}</span>
            {distance && (
              <>
                <span className="meta-dot">·</span>
                <span>{isJapanCity(city)
                  ? `${(distance * 1.609344).toFixed(1)} km`
                  : `${distance.toFixed(1)} mi`}</span>
              </>
            )}
          </div>
          
          <p className="card-description-v2">{description}</p>
          
          {/* Action buttons */}
          <div className="card-actions-v2">
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="action-btn-v2 primary">
              Info
            </a>
            <button 
              className={`action-btn-v2 outline ${isSaved ? 'saved' : ''}`} 
              onClick={() => onSave({ ...restaurant, type: 'restaurant', city })}
            >
              {isSaved ? 'Saved' : 'Save'}
            </button>
            <button
              className="action-btn-v2 outline share"
              onClick={() => onShare && onShare({ ...restaurant, type: 'restaurant', city })}
              aria-label="Share"
            >
              Share
            </button>
          </div>
          
          {/* Affiliate badges — DoorDash/Lyft have no Japan coverage */}
          <div className="affiliate-badges-v2">
            {!isJapanCity(city) && (
              <a href={doordashUrl} target="_blank" rel="noopener noreferrer" className="affiliate-badge">
                DOORDASH
              </a>
            )}
            <a href={ubereatsUrl} target="_blank" rel="noopener noreferrer" className="affiliate-badge">
              UBER EATS
            </a>
            <a href={uberUrl} target="_blank" rel="noopener noreferrer" className="affiliate-badge">
              UBER
            </a>
            {!isJapanCity(city) && (
              <a href={lyftUrl} target="_blank" rel="noopener noreferrer" className="affiliate-badge">
                LYFT
              </a>
            )}
          </div>
          
          {/* Spin again */}
          <button className="spin-again-btn" onClick={onSpinAgain || onDismiss}>
            <span className="spin-icon">✦</span>
            Spin again
          </button>
        </div>
      </div>
    </div>
  );
};

const ActivityCard = ({ activity, city, onDismiss, onSpinAgain, onSave, onShare, isSaved }) => {
  if (!activity) return null;
  
  // Always use business name + city for Google Maps (shows actual listing, not just a pin)
  const mapsQuery = activity.address 
    ? `${activity.name}, ${activity.address}`
    : `${activity.name} ${city}`;
  const mapsUrl = getMapsUrl(mapsQuery);
  
  const destination = activity.address || `${activity.name}, ${city}`;
  const uberUrl = `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${encodeURIComponent(destination)}`;
  const lyftUrl = `https://lyft.com/ride?destination[address]=${encodeURIComponent(destination)}`;
  const viatorUrl = `https://www.viator.com/searchResults/all?text=${encodeURIComponent(activity.name + ' ' + city)}&pid=P00292901&mcid=42383&medium=link`;
  // Prefer the direct Ticketmaster event URL (from scraper) over a generic search
  const ticketmasterUrl = activity.url || `https://www.ticketmaster.com/search?q=${encodeURIComponent(activity.name)}`;
  // For events, "Info" resolves to the ticketing page (rich event details + venue info + buy).
  // For brick-and-mortar activities, "Info" opens the venue on maps.
  const infoUrl = activity.event_date ? ticketmasterUrl : mapsUrl;
  
  // Determine tab label
  const tabLabel = activity.event_date ? 'EVENT' : 'EXPLORE';
  
  // Generate description
  const description = activity.description || 
    `${activity.category} experience ${activity.indoor ? 'indoors' : 'outdoors'}. ${activity.trending ? 'Currently trending in the area.' : ''}`;
  
  return (
    <div className="result-overlay" onClick={onDismiss}>
      <div className="result-card-v2 activity-card-v2" onClick={e => e.stopPropagation()}>
        {/* Gradient background */}
        <div className="card-gradient-bg"></div>

        {/* Close button */}
        <button className="card-close-x" onClick={onDismiss} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="card-header-v2">
          <span className="card-brand">carpe</span>
          <span className="card-tab-badge">{tabLabel}</span>
        </div>
        
        {/* Category pill */}
        <div className="card-category-pill">
          {activity.category?.toUpperCase()} • {activity.indoor ? 'INDOOR' : 'OUTDOOR'}
          {activity.sponsored && <span className="sponsored-dot">★</span>}
        </div>
        
        {/* Content */}
        <div className="card-content-v2">
          <h2 className="card-name-v2">{activity.name}</h2>
          
          <div className="card-meta-v2">
            <span>{activity.category}</span>
            <span className="meta-dot">·</span>
            <span>{activity.priceRange}</span>
            {activity.event_date && (
              <>
                <span className="meta-dot">·</span>
                <span>{activity.event_date}</span>
              </>
            )}
          </div>
          
          <p className="card-description-v2">{description}</p>
          
          {/* Action buttons */}
          <div className="card-actions-v2">
            <a href={infoUrl} target="_blank" rel="noopener noreferrer" className="action-btn-v2 primary">
              Info
            </a>
            <a href={activity.event_date ? ticketmasterUrl : viatorUrl} target="_blank" rel="noopener noreferrer" className="action-btn-v2 outline">
              {activity.event_date ? 'Tickets' : 'Book'}
            </a>
            <button 
              className={`action-btn-v2 outline ${isSaved ? 'saved' : ''}`} 
              onClick={() => onSave({ ...activity, type: 'activity', city })}
            >
              {isSaved ? 'Saved' : 'Save'}
            </button>
            <button
              className="action-btn-v2 outline share"
              onClick={() => onShare && onShare({ ...activity, type: 'activity', city })}
              aria-label="Share"
            >
              Share
            </button>
          </div>
          
          {/* Affiliate badges — Ticketmaster/Lyft have no Japan coverage */}
          <div className="affiliate-badges-v2">
            <a href={viatorUrl} target="_blank" rel="noopener noreferrer" className="affiliate-badge">
              VIATOR
            </a>
            {!isJapanCity(city) && (
              <a href={ticketmasterUrl} target="_blank" rel="noopener noreferrer" className="affiliate-badge">
                TICKETMASTER
              </a>
            )}
            <a href={uberUrl} target="_blank" rel="noopener noreferrer" className="affiliate-badge">
              UBER
            </a>
            {!isJapanCity(city) && (
              <a href={lyftUrl} target="_blank" rel="noopener noreferrer" className="affiliate-badge">
                LYFT
              </a>
            )}
          </div>
          
          {/* Spin again */}
          <button className="spin-again-btn" onClick={onSpinAgain || onDismiss}>
            <span className="spin-icon">✦</span>
            Spin again
          </button>
        </div>
      </div>
    </div>
  );
};

const ComboResultCard = ({ activity, restaurant, city, distance, onDismiss, onSave, onShare, isSaved }) => {
  const [isSpinning, setIsSpinning] = useState(false);
  if (!activity || !restaurant) return null;
  
  // Always use business name + city for Google Maps (shows actual listing, not just a pin)
  const activityMapsQuery = activity.address 
    ? `${activity.name}, ${activity.address}`
    : `${activity.name} ${city}`;
  const activityMapsUrl = getMapsUrl(activityMapsQuery);
  // For events in combo view, route "Info →" to the ticketing page like ActivityCard does.
  const activityTicketUrl = activity.url || `https://www.ticketmaster.com/search?q=${encodeURIComponent(activity.name)}`;
  const activityInfoUrl = activity.event_date ? activityTicketUrl : activityMapsUrl;
  
  const restaurantMapsQuery = restaurant.address 
    ? `${restaurant.name}, ${restaurant.address}`
    : `${restaurant.name} restaurant ${city}`;
  const restaurantMapsUrl = getMapsUrl(restaurantMapsQuery);
  
  const activityDest = activity.address || `${activity.name}, ${city}`;
  const activityUberUrl = `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${encodeURIComponent(activityDest)}`;
  const activityLyftUrl = `https://lyft.com/ride?destination[address]=${encodeURIComponent(activityDest)}`;
  
  const formatDistance = (dist) => {
    if (!dist) return null;
    if (isJapanCity(city)) {
      const km = dist * 1.609344;
      if (km < 1) return `${Math.round(km * 1000)} m away`;
      return `${km.toFixed(1)} km away`;
    }
    if (dist < 0.5) return `${Math.round(dist * 5280)} ft away`;
    return `${dist.toFixed(1)} mi away`;
  };
  
  const comboItem = { type: 'combo', activity, restaurant, city, name: `${activity.name} + ${restaurant.name}` };
  
  return (
    <div className="result-overlay" onClick={onDismiss}>
      <div className="result-card combo-card" onClick={e => e.stopPropagation()}>
        <div className="card-actions">
          <button className="close-btn" onClick={onDismiss}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="combo-header">
          <span className={`brand-icon ${isSpinning ? 'spinning' : ''}`} onClick={() => { setIsSpinning(true); setTimeout(() => setIsSpinning(false), 500); }}>✦</span>
          <h3>Your Perfect Day</h3>
        </div>
        
        <div className="combo-timeline">
          <div className="combo-stop">
            <div className="stop-marker first">1</div>
            <div className="stop-content">
              <div className="stop-label-row">
                <span className="stop-label">{activity.category}</span>
                {activity.trending && <span className="trending-badge-sm">Trending</span>}
              </div>
              <h4>{activity.name}</h4>
              <p>{activity.indoor ? 'Indoor' : 'Outdoor'} · {activity.priceRange}</p>
              <a href={activityInfoUrl} target="_blank" rel="noopener noreferrer">Info →</a>
            </div>
          </div>
          
          <div className="timeline-connector">
            {distance && (
              <span className="distance-badge">{formatDistance(distance)}</span>
            )}
          </div>
          
          <div className="combo-stop">
            <div className="stop-marker second">2</div>
            <div className="stop-content">
              <div className="stop-label-row">
                <span className="stop-label">{restaurant.cuisine}</span>
                {restaurant.trending && <span className="trending-badge-sm">Trending</span>}
              </div>
              <h4>{restaurant.name}</h4>
              <p>{restaurant.price} · {restaurant.style}</p>
              <a href={restaurantMapsUrl} target="_blank" rel="noopener noreferrer">Info →</a>
            </div>
          </div>
        </div>
        
        <div className="ride-section">
          <span className="ride-label">Need a ride to start?</span>
          <div className="ride-buttons">
            <a href={activityUberUrl} target="_blank" rel="noopener noreferrer" className="ride-btn uber">
              Uber
            </a>
            {!isJapanCity(city) && (
              <a href={activityLyftUrl} target="_blank" rel="noopener noreferrer" className="ride-btn lyft">
                Lyft
              </a>
            )}
          </div>
        </div>
        
        <div className="combo-save-row">
          <button className={`save-btn ${isSaved ? 'saved' : ''}`} onClick={() => onSave(comboItem)}>
            <svg viewBox="0 0 24 24" fill={isSaved ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            </svg>
            {isSaved ? 'Saved!' : 'Save for Later'}
          </button>
          <button className="save-btn share" onClick={() => onShare && onShare({ ...comboItem, type: 'combo', activity, restaurant, city })}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share
          </button>
        </div>
      </div>
    </div>
  );
};

// Binge Result Card
const BingeCard = ({ show, onDismiss, onSpinAgain, onSave, onShare, isSaved }) => {
  if (!show) return null;
  
  const getPlatformUrl = (platform, showName) => {
    const urls = {
      'Netflix': `https://www.netflix.com/search?q=${encodeURIComponent(showName)}`,
      'Hulu': `https://www.hulu.com/search?q=${encodeURIComponent(showName)}`,
      'Disney+': `https://www.disneyplus.com/search?q=${encodeURIComponent(showName)}`,
      'Max': `https://www.max.com/search?q=${encodeURIComponent(showName)}`,
      'Prime': `https://www.amazon.com/s?k=${encodeURIComponent(showName)}&i=instant-video`,
      'Apple TV+': `https://tv.apple.com/search?term=${encodeURIComponent(showName)}`,
      'Peacock': `https://www.peacocktv.com/search?q=${encodeURIComponent(showName)}`,
      'Paramount+': `https://www.paramountplus.com/search/?q=${encodeURIComponent(showName)}`,
      'Crunchyroll': `https://www.crunchyroll.com/search?q=${encodeURIComponent(showName)}`
    };
    return urls[platform] || `https://www.google.com/search?q=${encodeURIComponent(showName + ' ' + platform)}`;
  };
  
  const imdbUrl = `https://www.imdb.com/find/?q=${encodeURIComponent(show.name)}&s=tt&ttype=tv,ft`;
  const rottenUrl = `https://www.rottentomatoes.com/search?search=${encodeURIComponent(show.name)}`;
  const watchUrl = getPlatformUrl(show.platform, show.name);
  
  return (
    <div className="result-overlay" onClick={onDismiss}>
      <div className="result-card-v2 binge-card-v2" onClick={e => e.stopPropagation()}>
        {/* Gradient background */}
        <div className="card-gradient-bg"></div>

        {/* Close button */}
        <button className="card-close-x" onClick={onDismiss} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="card-header-v2">
          <span className="card-brand">carpe</span>
          <span className="card-tab-badge">STAY IN</span>
        </div>
        
        {/* Category pill */}
        <div className="card-category-pill">
          {show.genre?.toUpperCase()} • {show.length?.toUpperCase()}
          {show.sponsored && <span className="sponsored-dot">★</span>}
        </div>
        
        {/* Content */}
        <div className="card-content-v2">
          <h2 className="card-name-v2">{show.name}</h2>
          
          <div className="card-meta-v2">
            <span>{show.genre}</span>
            <span className="meta-dot">·</span>
            <span>{show.year}</span>
            <span className="meta-dot">·</span>
            <span>{show.platform}</span>
          </div>
          
          <p className="card-description-v2">{show.description}</p>
          
          {/* Action buttons */}
          <div className="card-actions-v2">
            <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="action-btn-v2 primary">
              Watch
            </a>
            <a href={imdbUrl} target="_blank" rel="noopener noreferrer" className="action-btn-v2 outline">
              Reviews
            </a>
            <button 
              className={`action-btn-v2 outline ${isSaved ? 'saved' : ''}`} 
              onClick={() => onSave({ ...show, type: 'binge' })}
            >
              {isSaved ? 'Saved' : 'Save'}
            </button>
            <button
              className="action-btn-v2 outline share"
              onClick={() => onShare && onShare({ ...show, type: 'binge' })}
              aria-label="Share"
            >
              Share
            </button>
          </div>
          
          {/* Affiliate badges */}
          <div className="affiliate-badges-v2">
            <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="affiliate-badge">
              {show.platform?.toUpperCase()}
            </a>
            <a href={imdbUrl} target="_blank" rel="noopener noreferrer" className="affiliate-badge">
              IMDB
            </a>
            <a href={rottenUrl} target="_blank" rel="noopener noreferrer" className="affiliate-badge">
              ROTTEN TOMATOES
            </a>
          </div>
          
          {/* Spin again */}
          <button className="spin-again-btn" onClick={onSpinAgain || onDismiss}>
            <span className="spin-icon">✦</span>
            Spin again
          </button>
        </div>
      </div>
    </div>
  );
};

// Delivery Order Card
const DeliveryCard = ({ restaurant, city, onDismiss, onSpinAgain, onSave, onShare, isSaved }) => {
  if (!restaurant) return null;
  
  const mapsQuery = restaurant.address 
    ? `${restaurant.name}, ${restaurant.address}`
    : `${restaurant.name} restaurant ${city}`;
  const mapsUrl = getMapsUrl(mapsQuery);
  const doordashUrl = `https://www.doordash.com/search/store/${encodeURIComponent(restaurant.name)}`;
  const ubereatsUrl = `https://www.ubereats.com/search?q=${encodeURIComponent(restaurant.name)}`;
  const grubhubUrl = `https://www.grubhub.com/search?queryText=${encodeURIComponent(restaurant.name)}`;
  
  return (
    <div className="result-overlay" onClick={onDismiss}>
      <div className="result-card-v2 delivery-card-v2" onClick={e => e.stopPropagation()}>
        {/* Gradient background */}
        <div className="card-gradient-bg"></div>

        {/* Close button */}
        <button className="card-close-x" onClick={onDismiss} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="card-header-v2">
          <span className="card-brand">carpe</span>
          <span className="card-tab-badge">ORDER IN</span>
        </div>
        
        {/* Category pill */}
        <div className="card-category-pill">
          {restaurant.cuisine?.toUpperCase()} • {restaurant.deliveryTime}
          {restaurant.sponsored && <span className="sponsored-dot">★</span>}
        </div>
        
        {/* Content */}
        <div className="card-content-v2">
          <h2 className="card-name-v2">{restaurant.name}</h2>
          
          <div className="card-meta-v2">
            <span>{restaurant.cuisine}</span>
            <span className="meta-dot">·</span>
            <span>{restaurant.price}</span>
          </div>
          
          {/* Delivery app buttons — DoorDash/Grubhub have no Japan coverage */}
          <div className="card-actions-v2">
            {!isJapanCity(city) && (
              <a href={doordashUrl} target="_blank" rel="noopener noreferrer" className="action-btn-v2 outline">
                DoorDash
              </a>
            )}
            <a href={ubereatsUrl} target="_blank" rel="noopener noreferrer" className="action-btn-v2 outline">
              Uber Eats
            </a>
            {!isJapanCity(city) && (
              <a href={grubhubUrl} target="_blank" rel="noopener noreferrer" className="action-btn-v2 outline">
                Grubhub
              </a>
            )}
          </div>

          {/* Info + Save row */}
          <div className="card-actions-secondary">
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="action-btn-secondary">
              Info
            </a>
            <button
              className={`action-btn-secondary ${isSaved ? 'saved' : ''}`}
              onClick={() => onSave({ ...restaurant, type: 'delivery', city })}
            >
              {isSaved ? '✓ Saved' : 'Save'}
            </button>
            <button
              className="action-btn-secondary share"
              onClick={() => onShare && onShare({ ...restaurant, type: 'delivery', city })}
              aria-label="Share"
            >
              Share
            </button>
          </div>
          
          {/* Spin again */}
          <button className="spin-again-btn" onClick={onSpinAgain || onDismiss}>
            <span className="spin-icon">✦</span>
            Spin again
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// NO MATCH CARD
// Shown when user filters yield zero results.
// =============================================================================
const NoMatchCard = ({ info, onDismiss, prompt = 'Please try expanding your distance or adjusting your filters.' }) => {
  if (!info) return null;
  return (
    <div className="result-overlay" onClick={onDismiss}>
      <div
        className="result-card-v2 no-match-card"
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--cream, #FBF7F4)',
          color: 'var(--text, #2D2A26)',
          textAlign: 'center',
          padding: '56px 32px 44px'
        }}
      >
        <button
          className="card-close-x"
          onClick={onDismiss}
          aria-label="Close"
          style={{ width: '44px', height: '44px', fontSize: '26px', lineHeight: 1, fontWeight: 300 }}
        >
          ×
        </button>
        <h2 style={{
          fontSize: '24px',
          marginBottom: '16px',
          fontFamily: 'DM Serif Display, serif',
          color: 'var(--text, #2D2A26)'
        }}>
          No matches found
        </h2>
        <p style={{
          fontSize: '15px',
          lineHeight: '1.55',
          color: 'var(--text, #2D2A26)',
          opacity: 0.75,
          marginBottom: '14px'
        }}>
          {info.message}
        </p>
        <p style={{
          fontSize: '14px',
          lineHeight: '1.5',
          color: 'var(--text, #2D2A26)',
          opacity: 0.55
        }}>
          {prompt}
        </p>
      </div>
    </div>
  );
};

// =============================================================================
// DATE RANGE CALENDAR
// Custom inline calendar — opens directly to grid (no blank bar like native input).
// Supports single date or range selection via two-tap pattern.
// =============================================================================
const DateRangeCalendar = ({ value, onChange, onClose }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const ymdOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const todayYMD = ymdOf(today);

  const parseInitialRange = () => {
    if (!value) return { start: null, end: null };
    if (value === 'today') return { start: todayYMD, end: todayYMD };
    if (value === 'tomorrow') {
      const tom = new Date(today);
      tom.setDate(tom.getDate() + 1);
      const tomYMD = ymdOf(tom);
      return { start: tomYMD, end: tomYMD };
    }
    if (typeof value === 'string') return { start: value, end: value };
    if (typeof value === 'object' && value.start) {
      return { start: value.start, end: value.end || value.start };
    }
    return { start: null, end: null };
  };

  const [range, setRange] = useState(parseInitialRange);
  const [monthDate, setMonthDate] = useState(() => {
    const ref = range.start ? new Date(range.start + 'T00:00:00') : today;
    return new Date(ref.getFullYear(), ref.getMonth(), 1);
  });

  const handleDayClick = (dateYMD) => {
    if (dateYMD < todayYMD) return;
    // No selection yet, or both endpoints set → start fresh
    if (!range.start || range.end) {
      setRange({ start: dateYMD, end: null });
      return;
    }
    // Have a start, no end yet → this tap sets the end
    if (dateYMD < range.start) {
      setRange({ start: dateYMD, end: range.start });
    } else {
      setRange({ start: range.start, end: dateYMD });
    }
  };

  const handleClear = () => {
    onChange(null);
    onClose();
  };

  const handleApply = () => {
    if (!range.start) {
      onChange(null);
    } else if (!range.end || range.start === range.end) {
      onChange(range.start);
    } else {
      onChange({ start: range.start, end: range.end });
    }
    onClose();
  };

  const monthName = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= lastDayOfMonth; d++) cells.push(d);

  const goPrev = () => setMonthDate(new Date(year, month - 1, 1));
  const goNext = () => setMonthDate(new Date(year, month + 1, 1));

  // Don't allow nav into months fully in the past
  const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const viewedMonthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const canGoPrev = viewedMonthKey > currentMonthKey;

  let hint = 'Tap a date to start';
  if (range.start && !range.end) hint = 'Tap end date — or Apply for single day';
  else if (range.start && range.end && range.start === range.end) hint = 'Single day selected';
  else if (range.start && range.end) hint = 'Range selected';

  return (
    <div className="date-picker-overlay" onClick={onClose}>
      <div className="date-picker-modal date-picker-modal-cal" onClick={e => e.stopPropagation()}>
        <div className="cal-header">
          <button
            type="button"
            className="cal-nav"
            onClick={goPrev}
            disabled={!canGoPrev}
            aria-label="Previous month"
          >‹</button>
          <h3>{monthName}</h3>
          <button
            type="button"
            className="cal-nav"
            onClick={goNext}
            aria-label="Next month"
          >›</button>
        </div>

        <div className="cal-weekdays">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div key={i} className="cal-weekday">{d}</div>
          ))}
        </div>

        <div className="cal-grid">
          {cells.map((day, idx) => {
            if (day === null) return <div key={idx} className="cal-cell cal-empty" />;
            const cellYMD = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isPast = cellYMD < todayYMD;
            const isToday = cellYMD === todayYMD;
            const isStart = cellYMD === range.start;
            const isEnd = cellYMD === range.end;
            const inRange = range.start && range.end && cellYMD > range.start && cellYMD < range.end;
            const isSelected = isStart || isEnd;
            const isRangeMode = range.start && range.end && range.start !== range.end;

            const classNames = ['cal-cell', 'cal-day'];
            if (isPast) classNames.push('cal-disabled');
            if (isToday) classNames.push('cal-today');
            if (isSelected) classNames.push('cal-selected');
            if (inRange) classNames.push('cal-in-range');
            if (isStart && isRangeMode) classNames.push('cal-range-start');
            if (isEnd && isRangeMode) classNames.push('cal-range-end');

            return (
              <button
                key={idx}
                type="button"
                className={classNames.join(' ')}
                disabled={isPast}
                onClick={() => handleDayClick(cellYMD)}
              >{day}</button>
            );
          })}
        </div>

        <div className="cal-hint">{hint}</div>

        <div className="cal-actions">
          <button type="button" className="cal-btn cal-btn-secondary" onClick={handleClear}>Clear</button>
          <button type="button" className="cal-btn cal-btn-primary" onClick={handleApply}>Apply</button>
        </div>
      </div>
    </div>
  );
};

function App() {
  // ==========================================================================
  // SUPABASE DATA
  // ==========================================================================
  const {
    streaming: dbStreaming,
    activities: dbActivities,
    restaurants: dbRestaurants,
    events: dbEvents,
    loading: dbLoading,
    dynamicMetroCoords,
    fetchActivitiesForCity,
    fetchRestaurantsForCity,
    fetchRestaurantsNearPoint,
    fetchEventsForCity,
  } = useSupabaseData();
  
  const [isSpinning, setIsSpinning] = useState(false);
  const [cityActivities, setCityActivities] = useState([]);
  const [cityRestaurants, setCityRestaurants] = useState([]);
  const [cityEvents, setCityEvents] = useState([]);
  const [loadingCity, setLoadingCity] = useState(false);
  const [fetchError, setFetchError] = useState(null); // Visible error for debugging API failures
  const [pendingSpin, setPendingSpin] = useState(null); // 'restaurant' | 'activity' | null
  
  // Auth state
  const [user, setUser] = useState(null);
  const [authSession, setAuthSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signin'); // 'signin', 'signup', 'forgot'
  const [authError, setAuthError] = useState('');
  const [authDupeHint, setAuthDupeHint] = useState(false);
  const [authSignupSuccess, setAuthSignupSuccess] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  
  const [history, setHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSponsorPortal, setShowSponsorPortal] = useState(false);
  
  const [view, setView] = useState('restaurant');
  const [city, setCity] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showLocationRequiredModal, setShowLocationRequiredModal] = useState(false);

  // Region (USA / Japan) — drives the filter taxonomy and the city picker pool.
  const [region, setRegion] = useState(() => {
    try { return localStorage.getItem('carpe_region') || 'usa'; } catch (_) { return 'usa'; }
  });
  const handleSetRegion = (r) => {
    if (r === region) return;
    setRegion(r);
    try { localStorage.setItem('carpe_region', r); } catch (_) {}
    // Selected city and filter labels don't exist in the other region — clear
    // them so stale selections can't silently zero out every spin.
    setCity('');
    setSearchCoords(null);
    setMaxDistance(null);
    setSelectedPrices(['$$', '$$$']);
    setSelectedCuisines([]);
    setSelectedStyles([]);
    setSelectedLocations([]);
    setSelectedCategories([]);
    setSelectedActivityPrices([]);
    setSelectedVibeFilters([]);
    setEventDateFilter(null);
  };

  // Gate filter interactions on having a location entered.
  // Returns true if location is set, otherwise opens the prompt and returns false.
  const requireLocation = () => {
    if (!city || !city.trim()) {
      setShowLocationRequiredModal(true);
      return false;
    }
    return true;
  };
  
  // Restaurant state
  const [rSpinning, setRSpinning] = useState(false);
  const [rSpinComplete, setRSpinComplete] = useState([false, false, false]);
  const [rFinalIndices, setRFinalIndices] = useState([0, 0, 0]);
  const [restaurantResult, setRestaurantResult] = useState(null);
  const [lastRestaurantId, setLastRestaurantId] = useState(null);
  const [selectedPrices, setSelectedPrices] = useState(['$$', '$$$']);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [searchCoords, setSearchCoords] = useState(null); // {lat, lng}
  const [maxDistance, setMaxDistance] = useState(null); // always miles internally, null = no limit
  // Japan shows metric pills; option values stay in miles (≈ 2/5/10/15/25/40 km)
  const distanceOptions = region === 'japan'
    ? [1.25, 3.1, 6.2, 9.3, 15.5, 25, null]
    : [1, 3, 5, 10, 15, 25, null]; // null = "Any distance"
  const distanceLabel = (d) => d == null ? 'Any'
    : region === 'japan' ? `${Math.round(d * 1.609344)} km` : `${d} mi`;

  // Delegate to module-level haversineDistance with null guard
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return null;
    return haversineDistance(lat1, lng1, lat2, lng2);
  };

  // Activity state
  const [aSpinning, setASpinning] = useState(false);
  const [aSpinComplete, setASpinComplete] = useState([false, false, false]);
  const [aFinalIndices, setAFinalIndices] = useState([0, 0, 0]);
  const [activityResult, setActivityResult] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [eventDateFilter, setEventDateFilter] = useState(null); // null, 'today', 'tomorrow', or 'YYYY-MM-DD'
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedActivityPrices, setSelectedActivityPrices] = useState([]);
  const [selectedVibeFilters, setSelectedVibeFilters] = useState([]); // ['parks', 'rec', 'spa']
  
  // Combo state
  const [comboStep, setComboStep] = useState(1);
  const [comboActivity, setComboActivity] = useState(null);
  const [comboRestaurant, setComboRestaurant] = useState(null);
  const [diningRadius, setDiningRadius] = useState(3); // must match a RADIUS_OPTIONS value
  const [comboDistance, setComboDistance] = useState(null);
  const [comboASpinning, setComboASpinning] = useState(false);
  const [comboASpinComplete, setComboASpinComplete] = useState([false, false, false]);
  const [comboAFinalIndices, setComboAFinalIndices] = useState([0, 0, 0]);
  const [comboRSpinning, setComboRSpinning] = useState(false);
  const [comboRSpinComplete, setComboRSpinComplete] = useState([false, false, false]);
  const [comboRFinalIndices, setComboRFinalIndices] = useState([0, 0, 0]);
  const [comboVibeFilter, setComboVibeFilter] = useState('Any');
  const [showComboResult, setShowComboResult] = useState(false);
  
  // Stay In state
  const [stayInMode, setStayInMode] = useState('binge'); // 'binge' or 'order'
  const [bingeSpinning, setBingeSpinning] = useState(false);
  const bingeCompletedWheels = useRef(0);
  const bingeSpinDataRef = useRef({ indices: [0,0,0], g: [], p: [], l: [], pool: [], result: null });
  const [bingeFinalIndices, setBingeFinalIndices] = useState([0, 0, 0]);
  const [bingeResult, setBingeResult] = useState(null);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedLengths, setSelectedLengths] = useState([]);
  const [orderSpinning, setOrderSpinning] = useState(false);
  const orderCompletedWheels = useRef(0);
  const orderSpinDataRef = useRef({ indices: [0,0,0], cuisines: [], pool: [], result: null });
  const [orderFinalIndices, setOrderFinalIndices] = useState([0, 0, 0]);
  const [orderResult, setOrderResult] = useState(null);
  const [selectedOrderCuisines, setSelectedOrderCuisines] = useState([]);

  // Result snapshots for spin handlers — wheels are derived from these
  const restaurantSpinResultRef = useRef(null);
  // Rolling buffers of recent result names — suppress repeats across spins
  const recentRestaurantNamesRef = useRef([]);
  const recentActivityNamesRef = useRef([]);
  // Pool-of-one tracking: prevents replaying the same lone-match result back-to-back
  const lastComboActivityNameRef = useRef(null);
  const lastComboRestaurantNameRef = useRef(null);
  const lastBingeNameRef = useRef(null);
  const lastOrderNameRef = useRef(null);
  const activitySpinResultRef = useRef(null);
  const comboASpinResultRef = useRef(null);
  const comboRSpinResultRef = useRef(null);

  // No-match states. Set when strict filtering yields zero results.
  // Shape: { message: string, suggestion: string }
  const [restaurantNoMatch, setRestaurantNoMatch] = useState(null);
  const [activityNoMatch, setActivityNoMatch] = useState(null);
  const [comboANoMatch, setComboANoMatch] = useState(null);
  const [comboRNoMatch, setComboRNoMatch] = useState(null);
  const [bingeNoMatch, setBingeNoMatch] = useState(null);
  const [orderNoMatch, setOrderNoMatch] = useState(null);
  
  // ==========================================================================
  // FETCH DATA WHEN CITY CHANGES
  // ==========================================================================
  
  // Immediate fetch - no debounce (for explicit city selection)
  const loadCityDataImmediate = async (cityName, coords = null) => {
    if (!cityName || cityName.length < 2) return;

    setLoadingCity(true);
    setFetchError(null);
    try {
      const [activities, restaurantsData, events] = await Promise.all([
        fetchActivitiesForCity(cityName, coords),
        fetchRestaurantsForCity(cityName, coords),
        fetchEventsForCity(cityName, coords),
      ]);
      setCityActivities(activities || []);
      setCityRestaurants(restaurantsData || []);
      setCityEvents(events || []);
      // Debug: flag if Supabase returned nothing (likely proxy/CORS issue)
      if ((!restaurantsData || restaurantsData.length === 0) && (!activities || activities.length === 0)) {
        setFetchError(`No data returned for "${cityName}". API may be blocked.`);
      }
    } catch (err) {
      console.error('Error loading city data:', err);
      setFetchError(`API error: ${err.message}`);
    }
    setLoadingCity(false);
  };

  // Merge a targeted proximity fetch (fetchRestaurantsNearPoint) into the
  // current restaurant pool without duplicating rows already loaded.
  const mergeRestaurantsIntoPool = (extra) => {
    if (!extra || extra.length === 0) return;
    setCityRestaurants(prev => {
      const seen = new Set(prev.map(r => r.google_id || r.id || r.name));
      const fresh = extra.filter(r => !seen.has(r.google_id || r.id || r.name));
      return fresh.length > 0 ? [...prev, ...fresh] : prev;
    });
  };

  // Debounced fetch removed — partial names (e.g. "Jack") caused 500s that poisoned cache.
  // Fetches now only trigger via suggestion click, Enter key, or location button.
  
  // Data — filter arrays are region-scoped; Japan sets live at module scope
  // next to JAPAN_CITIES and must stay in sync with daily_scraper_japan.py tags.
  const isJapan = region === 'japan';
  const prices = ['$', '$$', '$$$', '$$$$'];
  const cuisines = isJapan ? JAPAN_CUISINES : ['Italian', 'Japanese', 'Mexican', 'Chinese', 'Thai', 'Indian', 'American', 'Mediterranean', 'Korean', 'Vietnamese', 'French', 'Seafood'];
  const styles = isJapan ? JAPAN_STYLES : ['Casual', 'Fine Dining', 'Café', 'Bar & Grill', 'Fast Food'];
  const locations = ['Indoor', 'Outdoor'];
  const categories = isJapan ? JAPAN_CATEGORIES : ['Events', 'Family Fun', 'Arts', 'History', 'Nature', 'Sports', 'Science', 'Shows', 'Nightlife', 'Parks & Outdoors', 'Parks & Rec', 'Spa & Wellness'];
  const activityPrices = ['Free', '$0-25', '$25-50', '$50-100', '$100+'];

  const PARKS_SUBCATEGORIES = ['Park', 'City Park', 'State Park', 'National Park', 'Nature Preserve', 'Hiking Area', 'Beach', 'Botanical Garden', 'Garden', 'Zoo', 'Aquarium'];
  const PARKS_REC_SUBCATEGORIES = ['Playground', 'Athletic Field', 'Swimming Pool', 'Tennis Court', 'Golf Course', 'Indoor Golf Course', 'Sports Complex', 'Community Center', 'Gym', 'Fitness Center', 'Yoga Studio', 'Sports Club'];
  const SPA_SUBCATEGORIES = ['Spa', 'Massage Spa', 'Massage', 'Hair Salon', 'Beauty Salon', 'Nail Salon', 'Barber Shop', 'Wellness Center'];

  const VIBE_FILTERS = {
    parks: { label: '🏞️ Parks & Outdoors', subcategories: PARKS_SUBCATEGORIES },
    rec: { label: '🏃 Parks & Rec', subcategories: PARKS_REC_SUBCATEGORIES },
    spa: { label: '💆 Spa & Wellness', subcategories: SPA_SUBCATEGORIES },
  };

  const CATEGORY_SUBCATEGORY_MAP = isJapan ? JAPAN_CATEGORY_SUBCATEGORY_MAP : {
    'Parks & Outdoors': PARKS_SUBCATEGORIES,
    'Parks & Rec': PARKS_REC_SUBCATEGORIES,
    'Spa & Wellness': SPA_SUBCATEGORIES,
  };
  
  // Map UI labels to database values (for shorter wheel labels)
  const CATEGORY_DB_MAP = {
    'Shows': 'Entertainment',
  };
  

  // ==========================================================================
  // USE REAL DATA FROM SUPABASE (with fallbacks)
  // ==========================================================================
  
  // Activities: Combine activities AND events from DB (no synthetic fallback).
  const combinedActivities = [...cityActivities, ...cityEvents];
  const sampleActivities = combinedActivities;

  // Restaurants: Use actual restaurant data from database (no synthetic fallback —
  // empty pool = empty pool, surfaced honestly to the user and captured as a wish signal).
  const sampleRestaurants = cityRestaurants;

  // Stay In data
  const genres = ['Trending', 'Comedy', 'Drama', 'Action', 'Thriller', 'Sci-Fi', 'Romance', 'Documentary', 'Horror', 'Animation'];
  const platforms = ['Netflix', 'Hulu', 'Disney+', 'Max', 'Prime', 'Apple TV+', 'Peacock', 'Paramount+', 'Crunchyroll'];
  const lengths = ['Short', 'TV Show', 'Movie'];
  const deliveryPrices = ['$', '$$', '$$$'];

  // Use database streaming data only — no synthetic fallback.
  const sampleStreaming = dbStreaming;
  
  // Get date string from event date filter (single date — kept for back-compat)
  const getEventDateString = (filter) => {
    if (!filter) return null;
    const toLocalYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = new Date();
    if (filter === 'today') {
      return toLocalYMD(today);
    } else if (filter === 'tomorrow') {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return toLocalYMD(tomorrow);
    }
    if (typeof filter === 'object' && filter.start) return filter.start;
    return filter; // Already a date string
  };

  // Get date range {start, end} (inclusive) from event date filter — handles all forms
  const getEventDateRange = (filter) => {
    if (!filter) return null;
    const toLocalYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = new Date();
    if (filter === 'today') {
      const ymd = toLocalYMD(today);
      return { start: ymd, end: ymd };
    }
    if (filter === 'tomorrow') {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const ymd = toLocalYMD(tomorrow);
      return { start: ymd, end: ymd };
    }
    if (typeof filter === 'object' && filter.start) {
      return { start: filter.start, end: filter.end || filter.start };
    }
    if (typeof filter === 'string') return { start: filter, end: filter };
    return null;
  };

  // Format date for display (handles ranges)
  const formatDateDisplay = (filter) => {
    if (!filter) return 'Any Date';
    if (filter === 'today') return 'Today';
    if (filter === 'tomorrow') return 'Tomorrow';
    const fmtOne = (ymd) => {
      const date = new Date(ymd + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    if (typeof filter === 'object' && filter.start) {
      if (!filter.end || filter.start === filter.end) return fmtOne(filter.start);
      return `${fmtOne(filter.start)} – ${fmtOne(filter.end)}`;
    }
    return fmtOne(filter);
  };
  
  // Delivery restaurants - use city restaurants with added delivery metadata
  const getDeliveryRestaurants = () => {
    const pool = cityRestaurants;
    if (pool.length === 0) {
      // No synthetic fallback — empty means empty. Caller handles no-coverage UX.
      return [];
    }
    
    // Add delivery metadata based on price level.
    // Japan: only Uber Eats operates — DoorDash exited in 2022, Grubhub is US-only.
    const allPlatforms = isJapan ? ['Uber Eats'] : ['Uber Eats', 'DoorDash', 'Grubhub'];
    return pool.map((r, i) => {
      const deliveryTimes = { '$': '20-30 min', '$$': '25-35 min', '$$$': '30-40 min', '$$$$': '35-50 min' };
      const deliveryFees = { '$': '$1.99', '$$': '$2.99', '$$$': '$3.99', '$$$$': '$4.99' };
      // Assign platforms deterministically by index to avoid re-render churn
      const numPlatforms = (i % 2) + 1;
      const platformsSlice = allPlatforms.slice(0, numPlatforms);

      return {
        ...r,
        deliveryTime: deliveryTimes[r.price] || '25-35 min',
        deliveryFee: deliveryFees[r.price] || '$2.99',
        platforms: platformsSlice
      };
    });
  };

  // Load user data
  useEffect(() => {
    const stored = loadFromStorage();
    if (stored?.user) {
      setUser(stored.user);
      setHistory(stored.history || []);
      setFavorites(stored.favorites || []);
      if (stored.user.homeCity) setCity(stored.user.homeCity);
    } else {
      setShowOnboarding(true);
    }

    // Show how-to walkthrough on first launch ever (independent of signup state)
    if (!localStorage.getItem(TUTORIAL_STORAGE_KEY)) {
      setShowTutorial(true);
    }
    
    // Preload top 5 cities in background for faster experience
    const preloadCities = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Miami'];
    preloadCities.forEach((c, i) => {
      setTimeout(() => {
        fetchRestaurantsForCity(c);
        fetchActivitiesForCity(c);
      }, i * 200); // Stagger requests
    });
  }, []);

  const storageInitialized = useRef(false);
  useEffect(() => {
    if (!storageInitialized.current) { storageInitialized.current = true; return; }
    if (user) saveToStorage({ user, history, favorites });
  }, [user, history, favorites]);

  const handleOnboardingComplete = (userData) => {
    setUser(userData);
    setShowOnboarding(false);
    if (userData.homeCity) setCity(userData.homeCity);
  };

  const handleTutorialComplete = () => {
    try { localStorage.setItem(TUTORIAL_STORAGE_KEY, '1'); } catch (e) {}
    setShowTutorial(false);
  };

  // =============================================================================
  // AUTH FUNCTIONS
  // =============================================================================
  
  // Initialize auth on app load
  useEffect(() => {
    const initAuth = async () => {
      // Check for password recovery token in URL hash
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        const params = new URLSearchParams(hash.slice(1));
        const accessToken = params.get('access_token');
        const tokenType = params.get('type');
        if (accessToken && tokenType === 'recovery') {
          setResetToken(accessToken);
          setShowResetPasswordModal(true);
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          setAuthLoading(false);
          return;
        }
      }

      const storedSession = getStoredSession();
      if (storedSession?.access_token) {
        try {
          // Verify token is still valid
          const userData = await supabaseGetUser(storedSession.access_token);
          if (userData) {
            setUser(userData);
            setAuthSession(storedSession);
            // Load synced favorites and history
            await loadUserData(storedSession.access_token, userData.id);
          } else {
            // Try refresh token
            if (storedSession.refresh_token) {
              const newSession = await supabaseRefreshToken(storedSession.refresh_token);
              const refreshedUser = await supabaseGetUser(newSession.access_token);
              if (refreshedUser) {
                setUser(refreshedUser);
                setAuthSession(newSession);
                await loadUserData(newSession.access_token, refreshedUser.id);
              }
            }
          }
        } catch (err) {
          console.log('Auth init error:', err);
          storeSession(null);
        }
      }
      setAuthLoading(false);
    };
    initAuth();
  }, []);
  
  // Load user's synced data from server
  const loadUserData = async (accessToken, userId) => {
    try {
      // Load favorites
      const userFavorites = await supabaseAuthFetch('user_favorites', accessToken, {
        filters: [{ column: 'user_id', operator: 'eq', value: userId }],
        order: { column: 'created_at', ascending: false }
      });
      if (userFavorites?.length > 0) {
        setFavorites(userFavorites.map(f => ({
          id: f.id,
          name: f.item_name,
          type: f.item_type,
          ...f.item_data,
          savedAt: f.created_at
        })));
      }
      
      // Load history
      const userHistory = await supabaseAuthFetch('user_history', accessToken, {
        filters: [{ column: 'user_id', operator: 'eq', value: userId }],
        order: { column: 'created_at', ascending: false },
        limit: 50
      });
      if (userHistory?.length > 0) {
        setHistory(userHistory.map(h => ({
          id: h.id,
          name: h.item_name,
          type: h.spin_type,
          city: h.city,
          ...h.item_data,
          date: h.created_at
        })));
      }
    } catch (err) {
      console.error('Error loading user data:', err);
    }
  };
  
  // Merge localStorage data with server data on first login
  const mergeLocalDataToServer = async (accessToken, userId) => {
    try {
      // Merge local favorites
      for (const fav of favorites) {
        await supabaseAuthPost('user_favorites', accessToken, {
          user_id: userId,
          item_type: fav.type || 'restaurant',
          item_id: fav.google_place_id || fav.name,
          item_name: fav.name,
          item_data: fav
        }, { query: 'on_conflict=user_id,item_type,item_id' });
      }
      
      // Merge local history (last 20 items)
      const recentHistory = history.slice(-20);
      for (const item of recentHistory) {
        await supabaseAuthPost('user_history', accessToken, {
          user_id: userId,
          spin_type: item.type || 'restaurant',
          item_name: item.name,
          item_data: item,
          city: item.city || city
        });
      }
    } catch (err) {
      console.error('Error merging local data:', err);
    }
  };
  
  // Handle sign up
  const handleSignUp = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthDupeHint(false);
    setAuthSignupSuccess(false);

    try {
      const result = await supabaseSignUp(authEmail, authPassword, authName);
      if (result.user && result.access_token) {
        // Immediately confirmed (email confirmation disabled)
        setUser(result.user);
        setAuthSession(result);
        setShowAuthModal(false);

        // Merge any local data
        if (favorites.length > 0 || history.length > 0) {
          await mergeLocalDataToServer(result.access_token, result.user.id);
          await loadUserData(result.access_token, result.user.id);
        }

        // Reset form
        setAuthEmail('');
        setAuthPassword('');
        setAuthName('');
      } else if (result.user) {
        // Email confirmation required — show success banner, don't log in yet
        setAuthSignupSuccess(true);
      } else {
        // No user, no error — Supabase email enumeration protection (duplicate email)
        setAuthDupeHint(true);
      }
    } catch (err) {
      setAuthError(err.message);
    }
  };
  
  // Handle sign in
  const handleSignIn = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      const result = await supabaseSignIn(authEmail, authPassword);
      if (result.user) {
        setUser(result.user);
        setAuthSession(result);
        setShowAuthModal(false);
        
        // Merge local data if any, then load server data
        if (favorites.length > 0 || history.length > 0) {
          await mergeLocalDataToServer(result.access_token, result.user.id);
        }
        await loadUserData(result.access_token, result.user.id);
        
        // Reset form
        setAuthEmail('');
        setAuthPassword('');
      }
    } catch (err) {
      setAuthError(err.message);
    }
  };
  
  // Handle forgot password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      await supabaseResetPassword(authEmail);
      setResetEmailSent(true);
    } catch (err) {
      setAuthError(err.message);
    }
  };
  
  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setResetPasswordError('');
    if (resetNewPassword !== resetConfirmPassword) {
      setResetPasswordError('Passwords do not match');
      return;
    }
    if (resetNewPassword.length < 6) {
      setResetPasswordError('Password must be at least 6 characters');
      return;
    }
    try {
      await supabaseUpdatePassword(resetToken, resetNewPassword);
      setResetPasswordSuccess(true);
      setTimeout(() => {
        setShowResetPasswordModal(false);
        setResetToken('');
        setResetNewPassword('');
        setResetConfirmPassword('');
        setResetPasswordSuccess(false);
      }, 2000);
    } catch (err) {
      setResetPasswordError(err.message);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    if (window.confirm('Sign out? Your favorites and history are saved to your account.')) {
      try {
        await supabaseSignOut(authSession?.access_token);
      } catch (err) {
        console.error('Sign out error:', err);
      }
      setUser(null);
      setAuthSession(null);
      setFavorites([]);
      setHistory([]);
      setShowProfile(false);
    }
  };
  
  // Handle account deletion (Apple compliance)
  const handleDeleteAccount = async () => {
    if (window.confirm('Delete your account? This cannot be undone.')) {
      if (window.confirm('Are you absolutely sure? All your favorites and history will be permanently deleted.')) {
        try {
          // Delete user data via RPC
          if (authSession?.access_token) {
            await fetch(`${SUPABASE_URL}/rest/v1/rpc/delete_user_account`, {
              method: 'POST',
              headers: {
                'apikey': SUPABASE_ANON_KEY,
                'Authorization': `Bearer ${authSession.access_token}`,
                'Content-Type': 'application/json',
              },
            });
          }
          
          // Sign out
          await supabaseSignOut(authSession?.access_token);
          setUser(null);
          setAuthSession(null);
          setFavorites([]);
          setHistory([]);
          setShowProfile(false);
          alert('Account deleted successfully.');
        } catch (err) {
          console.error('Delete account error:', err);
          alert('Error deleting account. Please try again.');
        }
      }
    }
  };
  
  // Open auth modal
  const openAuthModal = (mode = 'signin') => {
    setAuthMode(mode);
    setAuthError('');
    setResetEmailSent(false);
    setShowAuthModal(true);
  };
  
  // =============================================================================
  // HISTORY & FAVORITES (with sync)
  // =============================================================================
  
  const addToHistory = async (item) => {
    const historyItem = { ...item, id: Date.now(), date: new Date().toISOString() };
    setHistory(prev => [...prev, historyItem]);
    
    // Sync to server if logged in
    if (authSession?.access_token && user?.id) {
      try {
        await supabaseAuthPost('user_history', authSession.access_token, {
          user_id: user.id,
          spin_type: item.type || 'restaurant',
          item_name: item.name,
          item_data: item,
          city: item.city || city
        });
      } catch (err) {
        console.error('Error syncing history:', err);
      }
    }
  };

  const deleteFromHistory = (id) => setHistory(prev => prev.filter(item => item.id !== id));
  const clearHistory = () => { if (window.confirm('Clear your entire history?')) setHistory([]); };
  
  // Favorites helpers
  const addToFavorites = async (item) => {
    const favItem = { ...item, id: Date.now(), savedAt: new Date().toISOString() };
    setFavorites(prev => [...prev, favItem]);
    
    // Track save engagement
    if (item.name) {
      trackEngagement(item.name, 'save');
    }
    
    // Sync to server if logged in
    if (authSession?.access_token && user?.id) {
      try {
        const result = await supabaseAuthPost('user_favorites', authSession.access_token, {
          user_id: user.id,
          item_type: item.type || 'restaurant',
          item_id: item.google_place_id || item.name,
          item_name: item.name,
          item_data: item
        });
        // Update local id with server id
        if (result?.[0]?.id) {
          setFavorites(prev => prev.map(f => 
            f.name === item.name && f.id === favItem.id 
              ? { ...f, id: result[0].id } 
              : f
          ));
        }
      } catch (err) {
        console.error('Error syncing favorite:', err);
      }
    }
  };
  
  const removeFromFavorites = async (id) => {
    const itemToRemove = favorites.find(f => f.id === id);
    setFavorites(prev => prev.filter(item => item.id !== id));
    
    // Sync to server if logged in
    if (authSession?.access_token && itemToRemove) {
      try {
        await supabaseAuthDelete('user_favorites', authSession.access_token, `id=eq.${id}`);
      } catch (err) {
        console.error('Error removing favorite from server:', err);
      }
    }
  };
  
  const isFavorited = (name) => favorites.some(f => f.name === name);
  
  const toggleFavorite = (item) => {
    if (isFavorited(item.name)) {
      const existing = favorites.find(f => f.name === item.name);
      if (existing) removeFromFavorites(existing.id);
    } else {
      addToFavorites(item);
    }
  };

  // Share helper — uses Web Share API on mobile (iOS/Android via Capacitor),
  // falls back to clipboard copy on desktop / unsupported browsers.
  const shareItem = async (item) => {
    if (!item) return;

    // Detect sender's platform to surface the right store link in shared messages.
    // Recipient platform is unknown, but sender's OS is the best proxy (people tend
    // to message on similar devices). Desktop falls back to the website, which has
    // both store badges visible.
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isAndroid = /Android/.test(ua);
    const carpeStoreUrl = isIOS
      ? 'https://apps.apple.com/app/id6761194918'
      : isAndroid
        ? 'https://play.google.com/store/apps/details?id=com.carpeapp.carpe'
        : 'https://carpe-app.com';

    const type = item.type || 'restaurant';
    const cityStr = item.city ? ` in ${item.city}` : '';
    let title = '';
    let text = '';
    let url = '';

    if (type === 'restaurant' || type === 'delivery') {
      title = `Carpe pick: ${item.name}`;
      text = `${type === 'delivery' ? 'Order from' : 'Check out'} ${item.name}${cityStr} — found on Carpe.`;
      const q = item.address ? `${item.name}, ${item.address}` : `${item.name} ${item.city || ''}`;
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
    } else if (type === 'activity') {
      title = `Carpe pick: ${item.name}`;
      text = `Let's do this: ${item.name}${cityStr} — found on Carpe.`;
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${item.name} ${item.city || ''}`)}`;
    } else if (type === 'combo') {
      const a = item.activity?.name || '';
      const r = item.restaurant?.name || '';
      title = `Carpe combo: ${a} + ${r}`;
      text = `Plan: ${a} then ${r}${cityStr} — found on Carpe.`;
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${a} ${item.city || ''}`)}`;
    } else if (type === 'binge') {
      title = `Carpe pick: ${item.name}`;
      text = `Watching ${item.name}${item.platform ? ` on ${item.platform}` : ''} — found on Carpe.`;
      url = `https://www.imdb.com/find/?q=${encodeURIComponent(item.name)}&s=tt&ttype=tv,ft`;
    } else {
      title = `Carpe pick: ${item.name || 'Activity'}`;
      text = `Found on Carpe.`;
      url = 'https://carpe-app.com';
    }

    // Append store link as a friendly footer so recipients can grab the app.
    // Done after type switch so every share type gets it consistently.
    text = `${text}\n\nGet Carpe: ${carpeStoreUrl}`;

    const trackName = type === 'combo'
      ? `${item.activity?.name} + ${item.restaurant?.name}`
      : item.name;

    // Try native share sheet (mobile)
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text, url });
        if (trackName) trackEngagement(trackName, 'share');
        return;
      } catch (err) {
        // User cancelled — bail silently. Any other error falls through to clipboard.
        if (err && err.name === 'AbortError') return;
      }
    }

    // Fallback: copy to clipboard
    const fallbackText = `${text}\n${url}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(fallbackText);
      } else {
        const ta = document.createElement('textarea');
        ta.value = fallbackText;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      if (trackName) trackEngagement(trackName, 'share');
      alert('Link copied to clipboard');
    } catch (err) {
      console.error('Share failed:', err);
      alert('Could not share — try again.');
    }
  };
  
  // Legacy logout (for local-only mode)
  const handleLogout = () => {
    if (user) {
      handleSignOut();
    } else if (window.confirm('Clear all your local data?')) {
      localStorage.removeItem(STORAGE_KEY);
      setHistory([]);
      setFavorites([]);
      setCity('');
      setShowProfile(false);
      setShowOnboarding(true);
    }
  };

  // Update home city and refresh data
  const handleUpdateHomeCity = (newHomeCity) => {
    if (newHomeCity) {
      setCity(newHomeCity);
      loadCityDataImmediate(newHomeCity);
    }
  };

  const toggleFilter = (arr, setArr, item) => {
    setArr(arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]);
  };

  // Same as toggleFilter, but blocks the action when no location is set
  // and surfaces the "please enter a location" prompt.
  const toggleFilterGated = (arr, setArr, item) => {
    if (!requireLocation()) return;
    toggleFilter(arr, setArr, item);
  };

  // Weighted random selection for sponsored/boosted items
  // @protected — DO NOT MODIFY. Boost-weighted random selection. This is the monetization hook.
  const weightedPick = (items, weightFn = null) => {
    if (items.length === 0) return null;
    const getWeight = weightFn || ((item) => item.boost || 1);
    // Shuffle first to eliminate any ordering bias before weighted selection
    const pool = fisherYatesShuffle(items);
    const totalWeight = pool.reduce((sum, item) => sum + getWeight(item), 0);
    // Use crypto-quality random if available, fall back to Math.random
    const rand = typeof crypto !== 'undefined' && crypto.getRandomValues
      ? crypto.getRandomValues(new Uint32Array(1))[0] / 0xFFFFFFFF
      : Math.random();
    let r = rand * totalWeight;
    for (const item of pool) {
      r -= getWeight(item);
      if (r <= 0) return item;
    }
    return pool[pool.length - 1];
  };

  // Spin handlers
  // Pick the result first, then derive wheel indices from the result's actual
  // attributes. This makes the wheel-result contract structural — the wheels
  // can only land on attributes the result actually has.

  // Decides whether an activity satisfies a category label, including meta-labels
  // (Trending / Events / Family Fun / vibe categories).
  const matchesActivityCategory = (activity, categoryLabel) => {
    if (categoryLabel === 'Trending') return !!activity.trending;
    if (categoryLabel === 'Events') return !!activity.event_date;
    if (categoryLabel === 'Family Fun') return isFamilyFriendly(activity);
    if (CATEGORY_SUBCATEGORY_MAP[categoryLabel]) {
      return !!activity.subcategory
        && CATEGORY_SUBCATEGORY_MAP[categoryLabel].includes(activity.subcategory);
    }
    const dbCategory = CATEGORY_DB_MAP[categoryLabel] || categoryLabel;
    return activity.category === dbCategory;
  };

  // Picks the best wheel label to display for a given activity result.
  // Prefers literal category match, then vibe category, then meta categories.
  const deriveActivityCategoryIdx = (activity, ac) => {
    const literalIdx = ac.indexOf(activity.category);
    if (literalIdx !== -1) return literalIdx;
    for (let i = 0; i < ac.length; i++) {
      const c = ac[i];
      if (CATEGORY_SUBCATEGORY_MAP[c] && activity.subcategory
          && CATEGORY_SUBCATEGORY_MAP[c].includes(activity.subcategory)) {
        return i;
      }
    }
    const familyIdx = ac.indexOf('Family Fun');
    if (familyIdx !== -1 && isFamilyFriendly(activity)) return familyIdx;
    const eventsIdx = ac.indexOf('Events');
    if (eventsIdx !== -1 && activity.event_date) return eventsIdx;
    const trendingIdx = ac.indexOf('Trending');
    if (trendingIdx !== -1 && activity.trending) return trendingIdx;
    return 0;
  };

  const handleRestaurantSpin = async () => {
    if (rSpinning) return;
    if (!city.trim()) { alert('Pick a destination first!'); return; }
    // If city typed but data not loaded yet, fetch first then spin
    if (cityRestaurants.length < 3) {
      setLoadingCity(true);
      await loadCityDataImmediate(city, searchCoords);
      setLoadingCity(false);
    }

    if (sampleRestaurants.length === 0) {
      const [emptyMetro, emptyState] = (city || '').split(',').map(s => s.trim());
      trackSpinEmpty({
        spinType: 'restaurant',
        metro: emptyMetro || null,
        state: emptyState || null,
        filters: { prices: selectedPrices, cuisines: selectedCuisines, styles: selectedStyles },
        reason: 'no_coverage'
      });
      const cityName = (city.split(',')[0] || 'this area').trim();
      setRestaurantNoMatch({
        message: `We don't have restaurant data for ${cityName} yet.`,
        suggestion: `We're working on expanding coverage. Try a nearby city in the meantime.`
      });
      return;
    }

    const ap = selectedPrices.length > 0 ? selectedPrices : prices;
    const ac = selectedCuisines.length > 0 ? selectedCuisines : cuisines;
    const as = selectedStyles.length > 0 ? selectedStyles : styles;

    // Reusable filter applier — `skip` lets us compute "what would the pool be if
    // we dropped just this filter?" for the no-match suggestion.
    const applyRestaurantFilters = (skip = null) => {
      let p = sampleRestaurants;
      if (skip !== 'price' && selectedPrices.length > 0) {
        p = p.filter(r => selectedPrices.includes(r.price));
      }
      if (skip !== 'cuisine') {
        if (selectedCuisines.length > 0 && !selectedCuisines.includes('Trending')) {
          p = p.filter(r => selectedCuisines.includes(r.cuisine));
        } else if (selectedCuisines.includes('Trending') && selectedCuisines.length === 1) {
          p = p.filter(r => r.trending);
        }
      }
      if (skip !== 'style' && selectedStyles.length > 0) {
        p = p.filter(r => selectedStyles.includes(r.style));
      }
      if (skip !== 'distance' && searchCoords && maxDistance) {
        p = p.filter(r => {
          if (!r.lat || !r.lng) return false;
          const dist = calculateDistance(searchCoords.lat, searchCoords.lng, r.lat, r.lng);
          return dist !== null && dist <= maxDistance;
        });
      }
      return p;
    };

    let pool = applyRestaurantFilters();

    // No matches: build a polite suggestion based on which single filter, if dropped,
    // would unlock the most results.
    if (pool.length === 0) {
      const [emptyMetro, emptyState] = (city || '').split(',').map(s => s.trim());
      trackSpinEmpty({
        spinType: 'restaurant',
        metro: emptyMetro || null,
        state: emptyState || null,
        filters: { prices: selectedPrices, cuisines: selectedCuisines, styles: selectedStyles },
        reason: sampleRestaurants.length === 0 ? 'no_coverage' : 'no_results'
      });
      const candidates = [];
      if (selectedPrices.length > 0) candidates.push({ key: 'price', label: 'Price' });
      const cuisineIsActive =
        (selectedCuisines.length > 0 && !selectedCuisines.includes('Trending')) ||
        (selectedCuisines.includes('Trending') && selectedCuisines.length === 1);
      if (cuisineIsActive) candidates.push({ key: 'cuisine', label: 'Cuisine' });
      if (selectedStyles.length > 0) candidates.push({ key: 'style', label: 'Vibe' });
      if (searchCoords && maxDistance) candidates.push({ key: 'distance', label: 'Distance' });

      const ranked = candidates
        .map(c => ({ ...c, count: applyRestaurantFilters(c.key).length }))
        .filter(c => c.count > 0)
        .sort((a, b) => b.count - a.count);

      const cityName = (city.split(',')[0] || 'this area').trim();
      setRestaurantNoMatch({
        message: `No spots in ${cityName} match all your filters right now.`,
        suggestion: ranked.length > 0
          ? `Try removing your ${ranked[0].label} filter — that would surface ${ranked[0].count} ${ranked[0].count === 1 ? 'spot' : 'spots'}.`
          : `Try clearing some filters or trying a nearby area.`
      });
      return;
    }

    // Pool-of-one back-to-back guard: if there's exactly 1 match and we already showed it,
    // surface a "you've seen the only option" message instead of replaying the same result.
    if (pool.length === 1 && pool[0].name === lastRestaurantId) {
      const cityName = (city.split(',')[0] || 'this area').trim();
      setRestaurantNoMatch({
        message: `Only one spot in ${cityName} matches your filters — and you just saw it.`,
        suggestion: `Try widening your filters or check a nearby area for more options.`
      });
      return;
    }

    // Soft filters (not user-selected): dedup recent results, wheelable representation.
    const recentRestaurantNames = recentRestaurantNamesRef.current;
    if (recentRestaurantNames.length > 0 && pool.length > 1) {
      const fresh = pool.filter(r => !recentRestaurantNames.includes(r.name));
      // Only swap to fresh pool if it has enough options — otherwise data sparsity
      // would force the same 1-2 results repeatedly even with the buffer.
      if (fresh.length >= 3) pool = fresh;
    }
    const wheelable = pool.filter(r =>
      ap.includes(r.price) &&
      (ac.includes(r.cuisine) || (ac.includes('Trending') && r.trending)) &&
      as.includes(r.style)
    );
    if (wheelable.length > 0) pool = wheelable;

    const result = weightedPick(pool);
    if (!result) return;
    restaurantSpinResultRef.current = result;
    setRestaurantNoMatch(null);

    // Derive wheel indices from the actual result.
    const priceIdx = ap.indexOf(result.price);
    const styleIdx = as.indexOf(result.style);
    let cuisineIdx = ac.indexOf(result.cuisine);
    if (cuisineIdx === -1 && ac.includes('Trending') && result.trending) {
      cuisineIdx = ac.indexOf('Trending');
    }

    setRSpinning(true);
    setRSpinComplete([false, false, false]);
    setRestaurantResult(null);
    setRFinalIndices([
      priceIdx   >= 0 ? priceIdx   : 0,
      cuisineIdx >= 0 ? cuisineIdx : 0,
      styleIdx   >= 0 ? styleIdx   : 0,
    ]);
  };

  const handleRWheelComplete = (index) => setRSpinComplete(prev => { const next = [...prev]; next[index] = true; return next; });

  useEffect(() => {
    if (rSpinComplete.every(Boolean) && rSpinning) {
      setRSpinning(false);
      setTimeout(() => {
        const result = restaurantSpinResultRef.current;
        if (!result) return;
        setRestaurantResult(result);
        setLastRestaurantId(result.name);
        recentRestaurantNamesRef.current = [result.name, ...recentRestaurantNamesRef.current].slice(0, RECENT_MEMORY);
        addToHistory({ type: 'restaurant', name: result.name, city, ...result });

        const [metro, state] = city.split(',').map(s => s.trim());
        trackSpin({
          spinType: 'restaurant',
          resultId: result.id,
          metro,
          state,
          filters: { prices: selectedPrices, cuisines: selectedCuisines, styles: selectedStyles },
          resultName: result.name,
          resultCategory: result.cuisine,
          resultPrice: result.price,
          resultSponsored: result.sponsored || false,
          resultBoost: result.boost || 0
        });
      }, 400 / SPIN_SPEED_MULTIPLIER);
    }
  }, [rSpinComplete, rSpinning, city, selectedPrices, selectedCuisines, selectedStyles]);

  const handleActivitySpin = async () => {
    if (aSpinning) return;
    if (!city.trim()) { alert('Pick a destination first!'); return; }
    // If city typed but data not loaded yet, fetch first then spin
    if (cityActivities.length < 3 && cityEvents.length < 3) {
      setLoadingCity(true);
      await loadCityDataImmediate(city, searchCoords);
      setLoadingCity(false);
    }

    const al = selectedLocations.length > 0 ? selectedLocations : locations;
    const ac = selectedCategories.length > 0 ? selectedCategories : categories;
    const ap = selectedActivityPrices.length > 0 ? selectedActivityPrices : activityPrices;

    const targetRange = getEventDateRange(eventDateFilter);
    const vibeSubs = [...new Set([
      ...selectedVibeFilters.flatMap(key => VIBE_FILTERS[key]?.subcategories || []),
      ...selectedCategories.flatMap(c => CATEGORY_SUBCATEGORY_MAP[c] || [])
    ])];

    // Reusable filter applier — strict; `skip` lets us calculate which single
    // filter, if dropped, would unlock the most matches.
    const applyActivityFilters = (skip = null) => {
      let p = sampleActivities;
      if (skip !== 'category' && selectedCategories.length > 0) {
        p = p.filter(a => selectedCategories.some(c => matchesActivityCategory(a, c)));
      }
      if (skip !== 'location' && selectedLocations.length > 0) {
        const wantIndoor = selectedLocations.includes('Indoor');
        const wantOutdoor = selectedLocations.includes('Outdoor');
        p = p.filter(a =>
          (wantIndoor && a.indoor === true) || (wantOutdoor && a.indoor === false)
        );
      }
      if (skip !== 'price' && selectedActivityPrices.length > 0) {
        p = p.filter(a => {
          if (selectedActivityPrices.includes(a.priceRange)) return true;
          // $0-25 is a logical superset of Free ($0 ∈ [$0,$25]) — Free items pass when $0-25 is selected
          if (a.priceRange === 'Free' && selectedActivityPrices.includes('$0-25')) return true;
          return false;
        });
      }
      if (skip !== 'vibe' && vibeSubs.length > 0 && selectedVibeFilters.length > 0) {
        p = p.filter(a => a.subcategory && vibeSubs.includes(a.subcategory));
      }
      if (skip !== 'date' && targetRange) {
        p = p.filter(a => {
          // Activities without any date field (museums, parks, hiking trails, restaurants etc.)
          // are date-agnostic — they're always available, so the date filter passes them through.
          // Only items with explicit dates (events) are checked against the selected range.
          const itemDate = a.event_date || (a.start_date ? a.start_date.slice(0, 10) : null);
          if (!itemDate) return true;
          return itemDate >= targetRange.start && itemDate <= targetRange.end;
        });
      }
      if (skip !== 'distance' && searchCoords && maxDistance) {
        p = p.filter(a => {
          if (!a.lat || !a.lng) return false;
          const dist = calculateDistance(searchCoords.lat, searchCoords.lng, a.lat, a.lng);
          return dist !== null && dist <= maxDistance;
        });
      }
      return p;
    };

    let pool = applyActivityFilters();

    if (pool.length === 0) {
      const [emptyMetro, emptyState] = (city || '').split(',').map(s => s.trim());
      const isNoCoverage = sampleActivities.length === 0;
      trackSpinEmpty({
        spinType: 'activity',
        metro: emptyMetro || null,
        state: emptyState || null,
        filters: { locations: selectedLocations, categories: selectedCategories, prices: selectedActivityPrices, eventDate: eventDateFilter, vibes: selectedVibeFilters },
        reason: isNoCoverage ? 'no_coverage' : 'no_results'
      });
      const cityName = (city.split(',')[0] || 'this area').trim();
      if (isNoCoverage) {
        setActivityNoMatch({
          message: `We don't have activity data for ${cityName} yet.`,
          suggestion: `We're working on expanding coverage. Try a nearby city in the meantime.`
        });
        return;
      }
      const candidates = [];
      if (selectedCategories.length > 0) candidates.push({ key: 'category', label: 'Category' });
      if (selectedLocations.length > 0) candidates.push({ key: 'location', label: 'Indoor/Outdoor' });
      if (selectedActivityPrices.length > 0) candidates.push({ key: 'price', label: 'Price' });
      if (selectedVibeFilters.length > 0) candidates.push({ key: 'vibe', label: 'Vibe' });
      if (targetRange) candidates.push({ key: 'date', label: 'Date' });
      if (searchCoords && maxDistance) candidates.push({ key: 'distance', label: 'Distance' });

      const ranked = candidates
        .map(c => ({ ...c, count: applyActivityFilters(c.key).length }))
        .filter(c => c.count > 0)
        .sort((a, b) => b.count - a.count);

      setActivityNoMatch({
        message: `Nothing in ${cityName} matches all your filters right now.`,
        suggestion: ranked.length > 0
          ? `Try removing your ${ranked[0].label} filter — that would surface ${ranked[0].count} ${ranked[0].count === 1 ? 'option' : 'options'}.`
          : `Try clearing some filters or trying a nearby area.`
      });
      return;
    }

    // Soft filter: suppress recently-seen activities so the spin doesn't loop the same handful
    const recentActivityNames = recentActivityNamesRef.current;
    // Pool-of-one back-to-back guard
    if (pool.length === 1 && pool[0].name === recentActivityNames[0]) {
      const cityName = (city.split(',')[0] || 'this area').trim();
      setActivityNoMatch({
        message: `Only one option in ${cityName} matches your filters — and you just saw it.`,
        suggestion: `Try widening your filters or check a nearby area for more options.`
      });
      return;
    }
    if (recentActivityNames.length > 0 && pool.length > 1) {
      const fresh = pool.filter(a => !recentActivityNames.includes(a.name));
      if (fresh.length >= 3) pool = fresh;
    }

    const result = weightedPick(pool);
    if (!result) return;
    activitySpinResultRef.current = result;
    setActivityNoMatch(null);

    // Derive wheel indices from result attributes
    const settingLabel = result.indoor === true ? 'Indoor'
      : result.indoor === false ? 'Outdoor' : null;
    const settingIdx = settingLabel ? al.indexOf(settingLabel) : -1;
    const categoryIdx = deriveActivityCategoryIdx(result, ac);
    const priceIdx = ap.indexOf(result.priceRange);

    setASpinning(true);
    setASpinComplete([false, false, false]);
    setActivityResult(null);
    setAFinalIndices([
      settingIdx  >= 0 ? settingIdx  : 0,
      categoryIdx >= 0 ? categoryIdx : 0,
      priceIdx    >= 0 ? priceIdx    : 0,
    ]);
  };

  const handleAWheelComplete = (index) => setASpinComplete(prev => { const next = [...prev]; next[index] = true; return next; });

  useEffect(() => {
    if (aSpinComplete.every(Boolean) && aSpinning) {
      setASpinning(false);
      setTimeout(() => {
        const result = activitySpinResultRef.current;
        if (!result) return;
        setActivityResult(result);
        recentActivityNamesRef.current = [result.name, ...recentActivityNamesRef.current].slice(0, RECENT_MEMORY);
        addToHistory({ type: 'activity', name: result.name, city, ...result });

        const [metro, state] = city.split(',').map(s => s.trim());
        trackSpin({
          spinType: 'activity',
          resultId: result.id,
          metro,
          state,
          filters: { locations: selectedLocations, categories: selectedCategories, prices: selectedActivityPrices, eventDate: eventDateFilter, vibes: selectedVibeFilters },
          resultName: result.name,
          resultCategory: result.category,
          resultPrice: result.priceRange,
          resultSponsored: result.sponsored || false,
          resultBoost: result.boost || 0
        });
      }, 400 / SPIN_SPEED_MULTIPLIER);
    }
  }, [aSpinComplete, aSpinning, city, selectedLocations, selectedCategories, selectedActivityPrices, selectedVibeFilters, eventDateFilter]);

  // Combo handlers
  const handleComboActivitySpin = async () => {
    if (comboASpinning) return;
    if (!city.trim()) { alert('Pick a destination first!'); return; }
    if (cityActivities.length < 3 && cityEvents.length < 3) {
      setLoadingCity(true);
      await loadCityDataImmediate(city, searchCoords);
      setLoadingCity(false);
    }

    const al = selectedLocations.length > 0 ? selectedLocations : locations;
    const ac = selectedCategories.length > 0 ? selectedCategories : categories;
    const ap = selectedActivityPrices.length > 0 ? selectedActivityPrices : activityPrices;

    const targetRange = getEventDateRange(eventDateFilter);
    const vibeSubs = [...new Set([
      ...selectedVibeFilters.flatMap(key => VIBE_FILTERS[key]?.subcategories || []),
      ...selectedCategories.flatMap(c => CATEGORY_SUBCATEGORY_MAP[c] || [])
    ])];

    // Density-aware pick: only surface activities that actually have dining
    // options within the chosen radius, so the step-2 spin can't strand the
    // user next to nothing. Cheap bounding-box reject before the exact
    // haversine keeps the activities × restaurants scan fast.
    const diningCoords = sampleRestaurants
      .filter(r => r.lat && r.lng)
      .map(r => ({ lat: r.lat, lng: r.lng }));
    const hasDiningNearby = (a) => {
      if (!a.lat || !a.lng) return false;
      const latPad = diningRadius / 69;
      const lngPad = diningRadius / (69 * Math.max(0.2, Math.cos(a.lat * Math.PI / 180)));
      let count = 0;
      for (const r of diningCoords) {
        if (Math.abs(r.lat - a.lat) > latPad || Math.abs(r.lng - a.lng) > lngPad) continue;
        if (haversineDistance(a.lat, a.lng, r.lat, r.lng) <= diningRadius) {
          count++;
          if (count >= MIN_COMBO_DINING_OPTIONS) return true;
        }
      }
      return false;
    };

    const applyActivityFilters = (skip = null) => {
      let p = sampleActivities;
      if (skip !== 'category' && selectedCategories.length > 0) {
        p = p.filter(a => selectedCategories.some(c => matchesActivityCategory(a, c)));
      }
      if (skip !== 'location' && selectedLocations.length > 0) {
        const wantIndoor = selectedLocations.includes('Indoor');
        const wantOutdoor = selectedLocations.includes('Outdoor');
        p = p.filter(a =>
          (wantIndoor && a.indoor === true) || (wantOutdoor && a.indoor === false)
        );
      }
      if (skip !== 'price' && selectedActivityPrices.length > 0) {
        p = p.filter(a => {
          if (selectedActivityPrices.includes(a.priceRange)) return true;
          // $0-25 is a logical superset of Free ($0 ∈ [$0,$25]) — Free items pass when $0-25 is selected
          if (a.priceRange === 'Free' && selectedActivityPrices.includes('$0-25')) return true;
          return false;
        });
      }
      if (skip !== 'vibe' && vibeSubs.length > 0 && selectedVibeFilters.length > 0) {
        p = p.filter(a => a.subcategory && vibeSubs.includes(a.subcategory));
      }
      if (skip !== 'date' && targetRange) {
        p = p.filter(a => {
          // Activities without any date field (museums, parks, hiking trails, restaurants etc.)
          // are date-agnostic — they're always available, so the date filter passes them through.
          // Only items with explicit dates (events) are checked against the selected range.
          const itemDate = a.event_date || (a.start_date ? a.start_date.slice(0, 10) : null);
          if (!itemDate) return true;
          return itemDate >= targetRange.start && itemDate <= targetRange.end;
        });
      }
      if (skip !== 'distance' && searchCoords && maxDistance) {
        p = p.filter(a => {
          if (!a.lat || !a.lng) return false;
          const dist = calculateDistance(searchCoords.lat, searchCoords.lng, a.lat, a.lng);
          return dist !== null && dist <= maxDistance;
        });
      }
      if (skip !== 'dining' && diningCoords.length > 0) {
        p = p.filter(hasDiningNearby);
      }
      return p;
    };

    let pool = applyActivityFilters();

    if (pool.length === 0) {
      const [emptyMetro, emptyState] = (city || '').split(',').map(s => s.trim());
      const isNoCoverage = sampleActivities.length === 0;
      trackSpinEmpty({
        spinType: 'combo_activity',
        metro: emptyMetro || null,
        state: emptyState || null,
        filters: { locations: selectedLocations, categories: selectedCategories, prices: selectedActivityPrices, eventDate: eventDateFilter, diningRadius },
        reason: isNoCoverage ? 'no_coverage' : 'no_results'
      });
      const candidates = [];
      if (selectedCategories.length > 0) candidates.push({ key: 'category', label: 'Category' });
      if (selectedLocations.length > 0) candidates.push({ key: 'location', label: 'Indoor/Outdoor' });
      if (selectedActivityPrices.length > 0) candidates.push({ key: 'price', label: 'Price' });
      if (selectedVibeFilters.length > 0) candidates.push({ key: 'vibe', label: 'Vibe' });
      if (targetRange) candidates.push({ key: 'date', label: 'Date' });
      if (searchCoords && maxDistance) candidates.push({ key: 'distance', label: 'Distance' });
      if (diningCoords.length > 0) candidates.push({ key: 'dining', label: 'Dining radius' });

      const ranked = candidates
        .map(c => ({ ...c, count: applyActivityFilters(c.key).length }))
        .filter(c => c.count > 0)
        .sort((a, b) => b.count - a.count);

      const cityName = (city.split(',')[0] || 'this area').trim();
      setComboANoMatch({
        message: `Nothing in ${cityName} matches all your filters right now.`,
        suggestion: ranked.length > 0
          ? (ranked[0].key === 'dining'
              ? `Try expanding your dining radius — that would surface ${ranked[0].count} ${ranked[0].count === 1 ? 'option' : 'options'}.`
              : `Try removing your ${ranked[0].label} filter — that would surface ${ranked[0].count} ${ranked[0].count === 1 ? 'option' : 'options'}.`)
          : `Try clearing some filters or trying a nearby area.`
      });
      return;
    }

    // Pool-of-one back-to-back guard
    if (pool.length === 1 && pool[0].name === lastComboActivityNameRef.current) {
      const cityName = (city.split(',')[0] || 'this area').trim();
      setComboANoMatch({
        message: `Only one activity in ${cityName} matches your filters — and you just saw it.`,
        suggestion: `Try widening your filters or check a nearby area for more options.`
      });
      return;
    }

    const result = weightedPick(pool);
    if (!result) return;
    comboASpinResultRef.current = result;
    setComboANoMatch(null);

    const settingLabel = result.indoor === true ? 'Indoor'
      : result.indoor === false ? 'Outdoor' : null;
    const settingIdx = settingLabel ? al.indexOf(settingLabel) : -1;
    const categoryIdx = deriveActivityCategoryIdx(result, ac);
    const priceIdx = ap.indexOf(result.priceRange);

    setComboASpinning(true);
    setComboASpinComplete([false, false, false]);
    setComboActivity(null);
    setComboRestaurant(null);
    setComboStep(1);
    setComboAFinalIndices([
      settingIdx  >= 0 ? settingIdx  : 0,
      categoryIdx >= 0 ? categoryIdx : 0,
      priceIdx    >= 0 ? priceIdx    : 0,
    ]);
  };

  const handleComboAWheelComplete = (index) => setComboASpinComplete(prev => { const next = [...prev]; next[index] = true; return next; });

  useEffect(() => {
    if (comboASpinComplete.every(Boolean) && comboASpinning) {
      setComboASpinning(false);
      setTimeout(() => {
        const activity = comboASpinResultRef.current;
        if (!activity) return;
        setComboActivity(activity);
        setComboStep(2);
        lastComboActivityNameRef.current = activity.name;

        // Backfill dining options around the chosen activity — the metro pool
        // is centered on the city search point, which can under-cover the
        // activity's neighborhood at walking radii. Fires while the user is
        // picking a radius, so results are merged before the dine spin.
        if (activity.lat && activity.lng) {
          fetchRestaurantsNearPoint(activity.lat, activity.lng, city).then(mergeRestaurantsIntoPool);
        }

        const [metro, state] = city.split(',').map(s => s.trim());
        trackSpin({
          spinType: 'combo_activity',
          resultId: activity.id,
          metro,
          state,
          filters: { locations: selectedLocations, categories: selectedCategories, prices: selectedActivityPrices, eventDate: eventDateFilter, diningRadius },
          resultName: activity.name,
          resultCategory: activity.category,
          resultPrice: activity.priceRange,
          resultSponsored: activity.sponsored || false,
          resultBoost: activity.boost || 0
        });
      }, 400 / SPIN_SPEED_MULTIPLIER);
    }
  }, [comboASpinComplete, comboASpinning, city, selectedLocations, selectedCategories, selectedActivityPrices, eventDateFilter]);

  const handleComboRestaurantSpin = () => {
    if (comboRSpinning || !comboActivity) return;

    const ap = selectedPrices.length > 0 ? selectedPrices : prices;
    const ac = selectedCuisines.length > 0 ? selectedCuisines : cuisines;
    const as = selectedStyles.length > 0 ? selectedStyles : styles;

    // Strict filter applier. The combo restaurant exposes a vibe (style)
    // selection, a cuisine selection, and a dining radius from the chosen
    // activity. Cuisine is a hard filter (matching the Eat tab) so the spin
    // never lands on a restaurant whose cuisine the user didn't pick.
    const applyComboRFilters = (skip = null) => {
      let p = sampleRestaurants;
      if (skip !== 'vibe' && comboVibeFilter !== 'Any') {
        p = p.filter(r => r.style === comboVibeFilter);
      }
      if (skip !== 'cuisine' && selectedCuisines.length > 0 && !selectedCuisines.includes('Trending')) {
        p = p.filter(r => selectedCuisines.includes(r.cuisine));
      }
      if (skip !== 'distance' && comboActivity?.lat && comboActivity?.lng) {
        p = p.filter(r => {
          // Coordinate-less restaurants could be anywhere — exclude them at
          // walking radii (≤1 mi) where "nearby" is the whole promise, but
          // let them through at driving radii to keep pools healthy.
          if (!r.lat || !r.lng) return diningRadius > 1;
          const dist = calculateDistance(comboActivity.lat, comboActivity.lng, r.lat, r.lng);
          return dist <= diningRadius;
        });
      }
      return p;
    };

    let pool = applyComboRFilters();

    if (pool.length === 0) {
      const [emptyMetro, emptyState] = (city || '').split(',').map(s => s.trim());
      trackSpinEmpty({
        spinType: 'combo_restaurant',
        metro: emptyMetro || null,
        state: emptyState || null,
        filters: { prices: selectedPrices, cuisines: selectedCuisines, radius: diningRadius, comboActivity: comboActivity?.name || null },
        reason: sampleRestaurants.length === 0 ? 'no_coverage' : 'no_results'
      });
      const candidates = [];
      if (comboVibeFilter !== 'Any') candidates.push({ key: 'vibe', label: 'Vibe' });
      if (selectedCuisines.length > 0 && !selectedCuisines.includes('Trending')) candidates.push({ key: 'cuisine', label: 'Cuisine' });
      if (comboActivity?.lat && comboActivity?.lng) candidates.push({ key: 'distance', label: 'Distance' });

      const ranked = candidates
        .map(c => ({ ...c, count: applyComboRFilters(c.key).length }))
        .filter(c => c.count > 0)
        .sort((a, b) => b.count - a.count);

      setComboRNoMatch({
        message: `No restaurants near ${comboActivity.name} match your settings.`,
        suggestion: ranked.length > 0
          ? `Try ${ranked[0].key === 'distance' ? 'expanding the dining radius' : 'changing the vibe to Any'} — that would surface ${ranked[0].count} ${ranked[0].count === 1 ? 'spot' : 'spots'}.`
          : `Try a different activity location or expand your search.`
      });
      return;
    }

    // Pool-of-one back-to-back guard
    if (pool.length === 1 && pool[0].name === lastComboRestaurantNameRef.current) {
      setComboRNoMatch({
        message: `Only one restaurant near ${comboActivity.name} matches your settings — and you just saw it.`,
        suggestion: `Try expanding the dining radius or changing the vibe.`
      });
      return;
    }

    // Soft filter: wheelable representation
    const wheelable = pool.filter(r =>
      ap.includes(r.price) &&
      (ac.includes(r.cuisine) || (ac.includes('Trending') && r.trending)) &&
      as.includes(r.style)
    );
    if (wheelable.length > 0) pool = wheelable;

    // Distance-decay weighting: within the radius, closer restaurants are
    // proportionally more likely (0.2 mi outweighs 2.9 mi ~3:1) while
    // sponsor boost still applies multiplicatively.
    const decayWeight = (r) => {
      const base = r.boost || 1;
      if (!comboActivity?.lat || !comboActivity?.lng || !r.lat || !r.lng) return base;
      const dist = calculateDistance(comboActivity.lat, comboActivity.lng, r.lat, r.lng);
      return dist === null ? base : base / (1 + dist);
    };

    const restaurant = weightedPick(pool, decayWeight);
    if (!restaurant) return;
    comboRSpinResultRef.current = restaurant;
    setComboRNoMatch(null);

    const priceIdx = ap.indexOf(restaurant.price);
    const styleIdx = as.indexOf(restaurant.style);
    let cuisineIdx = ac.indexOf(restaurant.cuisine);
    if (cuisineIdx === -1 && ac.includes('Trending') && restaurant.trending) {
      cuisineIdx = ac.indexOf('Trending');
    }

    setComboRSpinning(true);
    setComboRSpinComplete([false, false, false]);
    setComboRFinalIndices([
      priceIdx   >= 0 ? priceIdx   : 0,
      cuisineIdx >= 0 ? cuisineIdx : 0,
      styleIdx   >= 0 ? styleIdx   : 0,
    ]);
  };

  const handleComboRWheelComplete = (index) => setComboRSpinComplete(prev => { const next = [...prev]; next[index] = true; return next; });

  useEffect(() => {
    if (comboRSpinComplete.every(Boolean) && comboRSpinning) {
      setComboRSpinning(false);
      setTimeout(() => {
        const restaurant = comboRSpinResultRef.current;
        if (!restaurant) return;

        let distance = null;
        if (comboActivity?.lat && comboActivity?.lng && restaurant.lat && restaurant.lng) {
          distance = calculateDistance(comboActivity.lat, comboActivity.lng, restaurant.lat, restaurant.lng);
        }

        setComboRestaurant(restaurant);
        setComboDistance(distance);
        setShowComboResult(true);
        lastComboRestaurantNameRef.current = restaurant.name;
        addToHistory({ type: 'combo', activity: comboActivity, restaurant, city, distance });

        const [metro, state] = city.split(',').map(s => s.trim());
        trackSpin({
          spinType: 'combo_restaurant',
          resultId: restaurant.id,
          metro,
          state,
          filters: { prices: selectedPrices, cuisines: selectedCuisines, radius: diningRadius },
          resultName: restaurant.name,
          resultCategory: restaurant.cuisine,
          resultPrice: restaurant.price,
          resultSponsored: restaurant.sponsored || false,
          resultBoost: restaurant.boost || 0
        });
      }, 400 / SPIN_SPEED_MULTIPLIER);
    }
  }, [comboRSpinComplete, comboRSpinning, comboActivity, city, selectedPrices, selectedCuisines, diningRadius]);

  const resetCombo = () => { setComboStep(1); setComboActivity(null); setComboRestaurant(null); setComboDistance(null); setShowComboResult(false); setComboVibeFilter('Any'); };
  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'E';
  
  // Handle clicking on history/favorites items to show them again
  const handleItemClick = (item) => {
    setShowProfile(false); // Close profile panel
    
    if (item.type === 'restaurant') {
      setRestaurantResult(item);
    } else if (item.type === 'activity') {
      setActivityResult(item);
    } else if (item.type === 'combo' && item.activity && item.restaurant) {
      setComboActivity(item.activity);
      setComboRestaurant(item.restaurant);
      setComboDistance(item.distance || null);
      setShowComboResult(true);
    } else if (item.type === 'binge') {
      setBingeResult(item);
    } else if (item.type === 'delivery') {
      setOrderResult(item);
    }
  };

  // Binge spin handlers
  const handleBingeSpin = () => {
    if (bingeSpinning) return;

    const g = selectedGenres.length > 0 ? selectedGenres : genres;
    const p = selectedPlatforms.length > 0 ? selectedPlatforms : platforms;
    const l = selectedLengths.length > 0 ? selectedLengths : lengths;

    const streamingPool = sampleStreaming || [];
    if (streamingPool.length === 0) {
      trackSpinEmpty({
        spinType: 'binge',
        metro: null,
        state: null,
        filters: { genres: selectedGenres, platforms: selectedPlatforms, lengths: selectedLengths },
        reason: 'no_coverage'
      });
      setBingeNoMatch({
        message: `Streaming library is being updated.`,
        suggestion: `Please try again in a moment.`
      });
      return;
    }

    const applyBingeFilters = (skip = null) => {
      let p = streamingPool;
      if (skip !== 'platform' && selectedPlatforms.length > 0) {
        p = p.filter(s => selectedPlatforms.includes(s.platform));
      }
      if (skip !== 'genre' && selectedGenres.length > 0) {
        const isTrendingOnly = selectedGenres.length === 1 && selectedGenres[0] === 'Trending';
        p = p.filter(s =>
          isTrendingOnly
            ? s.trending
            : selectedGenres.includes(s.genre) || (selectedGenres.includes('Trending') && s.trending)
        );
      }
      if (skip !== 'length' && selectedLengths.length > 0) {
        p = p.filter(s => selectedLengths.includes(s.length));
      }
      return p;
    };

    let pool = applyBingeFilters();

    if (pool.length === 0) {
      trackSpinEmpty({
        spinType: 'binge',
        metro: null,
        state: null,
        filters: { genres: selectedGenres, platforms: selectedPlatforms, lengths: selectedLengths }
      });
      const candidates = [];
      if (selectedPlatforms.length > 0) candidates.push({ key: 'platform', label: 'Platform' });
      if (selectedGenres.length > 0) candidates.push({ key: 'genre', label: 'Genre' });
      if (selectedLengths.length > 0) candidates.push({ key: 'length', label: 'Length' });

      const ranked = candidates
        .map(c => ({ ...c, count: applyBingeFilters(c.key).length }))
        .filter(c => c.count > 0)
        .sort((a, b) => b.count - a.count);

      setBingeNoMatch({
        message: `Nothing in our library matches all your filters right now.`,
        suggestion: ranked.length > 0
          ? `Try removing your ${ranked[0].label} filter — that would surface ${ranked[0].count} ${ranked[0].count === 1 ? 'title' : 'titles'}.`
          : `Try clearing your filters to see what's available.`
      });
      return;
    }

    // Pool-of-one back-to-back guard
    if (pool.length === 1 && pool[0].name === lastBingeNameRef.current) {
      setBingeNoMatch({
        message: `Only one title in our library matches your filters — and you just saw it.`,
        suggestion: `Try clearing or changing a filter to see more.`
      });
      return;
    }

    const result = weightedPick(pool);
    if (!result) return;
    setBingeNoMatch(null);

    let genreIdx = g.indexOf(result.genre);
    if (genreIdx === -1 && g.includes('Trending') && result.trending) {
      genreIdx = g.indexOf('Trending');
    }
    const platformIdx = p.indexOf(result.platform);
    const lengthIdx = l.indexOf(result.length);

    const indices = [
      genreIdx    >= 0 ? genreIdx    : 0,
      platformIdx >= 0 ? platformIdx : 0,
      lengthIdx   >= 0 ? lengthIdx   : 0,
    ];

    bingeSpinDataRef.current = { indices, g, p, l, pool, result };
    bingeCompletedWheels.current = 0;

    setBingeResult(null);
    setBingeFinalIndices(indices);
    setBingeSpinning(true);
  };

  const handleBingeWheelComplete = () => {
    bingeCompletedWheels.current += 1;
    if (bingeCompletedWheels.current === 3) {
      setTimeout(() => {
        setBingeSpinning(false);
        const { result: show } = bingeSpinDataRef.current;
        if (!show) return;

        setBingeResult(show);
        lastBingeNameRef.current = show.name;
        addToHistory({ type: 'binge', ...show, city: 'Home' });

        trackSpin({
          spinType: 'binge',
          resultId: show.id,
          metro: null,
          state: null,
          filters: { genres: selectedGenres, platforms: selectedPlatforms, lengths: selectedLengths },
          resultName: show.name,
          resultCategory: show.genre,
          resultPrice: null,
          resultSponsored: show.sponsored || false,
          resultBoost: show.boost || 0
        });
      }, 400 / SPIN_SPEED_MULTIPLIER);
    }
  };

  // Order In spin handlers
  const handleOrderSpin = () => {
    if (orderSpinning) return;
    if (!city.trim()) { alert('Pick a destination first!'); return; }

    const c = selectedOrderCuisines.length > 0
      ? selectedOrderCuisines
      : cuisines.filter(x => x !== 'Trending');
    const deliveryPool = getDeliveryRestaurants();

    if (deliveryPool.length === 0) {
      const [emptyMetro, emptyState] = (city || '').split(',').map(s => s.trim());
      trackSpinEmpty({
        spinType: 'order',
        metro: emptyMetro || null,
        state: emptyState || null,
        filters: { cuisines: selectedOrderCuisines },
        reason: 'no_coverage'
      });
      const cityName = (city.split(',')[0] || 'this area').trim();
      setOrderNoMatch({
        message: `We don't have delivery data for ${cityName} yet.`,
        suggestion: `We're working on expanding coverage. Try a nearby city in the meantime.`
      });
      return;
    }

    const applyOrderFilters = (skip = null) => {
      let p = deliveryPool;
      if (skip !== 'cuisine' && selectedOrderCuisines.length > 0) {
        p = p.filter(r => selectedOrderCuisines.includes(r.cuisine));
      }
      if (skip !== 'distance' && searchCoords && maxDistance) {
        p = p.filter(r => {
          if (!r.lat || !r.lng) return false;
          const dist = calculateDistance(searchCoords.lat, searchCoords.lng, r.lat, r.lng);
          return dist !== null && dist <= maxDistance;
        });
      }
      return p;
    };

    let pool = applyOrderFilters();

    if (pool.length === 0) {
      const [emptyMetro, emptyState] = (city || '').split(',').map(s => s.trim());
      trackSpinEmpty({
        spinType: 'order',
        metro: emptyMetro || null,
        state: emptyState || null,
        filters: { cuisines: selectedOrderCuisines },
        reason: 'no_results'
      });
      const candidates = [];
      if (selectedOrderCuisines.length > 0) candidates.push({ key: 'cuisine', label: 'Cuisine' });
      if (searchCoords && maxDistance) candidates.push({ key: 'distance', label: 'Distance' });

      const ranked = candidates
        .map(c => ({ ...c, count: applyOrderFilters(c.key).length }))
        .filter(c => c.count > 0)
        .sort((a, b) => b.count - a.count);

      const cityName = (city.split(',')[0] || 'this area').trim();
      setOrderNoMatch({
        message: `No delivery spots in ${cityName} match your filters right now.`,
        suggestion: ranked.length > 0
          ? `Try removing your ${ranked[0].label} filter — that would surface ${ranked[0].count} ${ranked[0].count === 1 ? 'spot' : 'spots'}.`
          : `Try clearing your filters or expanding your area.`
      });
      return;
    }

    // Pool-of-one back-to-back guard
    if (pool.length === 1 && pool[0].name === lastOrderNameRef.current) {
      const cityName = (city.split(',')[0] || 'this area').trim();
      setOrderNoMatch({
        message: `Only one delivery spot in ${cityName} matches your filters — and you just saw it.`,
        suggestion: `Try widening your filters or expanding your area.`
      });
      return;
    }

    const result = weightedPick(pool);
    if (!result) return;
    setOrderNoMatch(null);

    const priceIdx = deliveryPrices.indexOf(result.price);
    const cuisineIdx = c.indexOf(result.cuisine);
    // Speed wheel is purely cosmetic — no field on the restaurant to match
    const speedIdx = Math.floor(Math.random() * 3);

    const indices = [
      priceIdx   >= 0 ? priceIdx   : 0,
      cuisineIdx >= 0 ? cuisineIdx : 0,
      speedIdx,
    ];

    orderSpinDataRef.current = { indices, cuisines: c, pool, result };
    orderCompletedWheels.current = 0;

    setOrderResult(null);
    setOrderFinalIndices(indices);
    setOrderSpinning(true);
  };

  const handleOrderWheelComplete = () => {
    orderCompletedWheels.current += 1;
    if (orderCompletedWheels.current === 3) {
      setTimeout(() => {
        setOrderSpinning(false);
        const { result: restaurant } = orderSpinDataRef.current;
        if (!restaurant) return;

        setOrderResult(restaurant);
        lastOrderNameRef.current = restaurant.name;
        addToHistory({ type: 'delivery', ...restaurant, city });

        const [metro, state] = city.split(',').map(s => s.trim());
        trackSpin({
          spinType: 'order',
          resultId: restaurant.id,
          metro,
          state,
          filters: { prices: [restaurant.price], cuisines: selectedOrderCuisines },
          resultName: restaurant.name,
          resultCategory: restaurant.cuisine,
          resultPrice: restaurant.price,
          resultSponsored: restaurant.sponsored || false,
          resultBoost: restaurant.boost || 0
        });
      }, 400 / SPIN_SPEED_MULTIPLIER);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@400;500;600;700&display=swap');
        
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        
        :root {
          --bg: #FBF7F4;
          --bg-warm: #F5EDE6;
          --bg-card: #FFFFFF;
          --text: #2D2A26;
          --text-secondary: #6B6560;
          --text-muted: #9C9590;
          --terracotta: #C4593B;
          --terracotta-light: #E8D5CE;
          --teal: #2D6A6A;
          --teal-light: #D4E5E5;
          --gold: #C9A962;
          --gold-light: #F5EED9;
          --border: #E8E2DC;
          --shadow: rgba(45, 42, 38, 0.08);
          --shadow-lg: rgba(45, 42, 38, 0.12);
        }
        
        body { 
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif; 
          background: var(--bg); 
          color: var(--text); 
          -webkit-font-smoothing: antialiased;
          line-height: 1.5;
        }
        
        .app { 
          max-width: 420px; 
          margin: 0 auto; 
          min-height: 100vh; 
          background: var(--bg);
          position: relative;
        }
        
        /* Header */
        .header { 
          padding: calc(env(safe-area-inset-top, 0px) + 48px) 24px 28px; 
          display: flex; 
          justify-content: space-between; 
          align-items: flex-start;
        }
        
        .brand {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .brand-icon {
          font-size: 1.5rem;
          color: var(--terracotta);
          display: inline-block;
        }
        .brand-icon.spinning {
          animation: sparkleSpin 0.5s linear;
        }
        @keyframes sparkleSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        
        .brand h1 { 
          font-family: 'DM Serif Display', serif;
          font-size: 1.8rem; 
          font-weight: 400;
          color: var(--text);
          letter-spacing: -0.02em;
        }
        
        .header-subtitle {
          font-size: 0.9rem;
          color: var(--text-secondary);
          margin-top: 4px;
        }
        
        .profile-btn { 
          width: 44px; 
          height: 44px; 
          border-radius: 50%; 
          border: 2px solid var(--border);
          background: var(--bg-card); 
          color: var(--text);
          font-weight: 600; 
          font-size: 0.85rem; 
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px var(--shadow);
        }
        
        .profile-btn:hover { 
          border-color: var(--terracotta);
          color: var(--terracotta);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px var(--shadow);
        }
        
        .sign-in-btn {
          padding: 10px 18px;
          border-radius: 10px;
          border: 2px solid var(--terracotta);
          background: transparent;
          color: var(--terracotta);
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .sign-in-btn:hover {
          background: var(--terracotta);
          color: white;
        }
        
        /* Navigation */
        .nav { 
          display: flex; 
          margin: 0 24px 20px; 
          background: var(--bg-card);
          border-radius: 14px; 
          padding: 5px;
          box-shadow: 0 2px 12px var(--shadow);
        }
        
        .nav-btn { 
          flex: 1; 
          padding: 12px 4px; 
          border: none; 
          background: transparent; 
          font-family: 'DM Sans', sans-serif;
          font-size: 0.85rem; 
          font-weight: 600;
          color: var(--text-muted); 
          cursor: pointer; 
          border-radius: 10px; 
          transition: all 0.2s ease;
        }
        
        .nav-btn.active { 
          background: var(--terracotta); 
          color: white;
          box-shadow: 0 2px 8px rgba(196, 89, 59, 0.3);
        }
        
        .nav-btn:not(.active):hover { 
          color: var(--text);
          background: var(--bg-warm);
        }
        
        /* City Search */
        .input-section { padding: 0 24px 20px; }
        
        .data-status {
          text-align: center;
          font-size: 0.75rem;
          margin-top: 8px;
          color: var(--text-muted);
        }
        
        .data-loading {
          color: var(--gold);
          animation: pulse 1.5s ease-in-out infinite;
        }
        
        .data-ready {
          color: var(--teal);
        }
        
        .data-none {
          color: var(--text-muted);
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .city-search { position: relative; }
        
        .search-input-wrapper { 
          display: flex; 
          align-items: center;
          gap: 12px; 
          background: var(--bg-card);
          border: 2px solid var(--border);
          border-radius: 14px;
          padding: 4px 4px 4px 16px;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px var(--shadow);
        }
        
        .search-input-wrapper:focus-within {
          border-color: var(--teal);
          box-shadow: 0 4px 16px var(--shadow);
        }
        
        .search-icon {
          width: 20px;
          height: 20px;
          color: var(--text-muted);
          flex-shrink: 0;
        }
        
        .input-field { 
          flex: 1; 
          padding: 14px 0;
          border: none; 
          background: transparent;
          font-family: 'DM Sans', sans-serif;
          font-size: 1rem; 
          color: var(--text);
        }
        
        .input-field:focus { outline: none; }
        .input-field::placeholder { color: var(--text-muted); }
        
        .location-btn { 
          width: 46px; 
          height: 46px; 
          border: none;
          border-radius: 10px; 
          background: var(--teal-light);
          color: var(--teal); 
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .location-btn:hover:not(:disabled) { 
          background: var(--teal);
          color: white;
        }
        
        .location-btn:disabled { opacity: 0.5; }
        .location-btn svg { width: 20px; height: 20px; }
        .location-btn.locating svg { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        
        .location-error { 
          margin-top: 8px; 
          font-size: 0.85rem; 
          color: var(--terracotta);
          padding-left: 4px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
        }
        
        .request-city-btn {
          padding: 6px 12px;
          background: var(--terracotta);
          color: white;
          border: none;
          border-radius: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .request-city-btn:hover {
          background: #A84832;
        }
        
        .request-city-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .request-success {
          margin-top: 8px;
          font-size: 0.85rem;
          color: #2D6A6A;
          padding: 8px 12px;
          background: #D4E5E5;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .request-success svg {
          flex-shrink: 0;
        }
        
        .request-loading,
        .request-downloading {
          margin-top: 8px;
          font-size: 0.85rem;
          padding: 8px 12px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .request-loading {
          color: var(--text-muted);
          background: var(--bg-warm);
        }
        
        .request-downloading {
          color: var(--terracotta);
          background: rgba(196, 89, 59, 0.1);
        }
        
        .loading-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid currentColor;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          flex-shrink: 0;
        }
        
        .disambig-picker {
          margin-top: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .disambig-label {
          font-size: 0.85rem;
          color: var(--text-muted);
          white-space: nowrap;
        }

        .disambig-pills {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .matched-city-feedback {
          margin-top: 8px;
          font-size: 0.8rem;
          color: var(--text-muted);
          padding-left: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .matched-city-feedback svg {
          color: var(--terracotta);
          flex-shrink: 0;
        }
        
        .suggestions-list { 
          position: absolute; 
          top: calc(100% + 8px); 
          left: 0; 
          right: 0; 
          background: var(--bg-card); 
          border: 1px solid var(--border);
          border-radius: 14px;
          list-style: none; 
          z-index: 50; 
          overflow: hidden;
          box-shadow: 0 8px 24px var(--shadow-lg);
        }
        
        .suggestion-item { 
          display: flex; 
          align-items: center; 
          gap: 12px; 
          padding: 14px 18px; 
          font-size: 0.95rem;
          cursor: pointer;
          transition: background 0.15s;
          border-bottom: 1px solid var(--border);
        }
        
        .suggestion-item:last-child { border-bottom: none; }
        .suggestion-item:hover { background: var(--bg-warm); }
        .suggestion-item svg { width: 18px; height: 18px; color: var(--terracotta); }
        .suggestion-header { font-size: 11px; color: #999; cursor: default; padding: 6px 14px; text-transform: uppercase; letter-spacing: 0.05em; }
        .suggestion-header:hover { background: none; }
        
        /* Event Date Filter */
        .event-date-filter {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 24px;
          margin-bottom: 16px;
        }
        
        .date-filter-btn {
          padding: 10px 16px;
          border: 2px solid var(--border);
          border-radius: 10px;
          background: var(--bg-card);
          color: var(--text-secondary);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .date-filter-btn:hover {
          border-color: var(--teal);
          color: var(--teal);
        }
        
        .date-filter-btn.active {
          background: var(--teal);
          border-color: var(--teal);
          color: white;
        }
        
        .date-filter-btn.calendar-btn {
          padding: 10px 12px;
        }
        
        .date-filter-btn.calendar-btn svg {
          flex-shrink: 0;
        }
        
        .date-clear-btn {
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 50%;
          background: var(--bg-warm);
          color: var(--text-muted);
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .date-clear-btn:hover {
          background: var(--terracotta-light);
          color: var(--terracotta);
        }
        
        .date-picker-overlay {
          position: fixed;
          inset: 0;
          background: rgba(45, 42, 38, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 200;
          padding: 20px;
        }
        
        .date-picker-modal {
          background: var(--bg-card);
          border-radius: 16px;
          padding: 20px;
          width: 100%;
          max-width: 340px;
          box-shadow: 0 20px 60px rgba(45, 42, 38, 0.3);
        }
        
        .date-picker-modal h3 {
          font-family: 'DM Serif Display', serif;
          font-size: 1.2rem;
          margin: 0;
          color: var(--text);
        }
        
        /* ----- Calendar (DateRangeCalendar) ----- */
        .date-picker-modal-cal {
          padding: 18px 16px 16px;
        }
        
        .cal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 14px;
        }
        
        .cal-header h3 {
          flex: 1;
          text-align: center;
          font-size: 1.15rem;
        }
        
        .cal-nav {
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 50%;
          background: var(--bg-warm);
          color: var(--text);
          font-size: 1.4rem;
          line-height: 1;
          cursor: pointer;
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }
        
        .cal-nav:hover:not(:disabled) {
          background: var(--teal);
          color: white;
        }
        
        .cal-nav:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        
        .cal-weekdays {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          margin-bottom: 6px;
        }
        
        .cal-weekday {
          text-align: center;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.72rem;
          font-weight: 600;
          color: var(--text-muted);
          padding: 4px 0;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }
        
        .cal-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
        }
        
        .cal-cell {
          aspect-ratio: 1 / 1;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.92rem;
          font-weight: 500;
        }
        
        .cal-empty {
          background: transparent;
        }
        
        .cal-day {
          border: none;
          background: transparent;
          color: var(--text);
          cursor: pointer;
          border-radius: 50%;
          transition: background 0.12s ease, color 0.12s ease;
          padding: 0;
        }
        
        .cal-day:hover:not(:disabled):not(.cal-selected) {
          background: var(--bg-warm);
        }
        
        .cal-day.cal-disabled {
          color: var(--text-muted);
          opacity: 0.3;
          cursor: not-allowed;
        }
        
        .cal-day.cal-today:not(.cal-selected) {
          box-shadow: inset 0 0 0 2px var(--teal);
          color: var(--teal);
          font-weight: 700;
        }
        
        .cal-day.cal-in-range {
          background: var(--teal-light, rgba(0, 128, 128, 0.15));
          border-radius: 0;
        }
        
        .cal-day.cal-selected {
          background: var(--teal);
          color: white;
          font-weight: 700;
          box-shadow: none;
        }
        
        /* Range endpoints: square inside-edge so range bar joins cleanly */
        .cal-day.cal-range-start {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
        
        .cal-day.cal-range-end {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
        
        .cal-hint {
          text-align: center;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.78rem;
          color: var(--text-muted);
          margin: 14px 0 12px;
          min-height: 18px;
        }
        
        .cal-actions {
          display: flex;
          gap: 8px;
        }
        
        .cal-btn {
          flex: 1;
          padding: 12px;
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.92rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        
        .cal-btn-secondary {
          border: 2px solid var(--border);
          background: transparent;
          color: var(--text-secondary);
        }
        
        .cal-btn-secondary:hover {
          border-color: var(--text-secondary);
          background: var(--bg-warm);
        }
        
        .cal-btn-primary {
          border: 2px solid var(--teal);
          background: var(--teal);
          color: white;
        }
        
        .cal-btn-primary:hover {
          filter: brightness(1.05);
        }
        
        /* Filters */
        .filter-toggle { 
          display: flex; 
          align-items: center; 
          justify-content: space-between; 
          padding: 14px 18px; 
          margin: 0 24px 20px; 
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px var(--shadow);
        }
        
        .filter-toggle:hover { 
          border-color: var(--text-muted);
        }
        
        .filter-toggle span { 
          font-size: 0.9rem; 
          font-weight: 600; 
          color: var(--text-secondary);
        }
        
        .filter-toggle svg { 
          width: 18px; 
          height: 18px; 
          color: var(--text-muted); 
          transition: transform 0.3s ease;
        }
        
        .filter-toggle.open svg { transform: rotate(180deg); }
        
        .filters-panel { 
          padding: 0 24px 0; 
          max-height: 0; 
          overflow: hidden; 
          transition: max-height 0.4s ease, padding 0.4s ease;
        }
        
        .filters-panel.open { max-height: 1000px; padding-bottom: 20px; }
        
        .filter-group { margin-bottom: 20px; }
        .filter-group:last-child { margin-bottom: 8px; }
        
        .filter-group-label { 
          font-size: 0.8rem; 
          font-weight: 600; 
          color: var(--text-muted); 
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 10px;
        }
        
        .filter-pills { display: flex; flex-wrap: wrap; gap: 8px; padding-bottom: 6px; }
        
.distance-pills { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
        .clear-address-btn {
          background: none;
          border: none;
          color: #8B7355;
          font-size: 12px;
          cursor: pointer;
          text-decoration: underline;
          padding: 0;
        }
        .distance-badge {
          margin-left: 8px;
          padding: 2px 8px;
          background: #e8f5e9;
          color: #2e7d32;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 500;
        }
        
        .filter-pill { 
          padding: 8px 14px; 
          border: 1px solid var(--border);
          border-radius: 20px; 
          background: var(--bg-card);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.85rem; 
          font-weight: 500;
          color: var(--text-secondary); 
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .filter-pill:hover { 
          border-color: var(--terracotta);
          color: var(--terracotta);
        }
        
        .filter-pill.active { 
          background: var(--terracotta); 
          border-color: var(--terracotta); 
          color: white;
        }
        
        .filter-pill.trending {
          border-color: #FF6B35;
          color: #FF6B35;
        }
        
        .filter-pill.trending:hover {
          background: rgba(255, 107, 53, 0.1);
        }
        
        .filter-pill.trending.active {
          background: linear-gradient(135deg, #FF6B35 0%, #F7C59F 100%);
          border-color: transparent;
          color: white;
        }
        
        /* Wheel Section */
        .wheel-section { 
          margin: 0 24px 24px; 
          padding: 32px 20px; 
          background: var(--bg-card);
          border-radius: 20px;
          box-shadow: 0 4px 20px var(--shadow);
          border: 1px solid var(--border);
        }
        
        .wheel-header {
          text-align: center;
          margin-bottom: 28px;
        }
        
        .wheel-title { 
          font-family: 'DM Serif Display', serif;
          font-size: 1.4rem; 
          font-weight: 400;
          color: var(--text);
          margin-bottom: 4px;
        }
        
        .wheel-subtitle {
          font-size: 0.9rem;
          color: var(--text-muted);
        }
        
        .wheels { 
          display: flex; 
          justify-content: space-between; 
          gap: 10px; 
          margin-bottom: 28px;
        }
        
        .wheel-container { 
          flex: 1 1 0;
          text-align: center;
          min-width: 0;
          max-width: 33.333%;
        }
        
        .wheel-label { 
          display: block; 
          font-size: 0.7rem; 
          font-weight: 700; 
          color: var(--text-muted); 
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 10px;
          transition: color 0.3s ease;
        }
        
        .wheel-container:has(.phase-settled) .wheel-label {
          color: var(--teal);
        }
        
        .wheel-container:has(.phase-spinning) .wheel-label,
        .wheel-container:has(.phase-slowing) .wheel-label {
          color: var(--gold);
        }
        
        .wheel-display { 
          height: 130px; 
          width: 100%;
          display: flex; 
          align-items: center; 
          justify-content: center; 
          background: linear-gradient(180deg, var(--bg-warm) 0%, #FFF 50%, var(--bg-warm) 100%);
          border-radius: 16px;
          border: 2px solid var(--border);
          transition: border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease, transform 0.15s ease;
          overflow: hidden;
          position: relative;
          box-shadow: inset 0 4px 12px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04);
        }
        
        /* Center highlight line */
        .wheel-display::before {
          content: '';
          position: absolute;
          left: 8px;
          right: 8px;
          top: 50%;
          height: 32px;
          transform: translateY(-50%);
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.7) 50%, transparent 100%);
          border-radius: 6px;
          z-index: 1;
          pointer-events: none;
        }
        
        /* Top/bottom fades */
        .wheel-display::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, 
            var(--bg-warm) 0%, 
            transparent 25%, 
            transparent 75%, 
            var(--bg-warm) 100%
          );
          z-index: 2;
          pointer-events: none;
          transition: all 0.3s ease;
        }
        
        /* Spinning states */
        .wheel-display.phase-spinning,
        .wheel-display.phase-slowing {
          border-color: var(--gold);
          background: linear-gradient(180deg, var(--gold-light) 0%, #FFFEF5 50%, var(--gold-light) 100%);
        }
        
        .wheel-display.phase-spinning::after,
        .wheel-display.phase-slowing::after {
          background: linear-gradient(180deg, 
            var(--gold-light) 0%, 
            transparent 25%, 
            transparent 75%, 
            var(--gold-light) 100%
          );
        }
        
        /* Speed-based effects */
        .wheel-display.speed-fast {
          box-shadow: inset 0 4px 12px rgba(201, 169, 98, 0.2), 0 0 30px rgba(201, 169, 98, 0.3);
        }
        
        .wheel-display.speed-fast::before {
          animation: shimmer 0.6s ease-in-out infinite;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%);
        }
        
        @keyframes shimmer {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        
        .wheel-display.speed-medium {
          box-shadow: inset 0 4px 12px rgba(201, 169, 98, 0.15), 0 0 20px rgba(201, 169, 98, 0.25);
        }
        
        .wheel-display.speed-slow {
          box-shadow: inset 0 4px 12px rgba(201, 169, 98, 0.1), 0 0 15px rgba(201, 169, 98, 0.2);
          border-color: #D4B896;
        }
        
        .wheel-display.speed-crawl {
          box-shadow: inset 0 4px 12px rgba(45, 106, 106, 0.1), 0 0 12px rgba(45, 106, 106, 0.15);
          border-color: var(--teal);
          background: linear-gradient(180deg, var(--teal-light) 0%, #F5FAFA 50%, var(--teal-light) 100%);
        }
        
        .wheel-display.speed-crawl::after {
          background: linear-gradient(180deg, 
            var(--teal-light) 0%, 
            transparent 25%, 
            transparent 75%, 
            var(--teal-light) 100%
          );
        }
        
        /* Settled state with celebration */
        .wheel-display.phase-settled {
          border-color: var(--teal);
          background: linear-gradient(180deg, var(--teal-light) 0%, #F0FAF8 50%, var(--teal-light) 100%);
          box-shadow: inset 0 2px 8px rgba(45, 106, 106, 0.1), 0 0 20px rgba(45, 106, 106, 0.25), 0 4px 12px rgba(45, 106, 106, 0.15);
          animation: settleBounce 0.31s cubic-bezier(0.34, 1.56, 0.64, 1); /* 0.4s / 1.3 spin speed-up */
        }
        
        .wheel-display.phase-settled::before {
          background: linear-gradient(90deg, transparent 0%, rgba(45, 106, 106, 0.15) 50%, transparent 100%);
        }
        
        .wheel-display.phase-settled::after {
          background: linear-gradient(180deg, 
            var(--teal-light) 0%, 
            transparent 25%, 
            transparent 75%, 
            var(--teal-light) 100%
          );
        }
        
        @keyframes settleBounce {
          0% { transform: scale(1.03); }
          50% { transform: scale(0.98); }
          100% { transform: scale(1); }
        }
        
        .wheel-reel {
          display: flex;
          flex-direction: column;
          align-items: center;
          animation-name: slotRoll;
          animation-timing-function: linear;
          animation-fill-mode: forwards;
          width: 100%;
        }
        
        @keyframes slotRoll {
          0% { transform: translateY(-28px); }
          100% { transform: translateY(0); }
        }
        
        .wheel-display.phase-settled .wheel-reel {
          animation: slotLand 0.31s cubic-bezier(0.34, 1.56, 0.64, 1); /* 0.4s / 1.3 spin speed-up */
        }
        
        @keyframes slotLand {
          0% { transform: translateY(-28px); }
          60% { transform: translateY(4px); }
          100% { transform: translateY(0); }
        }
        
        .wheel-item {
          font-weight: 600;
          color: var(--text);
          min-height: 28px;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          padding: 2px 4px;
          line-height: 1.1;
          font-size: clamp(0.6rem, 2.8vw, 0.9rem);
          transition: all 0.15s ease;
        }
        
        .wheel-item.far {
          opacity: 0.12;
          font-size: 0.6rem;
          color: var(--text-muted);
          filter: blur(1px);
        }
        
        .wheel-item.prev,
        .wheel-item.next {
          opacity: 0.35;
          font-size: 0.75rem;
          color: var(--text-muted);
        }
        
        .wheel-item.current {
          opacity: 1;
          font-size: 0.95rem;
          font-weight: 700;
          color: var(--text);
        }
        
        /* Speed-based text effects */
        .wheel-display.speed-fast .wheel-item.current {
          filter: blur(0.5px);
        }
        
        .wheel-display.speed-fast .wheel-item.prev,
        .wheel-display.speed-fast .wheel-item.next {
          filter: blur(1.5px);
          opacity: 0.25;
        }
        
        .wheel-display.speed-fast .wheel-item.far {
          filter: blur(2px);
          opacity: 0.08;
        }
        
        .wheel-display.speed-medium .wheel-item.prev,
        .wheel-display.speed-medium .wheel-item.next {
          filter: blur(0.5px);
        }
        
        /* Settled text highlight */
        .wheel-display.phase-settled .wheel-item.current {
          color: var(--teal);
          transform: scale(1.05);
          text-shadow: 0 0 8px rgba(45, 106, 106, 0.3);
        }
        
        .wheel-display.phase-settled .wheel-item.prev,
        .wheel-display.phase-settled .wheel-item.next,
        .wheel-display.phase-settled .wheel-item.far {
          opacity: 0.15;
        }
        
        .spin-btn { 
          width: 100%; 
          padding: 18px 24px; 
          border: none; 
          border-radius: 14px; 
          background: var(--terracotta);
          color: white; 
          font-family: 'DM Sans', sans-serif;
          font-size: 1.1rem; 
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(196, 89, 59, 0.3);
          position: relative;
          overflow: hidden;
        }
        
        .spin-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: none;
        }
        
        .spin-btn:hover:not(:disabled)::before {
          left: 100%;
          transition: left 0.5s ease;
        }
        
        .spin-btn:hover:not(:disabled) { 
          background: #B34E32;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(196, 89, 59, 0.4);
        }
        
        .spin-btn:disabled { 
          background: var(--gold);
          cursor: wait;
          animation: btnPulse 1.5s ease-in-out infinite;
          box-shadow: 0 4px 16px rgba(201, 169, 98, 0.4);
        }
        
        @keyframes btnPulse {
          0%, 100% { box-shadow: 0 4px 16px rgba(201, 169, 98, 0.4); }
          50% { box-shadow: 0 4px 24px rgba(201, 169, 98, 0.6); }
        }
        
        /* Combo Step Indicator */
        .combo-progress {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 24px;
        }
        
        .progress-step {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .step-circle {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          font-weight: 600;
          border: 2px solid var(--border);
          color: var(--text-muted);
          background: var(--bg-card);
          transition: all 0.3s ease;
        }
        
        .step-circle.active {
          border-color: var(--terracotta);
          background: var(--terracotta);
          color: white;
        }
        
        .step-circle.completed {
          border-color: var(--teal);
          background: var(--teal);
          color: white;
        }
        
        .step-label {
          font-size: 0.8rem;
          color: var(--text-muted);
          font-weight: 500;
        }
        
        .step-label.active { color: var(--terracotta); }
        .step-label.completed { color: var(--teal); }
        
        .progress-line {
          width: 40px;
          height: 2px;
          background: var(--border);
          transition: background 0.3s ease;
        }
        
        .progress-line.completed { background: var(--teal); }
        
        /* Selected Activity Card */
        .selected-card {
          background: var(--teal-light);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
          border-left: 4px solid var(--teal);
        }
        
        .selected-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--teal);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        
        .selected-name {
          font-family: 'DM Serif Display', serif;
          font-size: 1.15rem;
          color: var(--text);
          margin-bottom: 2px;
        }
        
        .selected-meta {
          font-size: 0.85rem;
          color: var(--text-secondary);
        }
        
        /* Result Cards */
        .result-overlay { 
          position: fixed; 
          inset: 0; 
          background: rgba(45, 42, 38, 0.6);
          backdrop-filter: blur(8px);
          display: flex; 
          align-items: center; 
          justify-content: center; 
          z-index: 100; 
          padding: 24px;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        
        .result-card { 
          background: var(--bg-card);
          border-radius: 24px; 
          padding: 28px; 
          width: 100%; 
          max-width: 340px;
          max-height: calc(100vh - 48px);
          overflow-y: auto;
          position: relative;
          animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 20px 60px rgba(45, 42, 38, 0.25);
        }
        
        .result-card.combo-card { max-width: 360px; }
        
        @keyframes slideUp { 
          from { opacity: 0; transform: translateY(40px) scale(0.95); } 
          to { opacity: 1; transform: translateY(0) scale(1); } 
        }
        
        .close-btn { 
          position: absolute; 
          top: 16px; 
          right: 16px; 
          width: 36px; 
          height: 36px; 
          border: none; 
          background: var(--bg-warm);
          border-radius: 50%;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .close-btn:hover { 
          background: var(--terracotta-light);
          color: var(--terracotta);
        }
        
        .close-btn svg { width: 18px; height: 18px; }
        
        /* Card Actions (favorite + close) */
        .card-actions {
          position: absolute;
          top: 16px;
          right: 16px;
          display: flex;
          gap: 8px;
        }
        
        .card-actions .close-btn {
          position: static;
        }
        
        /* Trending Badge */
        .result-type-row {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          flex: 1;
          min-width: 0;
        }
        
        .trending-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          background: linear-gradient(135deg, #FF6B35 0%, #F7C59F 100%);
          color: white;
          border-radius: 12px;
          font-weight: 600;
          letter-spacing: 0.02em;
        }
        
        .sponsored-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          background: linear-gradient(135deg, #6366F1 0%, #A78BFA 100%);
          color: white;
          border-radius: 12px;
          font-weight: 600;
          letter-spacing: 0.02em;
        }
        
        .trending-badge-sm {
          font-size: 0.65rem;
          padding: 2px 6px;
          background: linear-gradient(135deg, #FF6B35 0%, #F7C59F 100%);
          color: white;
          border-radius: 8px;
          font-weight: 600;
          margin-left: 6px;
        }
        
        .trending-badge-xs {
          font-size: 0.6rem;
          padding: 2px 5px;
          background: linear-gradient(135deg, #FF6B35 0%, #F7C59F 100%);
          color: white;
          border-radius: 6px;
          font-weight: 600;
          margin-left: 6px;
        }
        
        .stop-label-row {
          display: flex;
          align-items: center;
        }
        
        /* Favorite items in profile */
        .favorite-item .history-icon {
          position: relative;
        }
        
        .favorite-icon::after {
          content: '♥';
          position: absolute;
          bottom: -2px;
          right: -2px;
          font-size: 0.6rem;
          color: #E91E63;
        }
        
        .result-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-right: 40px;
          gap: 12px;
        }
        
        .result-type {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--teal);
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        
        .result-rating {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--gold);
          flex-shrink: 0;
        }
        
        .result-rating .star { font-size: 1rem; }
        
        .review-count {
          color: var(--text-muted);
          font-weight: 500;
          font-size: 0.85rem;
          margin-left: 2px;
        }
        
        .result-name { 
          font-family: 'DM Serif Display', serif;
          font-size: 1.8rem; 
          font-weight: 400;
          color: var(--text);
          margin-bottom: 16px;
          line-height: 1.2;
        }
        
        .result-tags { 
          display: flex; 
          flex-wrap: wrap; 
          gap: 8px; 
          margin-bottom: 20px;
        }
        
        .tag {
          font-size: 0.8rem;
          padding: 6px 12px;
          background: var(--bg-warm);
          border-radius: 20px;
          color: var(--text-secondary);
          font-weight: 500;
        }
        
        .tag.cuisine, .tag.category {
          background: var(--terracotta-light);
          color: var(--terracotta);
        }
        
        .result-description {
          font-size: 0.95rem;
          color: var(--text-secondary);
          line-height: 1.6;
          margin-bottom: 24px;
        }
        
        .maps-link { 
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%; 
          padding: 14px; 
          background: var(--teal);
          color: white; 
          text-decoration: none; 
          border-radius: 12px; 
          font-size: 0.95rem; 
          font-weight: 600;
          transition: all 0.2s ease;
        }
        
        .maps-link:hover { 
          background: #245858;
          transform: translateY(-1px);
        }
        
        .maps-link svg { width: 18px; height: 18px; }
        
        /* Ride Section */
        .ride-section {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid var(--border);
          text-align: center;
        }
        
        .ride-label {
          display: block;
          font-size: 0.85rem;
          color: var(--text-muted);
          margin-bottom: 12px;
        }
        
        .ride-buttons {
          display: flex;
          gap: 12px;
        }
        
        .ride-btn {
          flex: 1;
          padding: 12px 16px;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          text-decoration: none;
          text-align: center;
          transition: all 0.2s ease;
        }
        
        .ride-btn.uber {
          background: #000;
          color: #fff;
        }
        
        .ride-btn.uber:hover {
          background: #333;
          transform: translateY(-1px);
        }
        
        .ride-btn.lyft {
          background: #FF00BF;
          color: #fff;
        }
        
        .ride-btn.lyft:hover {
          background: #D600A1;
          transform: translateY(-1px);
        }
        
        /* Save Button */
        .save-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 12px;
          margin-top: 12px;
          border: 2px solid var(--border);
          border-radius: 10px;
          background: transparent;
          color: var(--text-secondary);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .save-btn:hover {
          border-color: var(--terracotta);
          color: var(--terracotta);
          background: var(--terracotta-light);
        }
        
        .save-btn.saved {
          border-color: var(--terracotta);
          background: var(--terracotta);
          color: white;
        }
        
        .save-btn svg {
          width: 18px;
          height: 18px;
        }

        .combo-save-row {
          display: flex;
          gap: 10px;
          margin-top: 12px;
        }

        .combo-save-row .save-btn {
          margin-top: 0;
          flex: 1;
        }

        .save-btn.share {
          margin-top: 0;
        }

        .save-btn.share:hover {
          border-color: var(--teal);
          color: var(--teal);
          background: rgba(45, 142, 138, 0.06);
        }
        
        /* =================================================================
           RESULT CARD V2 - New Premium Design
           ================================================================= */
        .result-card-v2 {
          position: relative;
          width: 100%;
          max-width: 340px;
          border-radius: 24px;
          overflow: hidden;
          animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 20px 60px rgba(45, 42, 38, 0.3);
        }
        
        .card-gradient-bg {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 200px;
          background: linear-gradient(180deg, 
            rgba(210, 140, 110, 0.9) 0%, 
            rgba(230, 180, 150, 0.7) 40%, 
            rgba(251, 247, 244, 0.3) 100%
          );
          filter: blur(0px);
          z-index: 0;
        }
        
        .restaurant-card-v2 .card-gradient-bg {
          background: linear-gradient(180deg, 
            rgba(196, 89, 59, 0.25) 0%, 
            rgba(220, 160, 130, 0.4) 30%, 
            rgba(251, 247, 244, 0.95) 70%,
            rgba(255, 255, 255, 1) 100%
          );
        }
        
        .activity-card-v2 .card-gradient-bg {
          background: linear-gradient(180deg, 
            rgba(45, 106, 106, 0.25) 0%, 
            rgba(100, 160, 160, 0.3) 30%, 
            rgba(251, 247, 244, 0.95) 70%,
            rgba(255, 255, 255, 1) 100%
          );
        }
        
        .binge-card-v2 .card-gradient-bg {
          background: linear-gradient(180deg, 
            rgba(99, 102, 241, 0.25) 0%, 
            rgba(167, 139, 250, 0.3) 30%, 
            rgba(251, 247, 244, 0.95) 70%,
            rgba(255, 255, 255, 1) 100%
          );
        }
        
        .delivery-card-v2 .card-gradient-bg {
          background: linear-gradient(180deg, 
            rgba(212, 168, 75, 0.25) 0%, 
            rgba(230, 200, 130, 0.3) 30%, 
            rgba(251, 247, 244, 0.95) 70%,
            rgba(255, 255, 255, 1) 100%
          );
        }
        
        .card-header-v2 {
          position: relative;
          z-index: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 60px 12px 24px;
        }
        
        .card-brand {
          font-family: 'DM Serif Display', serif;
          font-size: 1.1rem;
          font-style: italic;
          color: var(--text);
          opacity: 0.8;
        }
        
        .card-tab-badge {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.05em;
          padding: 6px 12px;
          background: var(--terracotta);
          color: white;
          border-radius: 8px;
        }
        
        .activity-card-v2 .card-tab-badge {
          background: var(--teal);
        }
        
        .binge-card-v2 .card-tab-badge {
          background: #6366F1;
        }
        
        .delivery-card-v2 .card-tab-badge {
          background: var(--gold);
          color: var(--text);
        }
        
        .card-category-pill {
          position: relative;
          z-index: 1;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin: 0 24px 16px;
          padding: 6px 14px;
          background: rgba(196, 89, 59, 0.15);
          border: 1px solid rgba(196, 89, 59, 0.25);
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          color: var(--terracotta);
        }
        
        .activity-card-v2 .card-category-pill {
          background: rgba(45, 106, 106, 0.15);
          border-color: rgba(45, 106, 106, 0.25);
          color: var(--teal);
        }
        
        .binge-card-v2 .card-category-pill {
          background: rgba(99, 102, 241, 0.15);
          border-color: rgba(99, 102, 241, 0.25);
          color: #6366F1;
        }
        
        .delivery-card-v2 .card-category-pill {
          background: rgba(212, 168, 75, 0.15);
          border-color: rgba(212, 168, 75, 0.25);
          color: #B8860B;
        }
        
        .sponsored-dot {
          color: var(--gold);
          font-size: 0.8rem;
        }
        
        .card-content-v2 {
          position: relative;
          z-index: 1;
          background: white;
          padding: 24px;
          border-radius: 20px 20px 0 0;
          margin-top: 20px;
        }
        
        .card-name-v2 {
          font-family: 'DM Serif Display', serif;
          font-size: 1.7rem;
          font-weight: 400;
          color: var(--text);
          margin-bottom: 8px;
          line-height: 1.2;
        }
        
        .card-meta-v2 {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 6px;
          font-size: 0.85rem;
          color: var(--text-secondary);
          margin-bottom: 12px;
        }
        
        .card-rating-v2 {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .card-rating-v2 .stars {
          color: var(--gold);
          font-size: 0.75rem;
          letter-spacing: -1px;
        }
        
        .card-rating-v2 .rating-num {
          font-weight: 600;
          color: var(--text);
        }
        
        .meta-dot {
          color: var(--text-muted);
        }
        
        .card-description-v2 {
          font-size: 0.9rem;
          color: var(--text-secondary);
          line-height: 1.5;
          margin-bottom: 20px;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-word;
        }
        
        .card-actions-v2 {
          display: flex;
          gap: 10px;
          margin-bottom: 16px;
        }
        
        .action-btn-v2 {
          flex: 1;
          padding: 12px 8px;
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.85rem;
          font-weight: 600;
          text-align: center;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 2px solid transparent;
        }
        
        .action-btn-v2.primary {
          background: var(--terracotta);
          color: white;
          border-color: var(--terracotta);
        }
        
        .action-btn-v2.primary:hover {
          background: #B34E32;
          border-color: #B34E32;
          transform: translateY(-1px);
        }
        
        .action-btn-v2.outline {
          background: transparent;
          color: var(--text);
          border-color: var(--border);
        }
        
        .action-btn-v2.outline:hover {
          border-color: var(--text-secondary);
          background: var(--bg-warm);
        }
        
        .action-btn-v2.outline.saved {
          background: var(--teal);
          color: white;
          border-color: var(--teal);
        }

        .action-btn-v2.outline.share {
          background: transparent;
          color: var(--text);
          border-color: var(--border);
        }

        .action-btn-v2.outline.share:hover {
          border-color: var(--teal);
          color: var(--teal);
          background: rgba(45, 142, 138, 0.06);
        }
        
        .affiliate-badges-v2 {
          display: flex;
          justify-content: center;
          gap: 8px;
          padding: 12px 0;
          border-top: 1px solid var(--border);
          border-bottom: 1px solid var(--border);
          margin-bottom: 16px;
        }
        
        .affiliate-badge {
          font-size: 0.6rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: var(--text-muted);
          text-decoration: none;
          padding: 6px 10px;
          background: var(--bg-warm);
          border-radius: 6px;
          transition: all 0.2s ease;
        }
        
        .affiliate-badge:hover {
          background: var(--bg);
          color: var(--text-secondary);
        }
        
        .card-actions-secondary {
          display: flex;
          gap: 10px;
          margin: 16px 0;
        }
        
        .action-btn-secondary {
          flex: 1;
          padding: 14px;
          background: var(--bg-warm);
          color: var(--text-secondary);
          border: 1px solid var(--border);
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          text-align: center;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .action-btn-secondary:hover {
          background: var(--bg);
          border-color: var(--text-muted);
        }
        
        .action-btn-secondary.saved {
          background: var(--teal);
          color: white;
          border-color: var(--teal);
        }

        .action-btn-secondary.share:hover {
          border-color: var(--teal);
          color: var(--teal);
        }
        
        .save-btn-standalone {
          width: 100%;
          padding: 14px;
          background: var(--bg-warm);
          color: var(--text-secondary);
          border: 1px solid var(--border);
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin: 16px 0;
        }
        
        .save-btn-standalone:hover {
          background: var(--bg);
          border-color: var(--text-muted);
        }
        
        .save-btn-standalone.saved {
          background: var(--teal);
          color: white;
          border-color: var(--teal);
        }
        
        .spin-again-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 16px;
          background: var(--terracotta);
          color: white;
          border: none;
          border-radius: 12px;
          font-family: 'DM Sans', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(196, 89, 59, 0.3);
        }
        
        .spin-again-btn:hover {
          background: #B34E32;
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(196, 89, 59, 0.4);
        }
        
        .spin-again-btn .spin-icon {
          font-size: 1.1rem;
        }
        
        /* Combo Result */
        .combo-header {
          text-align: center;
          margin-bottom: 24px;
        }
        
        .combo-header .brand-icon {
          font-size: 1.5rem;
          display: block;
          margin-bottom: 8px;
        }
        
        .combo-header h3 {
          font-family: 'DM Serif Display', serif;
          font-size: 1.4rem;
          font-weight: 400;
          color: var(--text);
        }
        
        .combo-timeline {
          position: relative;
        }
        
        .combo-stop {
          display: flex;
          gap: 16px;
        }
        
        .stop-marker {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.9rem;
          flex-shrink: 0;
        }
        
        .stop-marker.first {
          background: var(--teal);
          color: white;
        }
        
        .stop-marker.second {
          background: var(--terracotta);
          color: white;
        }
        
        .stop-content {
          flex: 1;
          padding-bottom: 8px;
        }
        
        .stop-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        
        .stop-content h4 {
          font-family: 'DM Serif Display', serif;
          font-size: 1.15rem;
          font-weight: 400;
          margin: 4px 0;
          color: var(--text);
        }
        
        .stop-content p {
          font-size: 0.85rem;
          color: var(--text-secondary);
          margin-bottom: 4px;
        }
        
        .stop-content a {
          font-size: 0.85rem;
          color: var(--teal);
          text-decoration: none;
          font-weight: 600;
        }
        
        .stop-content a:hover { text-decoration: underline; }
        
        .timeline-connector {
          width: 2px;
          height: 24px;
          background: var(--border);
          margin-left: 17px;
          margin-top: 4px;
          margin-bottom: 4px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .distance-badge {
          position: absolute;
          left: 20px;
          background: var(--teal-light);
          color: var(--teal);
          font-size: 0.7rem;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 20px;
          white-space: nowrap;
          border: 1px solid var(--teal);
        }
        
        /* Dining Radius Selector */
        .dining-radius-section {
          margin-bottom: 24px;
          padding: 16px;
          background: var(--bg-warm);
          border-radius: 14px;
        }
        
        .dining-radius-label {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-secondary);
          margin-bottom: 12px;
          text-align: center;
        }
        
        .dining-radius-options {
          display: flex;
          gap: 8px;
        }
        
        .radius-option {
          flex: 1;
          padding: 10px 8px;
          border: 2px solid var(--border);
          border-radius: 10px;
          background: var(--bg-card);
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
        }
        
        .radius-option:hover {
          border-color: var(--text-muted);
        }
        
        .radius-option.active {
          border-color: var(--teal);
          background: var(--teal-light);
        }
        
        .radius-option-label {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--text);
        }
        
        .radius-option.active .radius-option-label {
          color: var(--teal);
        }
        
        .radius-option-desc {
          font-size: 0.65rem;
          color: var(--text-muted);
        }
        
        .radius-option.active .radius-option-desc {
          color: var(--teal);
        }
        
        /* Stay In Tab Styles */
        .stayin-toggle {
          display: flex;
          gap: 12px;
          padding: 0 24px 20px;
        }
        
        .stayin-tab {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 16px 12px;
          border: 2px solid var(--border);
          border-radius: 14px;
          background: var(--bg-card);
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--text-secondary);
        }
        
        .stayin-tab:hover {
          border-color: var(--text-muted);
        }
        
        .stayin-tab.active {
          border-color: var(--teal);
          background: var(--teal-light);
          color: var(--teal);
        }
        
        .stayin-tab-icon {
          font-size: 1.5rem;
        }
        
        /* Binge Card */
        .result-card.binge-card {
          max-width: 360px;
        }
        
        .tag.genre {
          background: #E8D5F2;
          color: #6B3FA0;
        }
        
        .imdb-link {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 12px;
          background: #F5C518;
          color: #000;
          text-decoration: none;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          transition: all 0.2s ease;
          margin-bottom: 10px;
        }
        
        .imdb-link:hover {
          background: #E4B400;
          transform: translateY(-1px);
        }
        
        .imdb-logo {
          background: #000;
          color: #F5C518;
          padding: 3px 6px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: -0.5px;
        }
        
        .platform-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 20px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 0.95rem;
          margin-top: 8px;
        }
        
        .platform-icon {
          font-size: 0.8rem;
        }
        
        /* Delivery Card */
        .result-card.delivery-card {
          max-width: 360px;
        }
        
        .delivery-info {
          display: flex;
          gap: 20px;
          margin: 16px 0;
          padding: 12px 16px;
          background: var(--bg-warm);
          border-radius: 10px;
        }
        
        .delivery-stat {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          color: var(--text-secondary);
        }
        
        .delivery-icon {
          font-size: 1.1rem;
        }
        
        .platform-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }
        
        .platform-pill {
          padding: 8px 14px;
          background: var(--bg-warm);
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--text-secondary);
        }
        
        /* Profile Panel */
        .profile-overlay { 
          position: fixed; 
          inset: 0; 
          background: rgba(45, 42, 38, 0.6);
          backdrop-filter: blur(8px);
          display: flex; 
          align-items: flex-start; 
          justify-content: center; 
          z-index: 200; 
          padding: 24px;
          padding-top: 60px;
          overflow-y: auto;
          animation: fadeIn 0.3s ease;
        }
        
        .profile-panel { 
          background: var(--bg-card);
          border-radius: 24px; 
          padding: 28px; 
          width: 100%; 
          max-width: 380px;
          position: relative;
          animation: slideUp 0.4s ease;
          box-shadow: 0 20px 60px rgba(45, 42, 38, 0.25);
        }
        
        .profile-header { 
          display: flex; 
          gap: 16px; 
          align-items: center; 
          margin-bottom: 24px;
        }
        
        .avatar { 
          width: 60px; 
          height: 60px; 
          border-radius: 50%;
          background: var(--terracotta);
          color: white;
          display: flex; 
          align-items: center; 
          justify-content: center;
          font-weight: 700;
          font-size: 1.2rem;
        }
        
        .profile-info h2 { 
          font-family: 'DM Serif Display', serif;
          font-size: 1.3rem; 
          font-weight: 400;
          display: flex; 
          align-items: center; 
          gap: 8px; 
          cursor: pointer;
        }
        
        .profile-info h2 svg { color: var(--text-muted); }
        .profile-info h2:hover svg { color: var(--terracotta); }
        
        .member-since { 
          font-size: 0.85rem; 
          color: var(--text-muted); 
          margin-top: 2px;
        }
        
        .name-edit { display: flex; gap: 8px; }
        
        .name-edit input { 
          padding: 10px 14px; 
          border: 2px solid var(--border);
          border-radius: 8px;
          background: var(--bg-card);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          color: var(--text);
        }
        
        .name-edit input:focus { outline: none; border-color: var(--terracotta); }
        
        .name-edit button { 
          padding: 10px 16px; 
          border: none; 
          background: var(--terracotta); 
          color: white;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
        }
        
        .home-city-edit { display: flex; gap: 8px; flex-wrap: wrap; }
        
        .home-city-edit input { 
          flex: 1;
          min-width: 150px;
          padding: 10px 14px; 
          border: 2px solid var(--border);
          border-radius: 8px;
          background: var(--bg-card);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          color: var(--text);
        }
        
        .home-city-edit input:focus { outline: none; border-color: var(--terracotta); }
        
        .home-city-edit button { 
          padding: 10px 16px; 
          border: none; 
          background: var(--terracotta); 
          color: white;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
        }
        
        .home-city-edit .cancel-btn {
          background: var(--bg-warm);
          color: var(--text-muted);
          border: 2px solid var(--border);
        }
        
        .setting-value.editable {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: color 0.2s ease;
        }
        
        .setting-value.editable:hover {
          color: var(--terracotta);
        }
        
        .setting-value.editable svg {
          opacity: 0.5;
        }
        
        .setting-value.editable:hover svg {
          opacity: 1;
        }
        
        .profile-stats { 
          display: flex; 
          gap: 12px; 
          margin-bottom: 24px;
        }
        
        .stat { 
          flex: 1; 
          text-align: center; 
          padding: 16px 8px;
          background: var(--bg-warm);
          border-radius: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .stat:hover {
          background: var(--terracotta-light);
          transform: translateY(-2px);
        }
        
        .stat-value { 
          display: block; 
          font-family: 'DM Serif Display', serif;
          font-size: 1.8rem; 
          color: var(--terracotta);
        }
        
        .stat-label { 
          font-size: 0.75rem; 
          color: var(--text-muted); 
          font-weight: 500;
        }
        
        .profile-tabs { 
          display: flex; 
          gap: 8px; 
          margin-bottom: 20px;
        }
        
        .profile-tab { 
          flex: 1; 
          padding: 12px; 
          border: none; 
          background: var(--bg-warm);
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--text-muted);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .profile-tab.active { 
          background: var(--terracotta); 
          color: white;
        }
        
        .history-section { max-height: 280px; overflow-y: auto; }
        
        .empty-history { 
          text-align: center; 
          padding: 40px 20px;
        }
        
        .empty-history p { color: var(--text-secondary); }
        .empty-history .empty-sub { font-size: 0.9rem; color: var(--text-muted); margin-top: 4px; }
        
        .history-list { display: flex; flex-direction: column; gap: 10px; }
        
        .history-item { 
          display: flex; 
          align-items: center; 
          gap: 12px; 
          padding: 14px;
          background: var(--bg-warm);
          border-radius: 12px;
          transition: all 0.2s ease;
        }
        
        .history-item:hover {
          background: var(--terracotta-light);
          transform: translateY(-1px);
        }
        
        .history-icon { font-size: 1.2rem; }
        .history-content { flex: 1; min-width: 0; }

        .history-heart-btn {
          width: 32px;
          height: 32px;
          border: none;
          background: transparent;
          color: var(--text-muted);
          cursor: pointer;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          padding: 0;
          transition: all 0.2s ease;
        }

        .history-heart-btn:hover {
          background: rgba(233, 30, 99, 0.08);
          color: #E91E63;
          transform: scale(1.08);
        }

        .history-heart-btn.favorited {
          color: #E91E63;
        }

        .history-heart-btn.favorited:hover {
          background: rgba(233, 30, 99, 0.12);
        }

        .history-heart-btn svg {
          display: block;
        }
        
        .history-name { 
          font-weight: 600; 
          font-size: 0.9rem;
          white-space: nowrap; 
          overflow: hidden; 
          text-overflow: ellipsis;
        }
        
        .history-meta { 
          font-size: 0.8rem; 
          color: var(--text-muted);
        }
        
        .history-link {
          display: inline-block;
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--teal);
          text-decoration: none;
          margin-top: 4px;
        }
        
        .history-link:hover {
          text-decoration: underline;
        }
        
        .history-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 6px;
          flex-wrap: wrap;
        }
        
        .history-ride-link {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 6px;
          text-decoration: none;
          transition: all 0.2s ease;
        }
        
        .history-ride-link.uber {
          background: #000;
          color: #fff;
        }
        
        .history-ride-link.uber:hover {
          background: #333;
        }
        
        .history-ride-link.lyft {
          background: #FF00BF;
          color: #fff;
        }
        
        .history-ride-link.lyft:hover {
          background: #D600A1;
        }
        
        .history-ride-link.imdb {
          background: #F5C518;
          color: #000;
        }
        
        .history-ride-link.imdb:hover {
          background: #E4B400;
        }
        
        .delete-btn { 
          width: 32px; 
          height: 32px; 
          border: none; 
          background: transparent; 
          color: var(--text-muted); 
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .delete-btn:hover { 
          background: var(--terracotta-light);
          color: var(--terracotta);
        }
        
        .delete-btn svg { width: 16px; height: 16px; }
        
        .clear-history-btn { 
          width: 100%; 
          margin-top: 16px; 
          padding: 12px;
          border: 2px solid var(--terracotta);
          background: transparent;
          color: var(--terracotta);
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .clear-history-btn:hover { 
          background: var(--terracotta);
          color: white;
        }
        
        .settings-section { display: flex; flex-direction: column; gap: 12px; }
        
        .home-base-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: var(--bg-warm);
          border-radius: 10px;
          font-size: 0.9rem;
        }
        
        .home-base-label {
          color: var(--text-muted);
          font-weight: 500;
        }
        
        .home-base-value {
          font-weight: 600;
          color: var(--text);
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
        }
        
        .home-base-value svg {
          color: var(--text-muted);
          opacity: 0.6;
        }

        /* Default Maps toggle (Settings) */
        .maps-pref-row {
          justify-content: space-between;
        }

        .maps-pref-toggle {
          display: flex;
          gap: 4px;
          background: var(--bg-card);
          padding: 3px;
          border-radius: 8px;
          border: 1px solid var(--border);
        }

        .maps-pref-btn {
          padding: 6px 14px;
          border: none;
          background: transparent;
          color: var(--text-secondary);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
          border-radius: 6px;
          transition: background 0.15s ease, color 0.15s ease;
        }

        .maps-pref-btn:hover:not(.active) {
          color: var(--text);
        }

        .maps-pref-btn.active {
          background: var(--teal);
          color: white;
        }
        
        .home-city-edit-wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .home-city-edit-inline {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .home-city-edit-inline input {
          flex: 1;
          padding: 6px 10px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          min-width: 0;
        }

        .home-city-edit-inline input:focus {
          outline: none;
          border-color: var(--terracotta);
        }

        .home-city-edit-inline button {
          background: transparent;
          border: none;
          padding: 4px 8px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 1rem;
        }

        .home-city-edit-inline button:first-of-type { color: var(--teal); }
        .home-city-edit-inline button:last-of-type { color: var(--terracotta); }

        .home-city-error {
          font-size: 0.75rem;
          color: var(--terracotta);
        }
        
        .setting-item { 
          padding: 16px;
          background: var(--bg-warm);
          border-radius: 12px;
        }
        
        .setting-label { 
          font-size: 0.8rem; 
          color: var(--text-muted); 
          margin-bottom: 4px;
          font-weight: 500;
        }
        
        .setting-value { font-weight: 600; }
        
        .logout-btn { 
          margin-top: 8px; 
          padding: 14px;
          border: none; 
          background: var(--text);
          color: white;
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .logout-btn:hover { opacity: 0.9; }
        
        .delete-account-btn {
          margin-top: 12px;
          padding: 12px;
          border: 2px solid #dc3545;
          background: transparent;
          color: #dc3545;
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s ease;
          width: 100%;
        }
        
        .delete-account-btn:hover {
          background: #dc3545;
          color: white;
        }
        
        .sync-status {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 14px;
          background: var(--bg-warm);
          border-radius: 10px;
          margin-top: 12px;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }
        
        .sync-icon { font-size: 1.1rem; }
        
        .user-email {
          font-size: 0.85rem;
          color: var(--text-muted);
          margin-top: 2px;
        }
        
        /* Auth Modal */
        .auth-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(45, 42, 38, 0.7);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 300;
          padding: 20px;
        }
        
        .auth-modal {
          background: var(--bg-card);
          border-radius: 20px;
          padding: 32px 28px;
          width: 100%;
          max-width: 380px;
          box-shadow: 0 20px 60px rgba(45, 42, 38, 0.4);
          position: relative;
        }
        
        .auth-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 32px;
          height: 32px;
          border: none;
          background: var(--bg-warm);
          border-radius: 50%;
          color: var(--text-muted);
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .auth-modal-close:hover {
          background: var(--terracotta-light);
          color: var(--terracotta);
        }
        
        .auth-modal h2 {
          font-family: 'DM Serif Display', serif;
          font-size: 1.6rem;
          margin-bottom: 6px;
          color: var(--text);
        }
        
        .auth-subtitle {
          font-size: 0.9rem;
          color: var(--text-muted);
          margin-bottom: 24px;
        }
        
        .auth-field {
          margin-bottom: 16px;
        }
        
        .auth-field label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-secondary);
          margin-bottom: 6px;
        }
        
        .auth-field input {
          width: 100%;
          padding: 14px 16px;
          border: 2px solid var(--border);
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 1rem;
          color: var(--text);
          background: var(--bg);
          transition: border-color 0.2s ease;
          box-sizing: border-box;
        }
        
        .auth-field input:focus {
          outline: none;
          border-color: var(--terracotta);
        }
        
        .auth-field input::placeholder {
          color: var(--text-muted);
        }
        
        .auth-submit-btn {
          width: 100%;
          padding: 16px;
          border: none;
          background: var(--terracotta);
          color: white;
          border-radius: 12px;
          font-family: 'DM Sans', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 8px;
        }
        
        .auth-submit-btn:hover {
          background: #B34E32;
          transform: translateY(-1px);
        }
        
        .auth-error {
          background: #fff5f5;
          border: 1px solid #feb2b2;
          color: #c53030;
          padding: 12px 14px;
          border-radius: 10px;
          font-size: 0.9rem;
          margin-bottom: 16px;
        }
        
        .auth-success {
          text-align: center;
          padding: 20px 0;
        }
        
        .auth-success-icon {
          width: 48px;
          height: 48px;
          background: var(--teal);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          margin: 0 auto 16px;
        }
        
        .auth-success p {
          font-size: 1rem;
          color: var(--text);
          margin-bottom: 20px;
        }
        
        .auth-link-btn {
          background: none;
          border: none;
          color: var(--terracotta);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          cursor: pointer;
          margin-top: 16px;
          padding: 0;
        }
        
        .auth-link-btn:hover {
          text-decoration: underline;
        }
        
        .auth-divider {
          display: flex;
          align-items: center;
          margin: 24px 0;
        }
        
        .auth-divider::before,
        .auth-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--border);
        }
        
        .auth-divider span {
          padding: 0 16px;
          font-size: 0.85rem;
          color: var(--text-muted);
        }
        
        .auth-switch-btn {
          width: 100%;
          padding: 14px;
          border: 2px solid var(--border);
          background: transparent;
          color: var(--text);
          border-radius: 12px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .auth-switch-btn:hover {
          border-color: var(--text-secondary);
          background: var(--bg-warm);
        }
        
        .settings-footer {
          font-size: 0.65rem;
          color: var(--text-muted);
          text-align: center;
          margin-top: 16px;
          opacity: 0.6;
          line-height: 1.4;
        }
        
        /* Result card close (X) button */
        .card-close-x {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 32px;
          height: 32px;
          border: none;
          background: rgba(255, 255, 255, 0.85);
          border-radius: 50%;
          color: var(--text);
          cursor: pointer;
          z-index: 5;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          backdrop-filter: blur(4px);
          box-shadow: 0 2px 8px rgba(45, 42, 38, 0.15);
          transition: all 0.2s ease;
        }

        .card-close-x svg {
          width: 16px;
          height: 16px;
        }

        .card-close-x:hover {
          background: var(--terracotta);
          color: white;
          transform: scale(1.05);
        }

        /* Location bar inline clear (X) */
        .location-clear-btn {
          width: 32px;
          height: 32px;
          border: none;
          background: transparent;
          color: var(--text-muted);
          cursor: pointer;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          flex-shrink: 0;
          transition: all 0.15s ease;
        }

        .location-clear-btn svg {
          width: 14px;
          height: 14px;
        }

        .location-clear-btn:hover {
          background: var(--terracotta-light, rgba(196, 89, 59, 0.12));
          color: var(--terracotta);
        }

        /* Location-required prompt */
        .location-required-overlay {
          position: fixed;
          inset: 0;
          background: rgba(45, 42, 38, 0.7);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 350;
          padding: 20px;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .location-required-modal {
          background: var(--bg-card);
          border-radius: 20px;
          padding: 36px 28px 28px;
          width: 100%;
          max-width: 360px;
          box-shadow: 0 20px 60px rgba(45, 42, 38, 0.4);
          position: relative;
          text-align: center;
          animation: slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .location-required-close {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 32px;
          height: 32px;
          border: none;
          background: var(--bg-warm);
          border-radius: 50%;
          color: var(--text-muted);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: all 0.2s ease;
        }

        .location-required-close svg {
          width: 14px;
          height: 14px;
        }

        .location-required-close:hover {
          background: var(--terracotta-light, rgba(196, 89, 59, 0.12));
          color: var(--terracotta);
        }

        .location-required-icon {
          width: 56px;
          height: 56px;
          margin: 0 auto 16px;
          border-radius: 50%;
          background: var(--teal-light);
          color: var(--teal);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .location-required-icon svg {
          width: 28px;
          height: 28px;
        }

        .location-required-modal h2 {
          font-family: 'DM Serif Display', serif;
          font-size: 1.5rem;
          margin: 0 0 8px;
          color: var(--text);
        }

        .location-required-modal p {
          font-size: 0.95rem;
          color: var(--text-muted);
          margin: 0 0 22px;
          line-height: 1.5;
        }

        .location-required-btn {
          width: 100%;
          padding: 14px 20px;
          background: var(--teal);
          color: white;
          border: none;
          border-radius: 12px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .location-required-btn:hover {
          background: var(--teal-dark, #1f4a4a);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(45, 106, 106, 0.3);
        }

        /* Business Link in Settings */
        a.business-link,
        .business-link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          background: linear-gradient(135deg, #C4593B 0%, #A84832 100%);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          margin-top: 16px;
          text-decoration: none;
          color: white;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        
        a.business-link:hover,
        .business-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(196, 89, 59, 0.3);
          color: white;
        }
        
        a.business-link:visited {
          color: white;
        }
        
        .business-link-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .business-link-icon {
          font-size: 1.5rem;
          color: white !important;
        }
        
        .business-link-title {
          font-weight: 600;
          color: white !important;
          font-size: 0.95rem;
        }
        
        .business-link-desc {
          font-size: 0.8rem;
          color: rgba(255,255,255,0.8) !important;
        }
        
        .business-link-arrow {
          color: white !important;
          font-size: 1.2rem;
        }
        
        .affiliate-disclosure {
          margin-top: 8px;
        }
        
        .setting-value-small {
          font-size: 0.75rem;
          color: var(--text-muted);
          line-height: 1.4;
        }
        
        /* Sponsor Portal Modal */
        .sponsor-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 400;
          padding: 20px;
          overflow-y: auto;
        }
        
        .sponsor-modal {
          background: var(--bg);
          border-radius: 20px;
          max-width: 480px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        
        .sponsor-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 32px;
          height: 32px;
          border: none;
          background: var(--bg-warm);
          border-radius: 50%;
          font-size: 1.5rem;
          color: var(--text-muted);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }
        
        .sponsor-modal-close:hover {
          background: var(--border);
        }
        
        /* Sponsor Intro */
        .sponsor-intro {
          padding: 40px 32px;
          text-align: center;
        }
        
        .sponsor-icon {
          font-size: 3rem;
          color: var(--accent);
          margin-bottom: 16px;
        }
        
        .sponsor-intro h2 {
          font-family: 'DM Serif Display', serif;
          font-size: 1.75rem;
          margin-bottom: 12px;
          color: var(--text);
        }
        
        .sponsor-intro > p {
          color: var(--text-muted);
          font-size: 0.95rem;
          line-height: 1.5;
          margin-bottom: 24px;
        }
        
        .sponsor-tiers {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
        }
        
        .sponsor-tier {
          flex: 1;
          padding: 16px 12px;
          background: var(--bg-warm);
          border-radius: 12px;
          border: 2px solid var(--border);
          position: relative;
        }
        
        .sponsor-tier.featured {
          border-color: var(--accent);
          background: rgba(196, 89, 59, 0.05);
        }
        
        .tier-badge {
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: var(--accent);
          color: white;
          font-size: 0.65rem;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 10px;
          text-transform: uppercase;
        }
        
        .tier-name {
          font-weight: 600;
          font-size: 0.85rem;
          margin-bottom: 4px;
        }
        
        .tier-price {
          font-family: 'DM Serif Display', serif;
          font-size: 1.5rem;
          color: var(--accent);
        }
        
        .tier-price span {
          font-size: 0.75rem;
          color: var(--text-muted);
        }
        
        .tier-desc {
          font-size: 0.7rem;
          color: var(--text-muted);
          margin-top: 4px;
        }
        
        .sponsor-cta {
          width: 100%;
          padding: 16px;
          background: var(--accent);
          border: none;
          border-radius: 12px;
          color: white;
          font-family: 'DM Sans', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .sponsor-cta:hover {
          background: #A84832;
        }
        
        .sponsor-cta:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .sponsor-note {
          font-size: 0.8rem;
          color: var(--text-muted);
          margin-top: 12px;
        }
        
        /* Sponsor Form */
        .sponsor-form {
          padding: 32px;
        }
        
        .sponsor-form h2 {
          font-family: 'DM Serif Display', serif;
          font-size: 1.5rem;
          margin-bottom: 8px;
        }
        
        .sponsor-form > p {
          color: var(--text-muted);
          font-size: 0.9rem;
          margin-bottom: 24px;
        }
        
        .sponsor-error {
          background: #FEE2E2;
          color: #B91C1C;
          padding: 12px;
          border-radius: 8px;
          font-size: 0.85rem;
          margin-bottom: 16px;
        }
        
        .sponsor-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        
        .sponsor-form-grid .form-group {
          margin-bottom: 0;
        }
        
        .sponsor-form-grid .form-group.full-width {
          grid-column: 1 / -1;
        }
        
        .sponsor-form-grid label {
          display: block;
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--text-muted);
          margin-bottom: 6px;
        }
        
        .sponsor-form-grid input {
          width: 100%;
          padding: 12px 14px;
          background: var(--bg-warm);
          border: 2px solid var(--border);
          border-radius: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          transition: border-color 0.2s ease;
        }
        
        .sponsor-form-grid input:focus {
          outline: none;
          border-color: var(--accent);
        }
        
        .sponsor-form-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }
        
        .sponsor-back {
          flex: 1;
          padding: 14px;
          background: var(--bg-warm);
          border: 2px solid var(--border);
          border-radius: 12px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          cursor: pointer;
        }
        
        .sponsor-back:hover {
          border-color: var(--text-muted);
        }
        
        .sponsor-form-actions .sponsor-cta {
          flex: 2;
        }
        
        /* Sponsor Success */
        .sponsor-success {
          padding: 48px 32px;
          text-align: center;
        }
        
        .success-icon {
          width: 64px;
          height: 64px;
          background: #D4E5E5;
          color: #2D6A6A;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          font-weight: bold;
          margin: 0 auto 24px;
        }
        
        .sponsor-success h2 {
          font-family: 'DM Serif Display', serif;
          font-size: 1.5rem;
          margin-bottom: 12px;
        }
        
        .sponsor-success p {
          color: var(--text-muted);
          font-size: 0.95rem;
          line-height: 1.5;
          margin-bottom: 8px;
        }
        
        .success-email {
          margin-bottom: 24px;
        }
        
        .success-email strong {
          color: var(--text);
        }
        
        /* Onboarding */
        .onboarding-overlay { 
          position: fixed; 
          inset: 0; 
          background: var(--bg);
          display: flex; 
          align-items: center; 
          justify-content: center; 
          z-index: 300; 
          padding: 24px;
        }
        
        .onboarding-modal { 
          width: 100%; 
          max-width: 340px;
          text-align: center;
        }
        
        .onboarding-brand {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 8px;
        }
        
        .onboarding-brand .brand-icon {
          font-size: 2rem;
          color: var(--terracotta);
        }
        
        .onboarding-modal h1 { 
          font-family: 'DM Serif Display', serif;
          font-size: 3rem; 
          font-weight: 400;
          color: var(--text);
        }
        
        .onboarding-tagline { 
          color: var(--text-secondary); 
          margin-bottom: 36px;
          font-size: 1.05rem;
        }
        
        .onboarding-form { 
          text-align: left; 
          margin-bottom: 24px;
        }
        
        .form-group { margin-bottom: 16px; }
        
        .form-group label {
          display: block;
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 6px;
        }
        
        .form-group input { 
          width: 100%; 
          padding: 14px 16px;
          border: 2px solid var(--border);
          border-radius: 12px;
          background: var(--bg-card);
          font-family: 'DM Sans', sans-serif;
          font-size: 1rem;
          color: var(--text);
          transition: border-color 0.2s ease;
        }
        
        .form-group input:focus { 
          outline: none; 
          border-color: var(--terracotta);
        }
        
        .form-group input::placeholder { color: var(--text-muted); }

        .onboarding-form select {
          width: 100%;
          padding: 14px 16px;
          border: 2px solid var(--border);
          border-radius: 12px;
          background: var(--bg-card);
          font-family: 'DM Sans', sans-serif;
          font-size: 1rem;
          color: var(--text);
          cursor: pointer;
          transition: border-color 0.2s ease;
        }

        .onboarding-form select:focus {
          outline: none;
          border-color: var(--terracotta);
        }

        .onboarding-form input[type="number"]::-webkit-outer-spin-button,
        .onboarding-form input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        
        .onboarding-btn { 
          width: 100%; 
          padding: 16px;
          border: none; 
          background: var(--terracotta);
          color: white;
          border-radius: 12px;
          font-family: 'DM Sans', sans-serif;
          font-size: 1.05rem;
          font-weight: 600;
          cursor: pointer;
          margin-bottom: 12px;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(196, 89, 59, 0.3);
        }
        
        .onboarding-btn:hover {
          background: #B34E32;
          transform: translateY(-1px);
        }
        
        .skip-btn { 
          border: none; 
          background: none; 
          color: var(--text-muted);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.95rem;
          cursor: pointer;
        }
        
        .skip-btn:hover { color: var(--text); }

        /* How-to-use tutorial */
        .tut-overlay {
          position: fixed;
          inset: 0;
          background: var(--bg);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 350;
          padding: 20px;
        }
        .tut-modal {
          width: 100%;
          max-width: 380px;
          max-height: calc(100vh - 40px);
          overflow-y: auto;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 24px;
          padding: 22px 24px 20px;
          text-align: center;
          position: relative;
          box-shadow: 0 20px 60px rgba(45, 42, 38, 0.18);
        }
        .tut-skip-corner {
          position: absolute;
          top: 14px;
          right: 16px;
          background: none;
          border: none;
          color: var(--text-muted);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          cursor: pointer;
          padding: 6px 8px;
          z-index: 2;
        }
        .tut-skip-corner:hover { color: var(--text); }

        .tut-visual {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 6px 0 16px;
          animation: tutFade 0.35s ease;
        }
        @keyframes tutFade {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .tut-phone {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .tut-phone img {
          width: 220px;
          height: auto;
          border-radius: 22px;
          border: 1px solid var(--border);
          box-shadow: 0 14px 38px rgba(45, 42, 38, 0.18),
                      0 4px 12px rgba(45, 42, 38, 0.08);
          background: var(--bg);
          display: block;
        }

        .tut-title {
          font-family: 'DM Serif Display', serif;
          font-size: 1.7rem;
          font-weight: 400;
          color: var(--text);
          margin: 0 0 8px;
          line-height: 1.15;
        }
        .tut-body {
          color: var(--text-secondary);
          font-size: 0.98rem;
          line-height: 1.45;
          margin: 0 4px 20px;
        }

        .tut-dots {
          display: flex;
          gap: 6px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .tut-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          border: none;
          background: var(--border);
          padding: 0;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .tut-dot.active {
          width: 22px;
          border-radius: 4px;
          background: var(--terracotta);
        }

        .tut-actions {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .tut-back, .tut-back-spacer {
          min-height: 32px;
          background: none;
          border: none;
          color: var(--text-muted);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.9rem;
          cursor: pointer;
          padding: 6px 4px;
        }
        .tut-back-spacer { visibility: hidden; }
        .tut-back:hover { color: var(--text); }
        .tut-next {
          width: 100%;
          max-width: 260px;
          padding: 14px 18px;
          border: none;
          background: var(--terracotta);
          color: white;
          border-radius: 12px;
          font-family: 'DM Sans', sans-serif;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(196, 89, 59, 0.3);
          transition: all 0.2s ease;
        }
        .tut-next:hover {
          background: #B34E32;
          transform: translateY(-1px);
        }

        .bottom-spacer { height: 40px; }
      `}</style>

      <div className="app">
        {showTutorial && <HowToUseModal onComplete={handleTutorialComplete} />}
        {!showTutorial && showOnboarding && <OnboardingModal onComplete={handleOnboardingComplete} />}
        
        {showProfile && user && (
          <ProfilePanel
            user={user}
            history={history}
            favorites={favorites}
            region={region}
            onSetRegion={handleSetRegion}
            onUpdateUser={setUser}
            onDeleteHistory={deleteFromHistory} 
            onClearHistory={clearHistory}
            onDeleteFavorite={removeFromFavorites}
            onToggleFavorite={toggleFavorite}
            isFavorited={isFavorited}
            onItemClick={handleItemClick}
            onClose={() => setShowProfile(false)} 
            onLogout={handleLogout}
            onUpdateHomeCity={handleUpdateHomeCity}
            onDeleteAccount={authSession ? handleDeleteAccount : null}
            isAuthUser={!!authSession}
            authSession={authSession}
          />
        )}
        
        {showSponsorPortal && (
          <SponsorPortalModal onClose={() => setShowSponsorPortal(false)} />
        )}
        
        {/* Reset Password Modal (landing from email link) */}
        {showResetPasswordModal && (
          <div className="auth-modal-overlay">
            <div className="auth-modal" onClick={e => e.stopPropagation()}>
              <h2>Set New Password</h2>
              <p className="auth-subtitle">Choose a new password for your account</p>

              {resetPasswordSuccess ? (
                <div className="auth-success">
                  <div className="auth-success-icon">✓</div>
                  <p>Password updated! You're now signed in.</p>
                </div>
              ) : (
                <form onSubmit={handleResetPasswordSubmit}>
                  {resetPasswordError && <div className="auth-error">{resetPasswordError}</div>}

                  <div className="auth-field">
                    <label>New Password</label>
                    <input
                      type="password"
                      value={resetNewPassword}
                      onChange={e => setResetNewPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      minLength={6}
                      required
                      autoFocus
                    />
                  </div>

                  <div className="auth-field">
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      value={resetConfirmPassword}
                      onChange={e => setResetConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      minLength={6}
                      required
                    />
                  </div>

                  <button type="submit" className="auth-submit-btn">Update Password</button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Auth Modal */}
        {showAuthModal && (
          <div className="auth-modal-overlay" onClick={() => setShowAuthModal(false)}>
            <div className="auth-modal" onClick={e => e.stopPropagation()}>
              <button className="auth-modal-close" onClick={() => setShowAuthModal(false)}>✕</button>
              
              {authMode === 'signin' && (
                <>
                  <h2>Welcome back</h2>
                  <p className="auth-subtitle">Sign in to sync your favorites across devices</p>
                  
                  <form onSubmit={handleSignIn}>
                    {authError && <div className="auth-error">{authError}</div>}
                    
                    <div className="auth-field">
                      <label>Email</label>
                      <input 
                        type="email" 
                        value={authEmail} 
                        onChange={e => setAuthEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                    
                    <div className="auth-field">
                      <label>Password</label>
                      <input 
                        type="password" 
                        value={authPassword} 
                        onChange={e => setAuthPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                      />
                    </div>
                    
                    <button type="submit" className="auth-submit-btn">Sign In</button>
                  </form>
                  
                  <button className="auth-link-btn" onClick={() => { setAuthMode('forgot'); setAuthError(''); }}>
                    Forgot password?
                  </button>
                  
                  <div className="auth-divider"><span>or</span></div>
                  
                  <button className="auth-switch-btn" onClick={() => { setAuthMode('signup'); setAuthError(''); setAuthDupeHint(false); setAuthSignupSuccess(false); }}>
                    Create an account
                  </button>
                </>
              )}
              
              {authMode === 'signup' && (
                <>
                  <h2>Create account</h2>
                  <p className="auth-subtitle">Save your favorites and sync across devices</p>

                  {authSignupSuccess ? (
                    <div className="auth-success">
                      <div className="auth-success-icon">✓</div>
                      <p>Check your email to confirm your account.</p>
                    </div>
                  ) : (
                  <form onSubmit={handleSignUp}>
                    {authDupeHint && (
                      <div className="auth-error">
                        If this email is registered, try{' '}
                        <span
                          style={{ textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() => { setAuthMode('signin'); setAuthError(''); setAuthDupeHint(false); setAuthSignupSuccess(false); }}
                        >signing in instead</span>.
                      </div>
                    )}
                    {authError && <div className="auth-error">{authError}</div>}
                    
                    <div className="auth-field">
                      <label>Name</label>
                      <input 
                        type="text" 
                        value={authName} 
                        onChange={e => setAuthName(e.target.value)}
                        placeholder="Your name"
                      />
                    </div>
                    
                    <div className="auth-field">
                      <label>Email</label>
                      <input 
                        type="email" 
                        value={authEmail} 
                        onChange={e => setAuthEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                    
                    <div className="auth-field">
                      <label>Password</label>
                      <input 
                        type="password" 
                        value={authPassword} 
                        onChange={e => setAuthPassword(e.target.value)}
                        placeholder="Min 6 characters"
                        minLength={6}
                        required
                      />
                    </div>
                    
                    <button type="submit" className="auth-submit-btn">Create Account</button>
                  </form>
                  )}

                  <div className="auth-divider"><span>or</span></div>

                  <button className="auth-switch-btn" onClick={() => { setAuthMode('signin'); setAuthError(''); setAuthDupeHint(false); setAuthSignupSuccess(false); }}>
                    Already have an account? Sign in
                  </button>
                </>
              )}
              
              {authMode === 'forgot' && (
                <>
                  <h2>Reset password</h2>
                  <p className="auth-subtitle">We'll send you a link to reset your password</p>
                  
                  {resetEmailSent ? (
                    <div className="auth-success">
                      <div className="auth-success-icon">✓</div>
                      <p>Check your email for a reset link!</p>
                      <button className="auth-switch-btn" onClick={() => { setAuthMode('signin'); setResetEmailSent(false); }}>
                        Back to sign in
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPassword}>
                      {authError && <div className="auth-error">{authError}</div>}
                      
                      <div className="auth-field">
                        <label>Email</label>
                        <input 
                          type="email" 
                          value={authEmail} 
                          onChange={e => setAuthEmail(e.target.value)}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                      
                      <button type="submit" className="auth-submit-btn">Send Reset Link</button>
                    </form>
                  )}
                  
                  <button className="auth-link-btn" onClick={() => { setAuthMode('signin'); setAuthError(''); setResetEmailSent(false); }}>
                    ← Back to sign in
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        
        <header className="header">
          <div>
            <div className="brand">
              <span className={`brand-icon ${isSpinning ? 'spinning' : ''}`} onClick={() => { setIsSpinning(true); setTimeout(() => setIsSpinning(false), 500); }}>✦</span>
              <h1>Carpe</h1>
            </div>
            <p className="header-subtitle">
              {view === 'restaurant' && 'Discover your next meal'}
              {view === 'activities' && 'Find your next adventure'}
              {view === 'combo' && 'Plan the perfect day out'}
              {view === 'stayin' && stayInMode === 'binge' && 'Find your next binge'}
              {view === 'stayin' && stayInMode === 'order' && 'Order something delicious'}
            </p>
          </div>
          {user ? (
            <button className="profile-btn" onClick={() => setShowProfile(true)}>
              {getInitials(user.user_metadata?.full_name || user.email || 'U')}
            </button>
          ) : (
            <button className="sign-in-btn" onClick={() => openAuthModal('signin')}>
              Sign In
            </button>
          )}
        </header>
        
        <nav className="nav">
          <button className={`nav-btn ${view === 'restaurant' ? 'active' : ''}`} onClick={() => setView('restaurant')}>Eat</button>
          <button className={`nav-btn ${view === 'activities' ? 'active' : ''}`} onClick={() => setView('activities')}>Explore</button>
          <button className={`nav-btn ${view === 'combo' ? 'active' : ''}`} onClick={() => { setView('combo'); resetCombo(); }}>Eat & Play</button>
          <button className={`nav-btn ${view === 'stayin' ? 'active' : ''}`} onClick={() => setView('stayin')}>Stay In</button>
        </nav>
        
        {view !== 'stayin' && (
          <div className="input-section">
            <CitySearch city={city} setCity={setCity} region={region} onLocationDetected={() => {}} onCitySelect={loadCityDataImmediate} onAddressGeocoded={(coords) => setSearchCoords(coords)} metroCoords={dynamicMetroCoords || METRO_COORDS} />
            {city && (
              <div className="data-status">
                {loadingCity ? (
                  <span className="data-loading">Loading {city.split(',')[0]}...</span>
                ) : fetchError ? (
                  <span className="data-error" style={{color: 'var(--terracotta)', fontSize: '0.8rem'}}>{fetchError}</span>
                ) : cityActivities.length > 0 || cityRestaurants.length > 0 ? (
                  <span className="data-ready">✓ Carpe {city.split(',')[0].trim()}</span>
                ) : (
                  <span className="data-none">Enter a city to discover</span>
                )}
              </div>
            )}
          </div>
        )}
        
        {view === 'restaurant' && (
          <>
            <div className={`filter-toggle ${showFilters ? 'open' : ''}`} onClick={() => setShowFilters(!showFilters)}>
              <span>Refine your search</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            <div className={`filters-panel ${showFilters ? 'open' : ''}`}>
              <div className="filter-group">
                <div className="filter-group-label">Max Distance</div>
                {searchCoords ? (
                  <>
                    <div className="filter-pills">
                      {distanceOptions.map(d => (
                        <FilterPill 
                          key={d || 'any'} 
                          label={distanceLabel(d)}
                          active={maxDistance === d} 
                          onClick={() => { if (requireLocation()) setMaxDistance(d); }} 
                        />
                      ))}
                    </div>
                    <button className="clear-address-btn" onClick={() => { setSearchCoords(null); setMaxDistance(null); }}>
                      Clear distance filter
                    </button>
                  </>
                ) : (
                  <div className="filter-hint" style={{fontSize:'13px',color:'var(--terracotta)',fontWeight:500,padding:'6px 0',lineHeight:1.4}}>
                    Enable location or enter an address above to filter by distance.
                  </div>
                )}
              </div>
              <div className="filter-group">
                <div className="filter-group-label">Price Range</div>
                <div className="filter-pills">
                  {prices.map(p => <FilterPill key={p} label={p} active={selectedPrices.includes(p)} onClick={() => toggleFilterGated(selectedPrices, setSelectedPrices, p)} />)}
                </div>
              </div>
              <div className="filter-group">
                <div className="filter-group-label">Cuisine</div>
                <div className="filter-pills">
                  {cuisines.map(c => <FilterPill key={c} label={c} active={selectedCuisines.includes(c)} onClick={() => toggleFilterGated(selectedCuisines, setSelectedCuisines, c)} />)}
                </div>
              </div>
              <div className="filter-group">
                <div className="filter-group-label">Vibe</div>
                <div className="filter-pills">
                  {styles.map(s => <FilterPill key={s} label={s} active={selectedStyles.includes(s)} onClick={() => toggleFilterGated(selectedStyles, setSelectedStyles, s)} />)}
                </div>
              </div>
            </div>
            
            <div className="wheel-section">
              <div className="wheel-header">
                <h2 className="wheel-title">Spin the Wheels</h2>
                <p className="wheel-subtitle">Let fate decide your dinner</p>
              </div>
              <div className="wheels">
                <WheelSpinner items={selectedPrices.length > 0 ? selectedPrices : prices} spinning={rSpinning} finalIndex={rFinalIndices[0]} label="Price" stopDelay={0} onComplete={() => handleRWheelComplete(0)} />
                <WheelSpinner items={selectedCuisines.length > 0 ? selectedCuisines : cuisines} spinning={rSpinning} finalIndex={rFinalIndices[1]} label="Cuisine" stopDelay={400} onComplete={() => handleRWheelComplete(1)} />
                <WheelSpinner items={selectedStyles.length > 0 ? selectedStyles : styles} spinning={rSpinning} finalIndex={rFinalIndices[2]} label="Vibe" stopDelay={800} isLastWheel={true} onComplete={() => handleRWheelComplete(2)} />
              </div>
              <button className="spin-btn" onClick={handleRestaurantSpin} disabled={rSpinning}>
                {rSpinning ? 'Finding your spot...' : 'Discover'}
              </button>
            </div>
          </>
        )}
        
        {view === 'activities' && (
          <>
            {/* Event Date Filter - hidden in Japan mode (no event coverage there) */}
            {!isJapan && (
            <div className="event-date-filter">
              <button 
                className={`date-filter-btn ${eventDateFilter === 'today' ? 'active' : ''}`}
                onClick={() => { if (requireLocation()) setEventDateFilter(eventDateFilter === 'today' ? null : 'today'); }}
              >
                Today
              </button>
              <button 
                className={`date-filter-btn ${eventDateFilter === 'tomorrow' ? 'active' : ''}`}
                onClick={() => { if (requireLocation()) setEventDateFilter(eventDateFilter === 'tomorrow' ? null : 'tomorrow'); }}
              >
                Tomorrow
              </button>
              <button 
                className={`date-filter-btn calendar-btn ${eventDateFilter && eventDateFilter !== 'today' && eventDateFilter !== 'tomorrow' ? 'active' : ''}`}
                onClick={() => { if (requireLocation()) setShowDatePicker(!showDatePicker); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                {eventDateFilter && eventDateFilter !== 'today' && eventDateFilter !== 'tomorrow' 
                  ? formatDateDisplay(eventDateFilter) 
                  : 'Pick Date'}
              </button>
              {eventDateFilter && (
                <button className="date-clear-btn" onClick={() => setEventDateFilter(null)}>
                  ✕
                </button>
              )}
            </div>
            )}

            {/* Date Picker Modal */}
            {!isJapan && showDatePicker && (
              <DateRangeCalendar
                value={eventDateFilter}
                onChange={(val) => setEventDateFilter(val)}
                onClose={() => setShowDatePicker(false)}
              />
            )}
            
            <div className={`filter-toggle ${showFilters ? 'open' : ''}`} onClick={() => setShowFilters(!showFilters)}>
              <span>Refine your search</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            <div className={`filters-panel ${showFilters ? 'open' : ''}`}>
              <div className="filter-group">
                <div className="filter-group-label">Max Distance</div>
                {searchCoords ? (
                  <>
                    <div className="filter-pills">
                      {distanceOptions.map(d => (
                        <FilterPill 
                          key={d || 'any'} 
                          label={distanceLabel(d)}
                          active={maxDistance === d} 
                          onClick={() => { if (requireLocation()) setMaxDistance(d); }} 
                        />
                      ))}
                    </div>
                    <button className="clear-address-btn" onClick={() => { setSearchCoords(null); setMaxDistance(null); }}>
                      Clear distance filter
                    </button>
                  </>
                ) : (
                  <div className="filter-hint" style={{fontSize:'13px',color:'var(--terracotta)',fontWeight:500,padding:'6px 0',lineHeight:1.4}}>
                    Enable location or enter an address above to filter by distance.
                  </div>
                )}
              </div>
              <div className="filter-group">
                <div className="filter-group-label">Setting</div>
                <div className="filter-pills">
                  {locations.map(l => <FilterPill key={l} label={l} active={selectedLocations.includes(l)} onClick={() => toggleFilterGated(selectedLocations, setSelectedLocations, l)} />)}
                </div>
              </div>
              <div className="filter-group">
                <div className="filter-group-label">Category</div>
                <div className="filter-pills">
                  {categories.map(c => <FilterPill key={c} label={c} active={selectedCategories.includes(c)} onClick={() => toggleFilterGated(selectedCategories, setSelectedCategories, c)} />)}
                </div>
              </div>
              <div className="filter-group">
                <div className="filter-group-label">Budget</div>
                <div className="filter-pills">
                  {activityPrices.map(p => <FilterPill key={p} label={p} active={selectedActivityPrices.includes(p)} onClick={() => toggleFilterGated(selectedActivityPrices, setSelectedActivityPrices, p)} />)}
                </div>
              </div>
            </div>
            
            <div className="wheel-section">
              <div className="wheel-header">
                <h2 className="wheel-title">Spin the Wheels</h2>
                <p className="wheel-subtitle">Uncover your next adventure</p>
              </div>
              <div className="wheels">
                <WheelSpinner items={selectedLocations.length > 0 ? selectedLocations : locations} spinning={aSpinning} finalIndex={aFinalIndices[0]} label="Setting" stopDelay={0} onComplete={() => handleAWheelComplete(0)} />
                <WheelSpinner items={selectedCategories.length > 0 ? selectedCategories : categories} spinning={aSpinning} finalIndex={aFinalIndices[1]} label="Category" stopDelay={400} onComplete={() => handleAWheelComplete(1)} />
                <WheelSpinner items={selectedActivityPrices.length > 0 ? selectedActivityPrices : activityPrices} spinning={aSpinning} finalIndex={aFinalIndices[2]} label="Budget" stopDelay={800} isLastWheel={true} onComplete={() => handleAWheelComplete(2)} />
              </div>
              <button className="spin-btn" onClick={handleActivitySpin} disabled={aSpinning}>
                {aSpinning ? 'Finding adventure...' : 'Discover'}
              </button>
            </div>
          </>
        )}
        
        {view === 'combo' && (
          <>
            {/* Event Date Filter - hidden in Japan mode (no event coverage there) */}
            {!isJapan && (
            <div className="event-date-filter">
              <button 
                className={`date-filter-btn ${eventDateFilter === 'today' ? 'active' : ''}`}
                onClick={() => { if (requireLocation()) setEventDateFilter(eventDateFilter === 'today' ? null : 'today'); }}
              >
                Today
              </button>
              <button 
                className={`date-filter-btn ${eventDateFilter === 'tomorrow' ? 'active' : ''}`}
                onClick={() => { if (requireLocation()) setEventDateFilter(eventDateFilter === 'tomorrow' ? null : 'tomorrow'); }}
              >
                Tomorrow
              </button>
              <button 
                className={`date-filter-btn calendar-btn ${eventDateFilter && eventDateFilter !== 'today' && eventDateFilter !== 'tomorrow' ? 'active' : ''}`}
                onClick={() => { if (requireLocation()) setShowDatePicker(!showDatePicker); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                {eventDateFilter && eventDateFilter !== 'today' && eventDateFilter !== 'tomorrow' 
                  ? formatDateDisplay(eventDateFilter) 
                  : 'Pick Date'}
              </button>
              {eventDateFilter && (
                <button className="date-clear-btn" onClick={() => setEventDateFilter(null)}>
                  ✕
                </button>
              )}
            </div>
            )}

            {/* Date Picker Modal */}
            {!isJapan && showDatePicker && (
              <DateRangeCalendar
                value={eventDateFilter}
                onChange={(val) => setEventDateFilter(val)}
                onClose={() => setShowDatePicker(false)}
              />
            )}
            
            <div className={`filter-toggle ${showFilters ? 'open' : ''}`} onClick={() => setShowFilters(!showFilters)}>
              <span>Refine your search</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            <div className={`filters-panel ${showFilters ? 'open' : ''}`}>
              <div className="filter-group">
                <div className="filter-group-label">Max Distance</div>
                {searchCoords ? (
                  <>
                    <div className="filter-pills">
                      {distanceOptions.map(d => (
                        <FilterPill 
                          key={d || 'any'} 
                          label={distanceLabel(d)}
                          active={maxDistance === d} 
                          onClick={() => { if (requireLocation()) setMaxDistance(d); }} 
                        />
                      ))}
                    </div>
                    <button className="clear-address-btn" onClick={() => { setSearchCoords(null); setMaxDistance(null); }}>
                      Clear distance filter
                    </button>
                  </>
                ) : (
                  <div className="filter-hint" style={{fontSize:'13px',color:'var(--terracotta)',fontWeight:500,padding:'6px 0',lineHeight:1.4}}>
                    Enable location or enter an address above to filter by distance.
                  </div>
                )}
              </div>
              <div className="filter-group">
                <div className="filter-group-label">Activity Setting</div>
                <div className="filter-pills">
                  {locations.map(l => <FilterPill key={l} label={l} active={selectedLocations.includes(l)} onClick={() => toggleFilterGated(selectedLocations, setSelectedLocations, l)} />)}
                </div>
              </div>
              <div className="filter-group">
                <div className="filter-group-label">Activity Type</div>
                <div className="filter-pills">
                  {categories.map(c => <FilterPill key={c} label={c} active={selectedCategories.includes(c)} onClick={() => toggleFilterGated(selectedCategories, setSelectedCategories, c)} />)}
                </div>
              </div>
              <div className="filter-group">
                <div className="filter-group-label">Food Price</div>
                <div className="filter-pills">
                  {prices.map(p => <FilterPill key={p} label={p} active={selectedPrices.includes(p)} onClick={() => toggleFilterGated(selectedPrices, setSelectedPrices, p)} />)}
                </div>
              </div>
              <div className="filter-group">
                <div className="filter-group-label">Food Cuisine</div>
                <div className="filter-pills">
                  {cuisines.map(c => <FilterPill key={c} label={c} active={selectedCuisines.includes(c)} onClick={() => toggleFilterGated(selectedCuisines, setSelectedCuisines, c)} />)}
                </div>
              </div>
              <div className="filter-group">
                <div className="filter-group-label">Dining Vibe</div>
                <div className="filter-pills">
                  {['Any', 'Casual', 'Fine Dining', 'Café', 'Bar & Grill'].map(v => (
                    <FilterPill key={v} label={v} active={comboVibeFilter === v} onClick={() => { if (requireLocation()) setComboVibeFilter(v); }} />
                  ))}
                </div>
              </div>
            </div>
            
            {comboStep === 1 && (
              <div className="wheel-section">
                <div className="combo-progress">
                  <div className="progress-step">
                    <div className="step-circle active">1</div>
                    <span className="step-label active">Adventure</span>
                  </div>
                  <div className="progress-line"></div>
                  <div className="progress-step">
                    <div className="step-circle">2</div>
                    <span className="step-label">Dine</span>
                  </div>
                </div>

                <div className="dining-radius-section">
                  <div className="dining-radius-label">How far between adventure & dining?</div>
                  <div className="dining-radius-options">
                    {(isJapan ? JAPAN_RADIUS_OPTIONS : RADIUS_OPTIONS).map(opt => (
                      <button
                        key={opt.value}
                        className={`radius-option ${diningRadius === opt.value ? 'active' : ''}`}
                        onClick={() => setDiningRadius(opt.value)}
                      >
                        <span className="radius-option-label">{opt.label}</span>
                        <span className="radius-option-desc">{opt.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wheel-header">
                  <h2 className="wheel-title">First, an Adventure</h2>
                  <p className="wheel-subtitle">What will you discover?</p>
                </div>
                <div className="wheels">
                  <WheelSpinner items={selectedLocations.length > 0 ? selectedLocations : locations} spinning={comboASpinning} finalIndex={comboAFinalIndices[0]} label="Setting" stopDelay={0} onComplete={() => handleComboAWheelComplete(0)} />
                  <WheelSpinner items={selectedCategories.length > 0 ? selectedCategories : categories} spinning={comboASpinning} finalIndex={comboAFinalIndices[1]} label="Category" stopDelay={400} onComplete={() => handleComboAWheelComplete(1)} />
                  <WheelSpinner items={selectedActivityPrices.length > 0 ? selectedActivityPrices : activityPrices} spinning={comboASpinning} finalIndex={comboAFinalIndices[2]} label="Budget" stopDelay={800} isLastWheel={true} onComplete={() => handleComboAWheelComplete(2)} />
                </div>
                <button className="spin-btn" onClick={handleComboActivitySpin} disabled={comboASpinning}>
                  {comboASpinning ? 'Finding adventure...' : 'Find an Adventure'}
                </button>
              </div>
            )}
            
            {comboStep === 2 && comboActivity && (
              <div className="wheel-section">
                <div className="combo-progress">
                  <div className="progress-step">
                    <div className="step-circle completed">✓</div>
                    <span className="step-label completed">Adventure</span>
                  </div>
                  <div className="progress-line completed"></div>
                  <div className="progress-step">
                    <div className="step-circle active">2</div>
                    <span className="step-label active">Dine</span>
                  </div>
                </div>
                
                <div className="selected-card">
                  <div className="selected-label">Your Adventure</div>
                  <div className="selected-name">{comboActivity.name}</div>
                  <div className="selected-meta">{comboActivity.category} · {comboActivity.indoor ? 'Indoor' : 'Outdoor'}{comboActivity.priceRange ? ` · ${comboActivity.priceRange}` : ''}</div>
                </div>
                
                <div className="dining-radius-section">
                  <div className="dining-radius-label">How far for dinner?</div>
                  <div className="dining-radius-options">
                    {(isJapan ? JAPAN_RADIUS_OPTIONS : RADIUS_OPTIONS).map(opt => (
                      <button 
                        key={opt.value}
                        className={`radius-option ${diningRadius === opt.value ? 'active' : ''}`}
                        onClick={() => setDiningRadius(opt.value)}
                      >
                        <span className="radius-option-label">{opt.label}</span>
                        <span className="radius-option-desc">{opt.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="wheel-header">
                  <h2 className="wheel-title">Then, Dine</h2>
                  <p className="wheel-subtitle">Where will you refuel?</p>
                </div>
                <div className="wheels">
                  <WheelSpinner items={selectedPrices.length > 0 ? selectedPrices : prices} spinning={comboRSpinning} finalIndex={comboRFinalIndices[0]} label="Price" stopDelay={0} onComplete={() => handleComboRWheelComplete(0)} />
                  <WheelSpinner items={selectedStyles.length > 0 ? selectedStyles : styles} spinning={comboRSpinning} finalIndex={comboRFinalIndices[2]} label="Vibe" stopDelay={400} onComplete={() => handleComboRWheelComplete(2)} />
                  <WheelSpinner items={selectedCuisines.length > 0 ? selectedCuisines : cuisines} spinning={comboRSpinning} finalIndex={comboRFinalIndices[1]} label="Cuisine" stopDelay={800} isLastWheel={true} onComplete={() => handleComboRWheelComplete(1)} />
                </div>
                <button className="spin-btn" onClick={handleComboRestaurantSpin} disabled={comboRSpinning}>
                  {comboRSpinning ? 'Finding your spot...' : 'Find a Spot'}
                </button>
              </div>
            )}
          </>
        )}
        
        {view === 'stayin' && (
          <>
            <div className="stayin-toggle">
              <button 
                className={`stayin-tab ${stayInMode === 'binge' ? 'active' : ''}`}
                onClick={() => setStayInMode('binge')}
              >
                <span className="stayin-tab-icon"></span>
                What to Binge
              </button>
              <button 
                className={`stayin-tab ${stayInMode === 'order' ? 'active' : ''}`}
                onClick={() => setStayInMode('order')}
              >
                <span className="stayin-tab-icon"></span>
                Order In
              </button>
            </div>
            
            {stayInMode === 'binge' && (
              <>
                <div className={`filter-toggle ${showFilters ? 'open' : ''}`} onClick={() => setShowFilters(!showFilters)}>
                  <span>Refine your search</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                <div className={`filters-panel ${showFilters ? 'open' : ''}`}>
                  <div className="filter-group">
                    <div className="filter-group-label">Platform</div>
                    <div className="filter-pills">
                      {platforms.map(p => <FilterPill key={p} label={p} active={selectedPlatforms.includes(p)} onClick={() => toggleFilter(selectedPlatforms, setSelectedPlatforms, p)} />)}
                    </div>
                  </div>
                  <div className="filter-group">
                    <div className="filter-group-label">Genre</div>
                    <div className="filter-pills">
                      {genres.map(g => <FilterPill key={g} label={g} active={selectedGenres.includes(g)} onClick={() => toggleFilter(selectedGenres, setSelectedGenres, g)} />)}
                    </div>
                  </div>
                  <div className="filter-group">
                    <div className="filter-group-label">Length</div>
                    <div className="filter-pills">
                      {lengths.map(l => <FilterPill key={l} label={l} active={selectedLengths.includes(l)} onClick={() => toggleFilter(selectedLengths, setSelectedLengths, l)} />)}
                    </div>
                  </div>
                </div>
                
                <div className="wheel-section">
                  <div className="wheel-header">
                    <h2 className="wheel-title">Spin for a Show</h2>
                    <p className="wheel-subtitle">Let fate decide</p>
                  </div>
                  <div className="wheels">
                    <WheelSpinner items={selectedGenres.length > 0 ? selectedGenres : genres} spinning={bingeSpinning} finalIndex={bingeFinalIndices[0]} label="Genre" stopDelay={0} onComplete={() => handleBingeWheelComplete(0)} />
                    <WheelSpinner items={selectedPlatforms.length > 0 ? selectedPlatforms : platforms} spinning={bingeSpinning} finalIndex={bingeFinalIndices[1]} label="Platform" stopDelay={400} onComplete={() => handleBingeWheelComplete(1)} />
                    <WheelSpinner items={selectedLengths.length > 0 ? selectedLengths : lengths} spinning={bingeSpinning} finalIndex={bingeFinalIndices[2]} label="Length" stopDelay={800} isLastWheel={true} onComplete={() => handleBingeWheelComplete(2)} />
                  </div>
                  <button className="spin-btn" onClick={handleBingeSpin} disabled={bingeSpinning}>
                    {bingeSpinning ? 'Finding your show...' : 'Discover'}
                  </button>
                </div>
              </>
            )}
            
            {stayInMode === 'order' && (
              <>
                <div className="input-section">
                  <CitySearch city={city} setCity={setCity} region={region} onLocationDetected={() => {}} onCitySelect={loadCityDataImmediate} onAddressGeocoded={(coords) => setSearchCoords(coords)} metroCoords={dynamicMetroCoords || METRO_COORDS} />
                </div>
                
                <div className={`filter-toggle ${showFilters ? 'open' : ''}`} onClick={() => setShowFilters(!showFilters)}>
                  <span>Refine your search</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                <div className={`filters-panel ${showFilters ? 'open' : ''}`}>
                  <div className="filter-group">
                    <div className="filter-group-label">Max Distance</div>
                    {searchCoords ? (
                      <>
                        <div className="filter-pills">
                          {distanceOptions.map(d => (
                            <FilterPill 
                              key={d || 'any'} 
                              label={distanceLabel(d)}
                              active={maxDistance === d} 
                              onClick={() => { if (requireLocation()) setMaxDistance(d); }} 
                            />
                          ))}
                        </div>
                        <button className="clear-address-btn" onClick={() => { setSearchCoords(null); setMaxDistance(null); }}>
                          Clear distance filter
                        </button>
                      </>
                    ) : (
                      <div className="filter-hint" style={{fontSize:'13px',color:'var(--terracotta)',fontWeight:500,padding:'6px 0',lineHeight:1.4}}>
                        Enable location or enter an address above to filter by distance.
                      </div>
                    )}
                  </div>
                  <div className="filter-group">
                    <div className="filter-group-label">Cuisine</div>
                    <div className="filter-pills">
                      {cuisines.filter(c => c !== 'Trending').map(c => <FilterPill key={c} label={c} active={selectedOrderCuisines.includes(c)} onClick={() => toggleFilterGated(selectedOrderCuisines, setSelectedOrderCuisines, c)} />)}
                    </div>
                  </div>
                </div>
                
                <div className="wheel-section">
                  <div className="wheel-header">
                    <h2 className="wheel-title">Spin for Delivery</h2>
                    <p className="wheel-subtitle">Skip the dishes tonight</p>
                  </div>
                  <div className="wheels">
                    <WheelSpinner items={deliveryPrices} spinning={orderSpinning} finalIndex={orderFinalIndices[0]} label="Price" stopDelay={0} onComplete={() => handleOrderWheelComplete(0)} />
                    <WheelSpinner items={selectedOrderCuisines.length > 0 ? selectedOrderCuisines : cuisines.filter(c => c !== 'Trending')} spinning={orderSpinning} finalIndex={orderFinalIndices[1]} label="Cuisine" stopDelay={400} onComplete={() => handleOrderWheelComplete(1)} />
                    <WheelSpinner items={['Quick', 'Standard', 'Worth It']} spinning={orderSpinning} finalIndex={orderFinalIndices[2]} label="Speed" stopDelay={800} isLastWheel={true} onComplete={() => handleOrderWheelComplete(2)} />
                  </div>
                  <button className="spin-btn" onClick={handleOrderSpin} disabled={orderSpinning}>
                    {orderSpinning ? 'Finding food...' : 'Order Now'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
        
        <div className="bottom-spacer" />
        
        <RestaurantCard 
          restaurant={restaurantResult} 
          city={city}
          distance={restaurantResult && searchCoords ? calculateDistance(searchCoords.lat, searchCoords.lng, restaurantResult.lat, restaurantResult.lng) : null}
          onDismiss={() => setRestaurantResult(null)} 
          onSpinAgain={() => { setRestaurantResult(null); handleRestaurantSpin(); }}
          onSave={toggleFavorite}
          onShare={shareItem}
          isSaved={restaurantResult ? isFavorited(restaurantResult.name) : false}
        />
        <ActivityCard 
          activity={activityResult} 
          city={city} 
          onDismiss={() => setActivityResult(null)}
          onSpinAgain={() => { setActivityResult(null); handleActivitySpin(); }}
          onSave={toggleFavorite}
          onShare={shareItem}
          isSaved={activityResult ? isFavorited(activityResult.name) : false}
        />
        {showComboResult && (
          <ComboResultCard 
            activity={comboActivity} 
            restaurant={comboRestaurant} 
            city={city}
            distance={comboDistance}
            onDismiss={() => { setShowComboResult(false); resetCombo(); }}
            onSave={toggleFavorite}
            onShare={shareItem}
            isSaved={comboActivity && comboRestaurant ? isFavorited(`${comboActivity.name} + ${comboRestaurant.name}`) : false}
          />
        )}
        <BingeCard 
          show={bingeResult}
          onDismiss={() => setBingeResult(null)}
          onSpinAgain={() => { setBingeResult(null); handleBingeSpin(); }}
          onSave={toggleFavorite}
          onShare={shareItem}
          isSaved={bingeResult ? isFavorited(bingeResult.name) : false}
        />
        <DeliveryCard 
          restaurant={orderResult}
          city={city}
          onDismiss={() => setOrderResult(null)}
          onSpinAgain={() => { setOrderResult(null); handleOrderSpin(); }}
          onSave={toggleFavorite}
          onShare={shareItem}
          isSaved={orderResult ? isFavorited(orderResult.name) : false}
        />

        {/* No-match prompts — shown when strict filters yield zero results */}
        <NoMatchCard info={restaurantNoMatch} onDismiss={() => setRestaurantNoMatch(null)} />
        <NoMatchCard info={activityNoMatch}   onDismiss={() => setActivityNoMatch(null)} />
        <NoMatchCard info={comboANoMatch}     onDismiss={() => setComboANoMatch(null)} />
        <NoMatchCard info={comboRNoMatch}     onDismiss={() => setComboRNoMatch(null)} />
        <NoMatchCard info={bingeNoMatch}      onDismiss={() => setBingeNoMatch(null)}
                     prompt="Please try adjusting your filters." />
        <NoMatchCard info={orderNoMatch}      onDismiss={() => setOrderNoMatch(null)} />

        {/* Location required prompt */}
        {showLocationRequiredModal && (
          <div className="location-required-overlay" onClick={() => setShowLocationRequiredModal(false)}>
            <div className="location-required-modal" onClick={e => e.stopPropagation()}>
              <button className="location-required-close" onClick={() => setShowLocationRequiredModal(false)} aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
              <div className="location-required-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <h2>Location needed</h2>
              <p>Please enter a city or address above so we can tailor your filters to where you are.</p>
              <button className="location-required-btn" onClick={() => setShowLocationRequiredModal(false)}>
                Got it
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function WrappedApp() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}